import {
  ChangeDetectorRef,
  Directive,
  computed,
  effect,
  EffectRef,
  HostListener,
  NgZone,
  AfterViewInit,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  Signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { DashboardService } from '../../../services/dashboard.service';
import { DashboardBulkSelectionService } from '../../../services/dashboard-bulk-selection.service';
import { DashboardBulkSelection, selectionCount, validSelectionCount } from '../../../types/dashboard-bulk-selection';
import { HttpParams } from '@angular/common/http';
import { publicMethods } from '../../../globals';
import { CommunicationService } from '../../../services/communication.service';
import { DataSharingService } from '../../../services/data-sharing.service';
import { TaggingService } from '../../../services/tagging.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../../../services/authentication.service';
import { CustomColumnService } from '../../../services/custom-column.service';
import { AlertsService } from '../../../services/alerts.service';
import { ZintPropensityScoreService } from '../../../services/zint-propensity-score.service';
import { ResultsFormatEditorService } from '../../../services/results-format-editor.service';
import { TerritoryService } from '../../../services/territory.service';
import { tangibleAssets, intangibleAssets } from '../../../classes/asset-classes';
import {
  heatScore,
  wages,
  fleet,
  numberOfActiveTrademarks,
  esosNotifications,
  epcEnergy,
} from '../../../classes/zint-slider-configs';
import { IZintSliderElementUpdate } from '../../../classes/types';
import { ZintNgxSliderComponent } from '../../zint-ngx-slider/zint-ngx-slider.component';
import {
  collapseCardToParamsMappings,
  filterIdToKeywordsMappings,
  magicFilterParams,
} from '../../../classes/dashboard-filter-keywords';
import { MatDrawer } from '@angular/material/sidenav';
import { fromEvent, Observable, of } from 'rxjs';
import { debounceTime, delay, tap } from 'rxjs/operators';
import { extendedCountriesList } from '../../../classes/company';
import { NEWS_EVENT_CATEGORY_GROUPS } from '../../../classes/news-event-categories';
import { PushMessagesService } from '../../../services/push-messages.service';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { MessageService } from '../../../services/message.service';
import { AccountService } from '../../../services/account.service';
import { GetInsightsComponent } from '../../html-snippet-components/get-insights/get-insights.component';
import { IConfigSlider } from '../../../classes/custom-range-slider';
import {
  buildLogisticsMagicInsightSections,
  countActiveLogisticsFilters,
  LOGISTICS_ASSET_SLIDER_LABELS,
  LOGISTICS_DASHBOARD_SKIN,
  LOGISTICS_FINANCIAL_SLIDER_LABELS,
  LOGISTICS_FUNCTIONAL_DEPARTMENTS,
  LOGISTICS_NEWS_EVENT_CATEGORIES,
  LOGISTICS_NEWS_EVENT_GROUPS,
  LOGISTICS_WEB_TECHNOLOGY_CATEGORIES,
  LogisticsMagicInsightSection,
  LogisticsNewsEventGroup,
  selectOptionsByValue,
  selectSliderConfigs,
} from '../logistics-dashboard/logistics-dashboard.config';

declare var $: any;
declare var bootstrap: any;

type LogisticsResultsPerspective =
  | 'overview'
  | 'freight-forwarding'
  | 'third-party-logistics'
  | 'parcel-courier'
  | 'customs-trade';

/** Shared filter state, endpoint loading, and request serialisation. */
@Directive()
export abstract class DashboardSearchBase
  implements OnInit, AfterViewInit, OnDestroy
{
  selectionAmount: number = null;
  selectionAmountEdited = false;
  actionSelection: DashboardBulkSelection = Object.freeze({ mode: 'none' });
  private displayingInitialCompanies = false;
  private displayedPageOffset = 0;
  private displayedSearchParams = new HttpParams();
  get selectableCompanyCount(): number {
    return Math.max(0, this.companyCount().count - (this.companyCount().locked_mi_companies || 0));
  }
  get isSelectionAmountValid(): boolean {
    return validSelectionCount(this.selectionAmount, this.selectableCompanyCount);
  }

  get selectionAmountError(): string | null {
    if (!this.selectionAmountEdited || this.isSelectionAmountValid) return null;
    if (!Number.isInteger(this.selectionAmount) || this.selectionAmount <= 0) {
      return 'Enter a whole number greater than 0.';
    }
    return `You can select up to ${this.selectableCompanyCount.toLocaleString('en-GB')} companies.`;
  }

  get canApplySelection(): boolean {
    return !this.isLoading().loadingCompanyCount && !this.isLoading().loadingCompanies &&
      this.isSelectionAmountValid;
  }

  applySelectionAmount(): void {
    if (!this.canApplySelection) return;
    this.bulkSelection.selectQuery(this.selectionAmount, this.displayedSearchParams);
    this.hasSelectedTotalNumberMatchingFilter = true;
    this.totalCompaniesSelected = this.selectionAmount;
    this.showSelectedNumbersSection = true;
    if (this.displayingInitialCompanies) {
      this.searchCompaniesAfterFiltersNoCount(false, this.displayedSearchParams);
      return;
    }
    this.syncQuerySelectionCheckboxes();
  }

  syncQuerySelectionCheckboxes(): void {
    const selection = this.bulkSelection.selection();
    if (selection.mode !== 'query' || this.displayingInitialCompanies) return;
    this.companies.forEach((company, index) => {
      company.checked = (
        (this.displayedPageOffset + index < selection.count && !selection.excludedIds?.includes(company.id)) ||
        !!selection.addedIds?.includes(company.id));
    });
    this.checkboxSelectAllCompanies = this.companies.length > 0 && this.companies.every(company => company.checked);
  }

  get isPageSelectionIndeterminate(): boolean {
    return this.companies?.some(company => company.checked) && !this.checkboxSelectAllCompanies;
  }

  clearDashboardSelection(): void {
    this.selectionAmountEdited = false;
    this.bulkSelection.clear();
    this.hasSelectedTotalNumberMatchingFilter = false;
    this.showSelectedNumbersSection = false;
    this.totalCompaniesSelected = null;
    this.checkboxSelectAllCompanies = false;
    this.companies.forEach(company => company.checked = false);
    this.dataStore.changeDataSourceCurrentSearchParams(new HttpParams());
  }

  captureActionSelection(): boolean {
    this.actionSelection = this.bulkSelection.selection();
    this.cdr.detectChanges();
    return selectionCount(this.actionSelection) > 0;
  }

  openSelectionModal(modalId: string): void {
    if (this.captureActionSelection()) $(`#${modalId}`).modal('show');
  }

  openCreateTagModal(): void {
    this.actionSelection = Object.freeze({ mode: 'none' });
    this.cdr.detectChanges();
    $('#dashboardTagModal').modal('show');
  }

  constructor(
    public bulkSelection: DashboardBulkSelectionService,
    private dashboardService: DashboardService,
    private territoryService: TerritoryService,
    private communicationService: CommunicationService,
    public pubMethods: publicMethods,
    private dataStore: DataSharingService,
    private taggingService: TaggingService,
    private customColumnService: CustomColumnService,
    private alertsService: AlertsService,
    private zintPropensityScoreService: ZintPropensityScoreService,
    private resultsFormatEditorService: ResultsFormatEditorService,
    public pushMessagesService: PushMessagesService,
    private crmService: CRMIntegrationsService,
    private accountService: AccountService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.captureDashboardModeSwitchNavigation();

    effect(() => {
      if (this.allEndpointsLoaded() === true) {
        this.initialiseBasedUponGetParamsIfAllLoaded();
        this.confidenceSliders?.changes.subscribe(() => {
          this.applySavedConfidenceToSliders();
        });

        if (this.pushMessagesService.showPopupPushMsg()) {
          // The static backdrop is set by data-bs-backdrop in the template.
          $('#gr-popup-modal').modal('show');
          $('#gr-popup-modal').on('hide.bs.modal', _ =>
            this.pushMessagesService.showPopupPushMsg.set(false)
          );
        }
      }
    });

    this.watchCRMExportModalKeyboard();

    effect(() => {
      const isSmall = this.isScreenTooSmall();
      this.ngZone.run(() => {
        if (isSmall) {
          this.toggleSidebarHeight(false);
        } else {
          this.toggleSidebarHeight(true);
        }
      });
      this.cdr.detectChanges();
    });

    effect(() => {
      if (!this.profilePreviewIsOpen()) {
        this.dataStore.companyNumberFromParent.set('');
      }
    });
  }

  urlQueryParams: Record<string, any> = {};
  @ViewChild('searchForm') searchForm: NgForm;
  companies: Array<any> = [];
  showDownloadModal = false;
  numberUBOsRange: Array<any>;
  countriesOfUBOsOptions: Array<any> = [];
  countriesOfOriginOptions: Array<any>;
  countriesOfOfficesOptions: Array<any>;
  countriesOfGeographicalRevenueOptions: Array<any>;
  territoriesOfGeographicalRevenueOptions: Array<any>;
  geographicalRevenueOptions: Array<any>;
  ukRegions: Array<any>;
  companyCategoryOptions: Array<any>;
  // selected and excluded properties will be set and used to build selectedSICCodes and excludedSICCodes arrays
  companySICCodeOptions: Array<any>;
  // SIC-code selections restored from a saved search / shared URL can arrive
  // before companySICCodeOptions has loaded. Stash them and re-apply once the
  // options land in updateUIComponents() (ZINT-ANGULARAPP-2AY).
  pendingSelectedSICCodes: Array<string> = [];
  pendingExcludedSICCodes: Array<string> = [];
  companyStatusOptions: Array<any> = [];
  employeeRangeFigures: Array<any>;
  headcountGrowthPercentageRange: Array<number>;
  functionalDepartmentOptions: Array<any>;
  companyAgeRange: Array<any>;
  numberOfKnownOfficeLocationsRange: Array<any>;
  LEIStatusOptions: Array<any>;
  importExportCategories: Array<any> = [];
  importingExportingBothOptions: Array<any>;
  importExportMonthRange: Array<any>;
  importExportFrequencyRange: Array<any>;
  importExportFrequencyMonthCountRange: Array<any>;
  webTechnologyOptions: Array<any> = [];
  webTrafficOptions: Array<any>;
  averageDirectorAgeRange: Array<any>;
  numberOfDirectorsRange: Array<any>;
  numberOfPSCsRange: Array<any>;
  recentDirectorshipMonthRange: Array<any>;
  nationalitiesOfDirectorsOptions: Array<any> = [];
  nationalitiesOfPSCsOptions: Array<any> = [];
  daysUntilFinancialYearEndLimits: Array<any>;
  financialYearEndsOptions: Array<any>;
  daysUntilAnniversaryLimits: Array<any>;
  hiringDayLimits: Array<any>;
  numberOfRolesHiringForLimits: Array<any>;
  numberOfRolesHiringForDayLimits: Array<any>;
  investmentRaisedOptions: Array<any>;
  shareFilingEventWithinDaysOptions: Array<any>;
  shareFilingCapitalValueOptions: Array<any>;
  accountsDueInRange: Array<any>;
  latestAccountsSubmittedRange: Array<any>;
  accountGroupOptions: Array<any>;
  accountAuditOptions: Array<any>;
  grantRangeOptions: Array<any>;
  grantMonthsUntilProjectEndRange: Array<any>;
  mileRadiusRegisteredAddressRange: Array<any>;
  mileRadiusEventsRange: Array<any>;
  eventDaysRange: Array<any>;
  addressOptions: Array<any>;
  newsDaysRange: Array<any>;
  accountsSearchDayRange: Array<any>;
  trademarkClassOptions: Array<any>;
  trademarkStatusOptions: Array<any>;
  companyColumnsNotAllowedToFilter: Array<any>;
  tagsPositiveOptions: Array<any> = [];
  tagsNegativeOptions: Array<any> = [];
  crossSellTagOptions: Array<any> = [];
  alertOptions: Array<any> = [];
  teamMembers: Array<any> = [];
  teamMemberIdToCreateAlertFor: number = null;
  totalEnergyKwhOptions: Array<any>;
  gasKwhOptions: Array<any>;
  electricKwhOptions: Array<any>;
  scope1KwhOptions: Array<any>;
  scope2KwhOptions: Array<any>;
  scope3KwhOptions: Array<any>;
  totalEmissionsKgCo2eOptions: Array<any>;
  scope1KgCo2eOptions: Array<any>;
  scope2KgCo2eOptions: Array<any>;
  scope3KgCo2eOptions: Array<any>;
  kgCo2ePerMillionRevenueOptions: Array<any>;
  kgCo2ePerEmployeeOptions: Array<any>;
  patentFiledWithinDaysOptions: Array<any>;
  patentAwardedWithinDaysOptions: Array<any>;
  contractAwardedWithinMonthsOptions: Array<any>;
  contractStartMonthsOptions: Array<any>;
  contractEndMonthsOptions: Array<any>;
  contractValueOptions: Array<any>;
  chargeCreatedMonthLimits: Array<any>;
  companyNameContains = '';
  accountantNameContains = '';
  bankerNameContains = '';
  auditorNameContains = '';
  lawyerNameContains = '';

  selectedAccountants: Array<any> = [];
  selectedBankers: Array<any> = [];
  selectedAuditors: Array<any> = [];
  selectedLawyers: Array<any> = [];

  accountantSearchInput = '';
  bankerSearchInput = '';
  auditorSearchInput = '';
  lawyerSearchInput = '';

  possibleAccountants: Array<any> = [];
  possibleBankers: Array<any> = [];
  possibleAuditors: Array<any> = [];
  possibleLawyers: Array<any> = [];
  loadingSupplierType: string = '';

  selectedCountries: Array<any> = [];
  selectedCompanyCategories: Array<any> = [];
  selectedSICCodes: Array<any> = [];
  excludedSICCodes: Array<any> = [];
  websiteKeywords: Array<any> = [];
  selectedCompanyStatuses: Array<any> = [];
  corporateStructureIsTopLevelParentCompany = false;
  corporateStructureHasParent = false;
  corporateStructureDoesNotHaveParent = false;
  corporateStructureHasChild = false;
  corporateStructureDoesNotHaveChild = false;
  lowerNumberOfUBOs: number = null;
  upperNumberOfUBOs: number = null;
  selectedUBOCountries: Array<any> = [];
  corporateStructureRelatedCompanies = '';
  hasWebsite = false;
  employeeLowerCount: number = null;
  employeeUpperCount: number = null;
  excludeEstimatedHeadcountFigures = false;
  departmentHeadCountLowerCount: number = null;
  departmentHeadCountUpperCount: number = null;
  lowerHeadCountGrowthPercentage: number = null;
  upperHeadCountGrowthPercentage: number = null;
  selectedFunctionalDepartments: Array<any> = [];
  lowerAge: number = null;
  upperAge: number = null;
  postcodeBeginsWith = '';
  postcodeWithinRadius: string = '';
  postcodeDistrict = '';
  ukRegion: Array<string> = [];
  selectedTownId = '';
  mileRadiusOfPostcode: number = null;
  selectedAddressTypeOption: string = '';
  hasOverseasParent = false;
  hasOfficesOverseas = false;
  selectedOfficeCountries: Array<any> = [];
  selectedGeographicalRevenueCountries: Array<any> = [];
  selectedGeographicalRevenueTerritories: Array<any> = [];
  lowerGeographicalRevenueSelected: number = null;
  upperGeographicalRevenueSelected: number = null;
  lowerNumberOfKnownOfficeLocations: number = null;
  upperNumberOfKnownOfficeLocations: number = null;
  hasLEINumber = false;
  selectedLEIStatus = '';
  selectedImportExportCategories: Array<any> = [];
  selectedImportingOrExportingOption = '';
  importExportTimeScale: number = null;
  importExportFrequencyImportingOrExporting = '';
  importExportFrequency: number = null;
  importExportFrequencyMonthCount: number = null;
  selectedWebTechnologies: Array<any> = [];
  webTrafficLow: number = null;
  webTrafficHigh: number = null;
  lowerAverageDirectorAgeRange: number = null;
  upperAverageDirectorAgeRange: number = null;
  lowerNumberOfDirectors: number = null;
  upperNumberOfDirectors: number = null;
  lowerNumberOfPSCs: number = null;
  upperNumberOfPSCs: number = null;
  recentDirectorshipTitleContains: string = '';
  lowerRecentDirectorshipMonth: number = null;
  upperRecentDirectorshipMonth: number = null;
  PSCsRealPersonsOnly = false;
  nationalitiesOfDirectorsSelected: Array<any> = [];
  nationalitiesOfPSCsSelected: Array<any> = [];
  directorJobTitleContains = '';
  lowerDaysUntilFinancialYearEnd: number = null;
  upperDaysUntilFinancialYearEnd: number = null;
  selectedFinancialYearEnds: Array<any> = [];
  lowerDaysUntilAnniversary: number = null;
  upperDaysUntilAnniversary: number = null;
  isHiring = false;
  hiringJobTitleContains = '';
  hiringDescriptionContains = '';
  selectedHiringDayLimit: number = null;
  lowerNumberOfRolesHiringFor: number = null;
  upperNumberOfRolesHiringFor: number = null;
  selectedNumberOfRolesHiringForDayLimit: number = null;
  includeRecruiters = false;
  amazonSeller = false;
  listedOnGCloud = false;
  hasPhoneNumber = false;
  hasEmailAddress = false;
  hasRaisedInvestment = false;
  hasShareFilingEvent = false;
  shareFilingEventWithinDays: number = null;
  lowerShareFilingCapitalValue: number = null;
  upperShareFilingCapitalValue: number = null;
  lowerInvestmentAmount: number = null;
  upperInvestmentAmount: number = null;
  ignoreRevenueEstimates = false;
  lowerAccountsDueSelected: number = null;
  upperAccountsDueSelected: number = null;
  lowerLatestAccountsSubmittedSelected: number = null;
  upperLatestAccountsSubmittedSelected: number = null;
  selectedLatestAccountsGroupOption = '';
  selectedLatestAccountsAuditOption = '';
  accountsAreOverdue = false;
  accountsSearchTerm = '';
  accountsSearchWithinDays: number = null;
  issuedAGrant = false;
  chargeRegistered = false;
  outstandingChargeRegistered = false;
  noOutstandingCharges = false;
  lowerGrantAmount: number = null;
  upperGrantAmount: number = null;
  grantLowerMonthsUntilProjectEnd: number = null;
  grantUpperMonthsUntilProjectEnd: number = null;
  homepageSearchTerm = '';
  fullWebsiteSearchTerm = '';
  selectedEvents: Array<any> = [];
  mileRadiusOfEventsSelected: number = null;
  postcodeEventsWithinRadius = '';
  eventsWithinDaysLower: number = null;
  eventsWithinDaysUpper: number = null;
  newsWithinDays: number = null;
  newsContaining = '';
  newsSource = '';
  newsEventCategories: Array<string> = [];
  newsEventCategoryGroups = NEWS_EVENT_CATEGORY_GROUPS;
  logisticsNewsEventOptions: Array<any> = [];
  trademarkClassIdsSelected: Array<any> = [];
  trademarkDescriptionContains = '';
  trademarkNameContains = '';
  selectedTrademarkStatuses: Array<any> = [];
  excludeCompaniesTaggedByTeam = false;
  tagNegativeIdsSelected: Array<any> = [];
  tagPositiveIdsSelected: Array<any> = [];
  matchAllPositiveTags = false;
  tagSearchTerm = '';
  crossSellTagIdsSelected: Array<any> = [];
  crossSellLinkedThroughPeople = false;
  crossSellLinkedThroughCorporateStructure = false;
  selectedCustomColumns: Array<any> = [];
  unreadAlertsOnly = false;
  lowerAlertsDayRange: number = null;
  upperAlertsDayRange: number = null;
  alertIdsSelected: Array<any> = [];
  isLowEnergy = false;
  lowerTotalEnergyKwhSelected: number = null;
  upperTotalEnergyKwhSelected: number = null;
  lowerGasKwhSelected: number = null;
  upperGasKwhSelected: number = null;
  lowerElectricKwhSelected: number = null;
  upperElectricKwhSelected: number = null;
  lowerScope1KwhSelected: number = null;
  upperScope1KwhSelected: number = null;
  lowerScope2KwhSelected: number = null;
  upperScope2KwhSelected: number = null;
  lowerScope3KwhSelected: number = null;
  upperScope3KwhSelected: number = null;
  lowerTotalEmissionsKgCo2eSelected: number = null;
  upperTotalEmissionsKgCo2eSelected: number = null;
  lowerScope1KgCo2eSelected: number = null;
  upperScope1KgCo2eSelected: number = null;
  lowerScope2KgCo2eSelected: number = null;
  upperScope2KgCo2eSelected: number = null;
  lowerScope3KgCo2eSelected: number = null;
  upperScope3KgCo2eSelected: number = null;
  lowerKgCo2ePerMillionRevenueSelected: number = null;
  upperKgCo2ePerMillionRevenueSelected: number = null;
  lowerKgCo2ePerEmployeeSelected: number = null;
  upperKgCo2ePerEmployeeSelected: number = null;

  hasActivePatent = false;
  hasAnyPatentRecord = false;
  patentFiledWithinDays: number = null;
  lowerPatentAwardedDays: number = null;
  upperPatentAwardedDays: number = null;

  lowerContractValueSelected: number = null;
  upperContractValueSelected: number = null;
  contractAwardedWithinMonths: number = null;
  lowerContractStartMonthsSelected: number = null;
  upperContractStartMonthsSelected: number = null;
  lowerContractEndMonthsSelected: number = null;
  upperContractEndMonthsSelected: number = null;
  hasContract = false;
  contractTitleContains = '';
  contractDescriptionContains = '';

  chargeHolderNameContains = '';
  ignoreSatisfiedCharges = false;
  chargeCreatedMonthsAgoLower: number = null;
  chargeCreatedMonthsAgoUpper: number = null;

  supplierTagSelected: number = null;
  associateWithOneSupplierOnly = false;
  customerTagSelected: number = null;

  similarCompanyUrl = '';

  hasLandTitle: boolean = false;
  landTenure: string = '';
  landCategory: string = '';

  sortKey = '';
  sortDirectionIsAscending = false;

  companyCount = signal<
    Partial<{ count: number; exact: boolean; [key: string]: any }>
  >({ count: 5500000, exact: false });

  sicCodeTextSearch = '';
  isSearchingSicCodes = false;
  searchTags: Array<any> = [];
  activeParamsFromMapper: string[] = [];
  importExportTextSearch = '';

  webTechsTextSearch = '';
  isSearchingWebTechs = false;
  logisticsWebTechnologySearch = '';
  logisticsSicSelectionMode: 'include' | 'exclude' = 'include';
  private preferredLogisticsPostcodeMode: 'prefix' | 'district' | 'radius' =
    'radius';

  checkboxSelectAllCompanies: boolean;
  hasSelectedTotalNumberMatchingFilter = false;
  showSelectedNumbersSection = false;
  totalCompaniesSelected: number = null;

  savedSearchName = '';
  makeSavedSearchShared = false;

  propensityScoreComponentDescription = '';
  propensityWeightingRange: Array<any> = [];
  propensityTAMLimitCount: number = null;
  propensityScoreWeighting: number = null;
  propensityModels: Array<any> = [];
  editablePropensityModels: Array<any> = [];
  selectedPropensityModelId: number = null;
  propensityScoreTAMName = '';
  stringToTriggerNewPropensityModel = 'STRINGTOTRIGGERNEWPROPENSITYMODEL';
  canEditTAM = false;
  selectedPropensityModelIdToUpdateTAM: number = null;

  savedAlertName = '';
  selectedAlertFrequency = '';
  alertFrequencyOptions: Array<any> = [];
  jsonParamKeysNotAllowedForAlertsOrPropensityScore: Array<any> = [];
  alertsDaysRange: Array<any>;

  territoryName = '';

  hasSemanticSearchAccess: boolean = false;
  summarySemanticSearchTerm: string = '';
  // ZIN-5595: concept-level matching is the only search behaviour; the
  // "Precise matches only" toggle was removed, so this is always true.
  summarySemanticSearchConceptMatch: boolean = true;
  // ZIN-5642: continuous match-quality slider. The value is a minimum
  // displayed match-score floor (0-40) applied backend-side: 0 = Reasonable
  // (loosest, all matches, left), 20 = Good (default, middle), 40 = Excellent
  // (strictest, right). Low-to-high left-to-right like every other slider.
  readonly semanticMatchScoreMax: number = 40;
  readonly defaultSemanticMatchScore: number = 20;
  summarySemanticSearchMinScore: number = 20;
  // ZIN-5643: backend sortKey that maps to SEMANTIC_SEARCH_SCORE_SORT
  // (web_app/_constants.py SEMANTIC_SEARCH_SCORE).
  readonly semanticSearchScoreSortKey: string = 'Products & Services Score';
  clusterSearchMode: boolean = false;
  hasClusterSearchAccess: boolean = false;

  dashboardMode: 'full' | 'logistics' | 'tax_accountancy' = 'full';
  logisticsResultsPerspective: LogisticsResultsPerspective = 'overview';
  readonly logisticsResultsPerspectives: Array<{
    id: LogisticsResultsPerspective;
    label: string;
    shortLabel: string;
    description: string;
    icon: string;
  }> = [
    {
      id: 'overview',
      label: 'Overview',
      shortLabel: 'Overview',
      description: 'Balanced operational signals',
      icon: 'fa-grid-2',
    },
    {
      id: 'freight-forwarding',
      label: 'Freight forwarding',
      shortLabel: 'Forwarding',
      description: 'Containers, modes and shipment profile',
      icon: 'fa-truck-fast',
    },
    {
      id: 'third-party-logistics',
      label: 'Third-party logistics',
      shortLabel: '3PL',
      description: 'Warehousing, handling and fulfilment',
      icon: 'fa-warehouse',
    },
    {
      id: 'parcel-courier',
      label: 'Parcel & courier',
      shortLabel: 'Courier',
      description: 'Parcel demand, channels and returns',
      icon: 'fa-box-taped',
    },
    {
      id: 'customs-trade',
      label: 'Customs & trade',
      shortLabel: 'Customs',
      description: 'Countries, modes and shipment complexity',
      icon: 'fa-globe-europe',
    },
  ];
  availableDashboardSkins: string[] = [];
  showLogisticsStickySearch = false;
  logisticsFinancialSliders: IConfigSlider[] = [];
  logisticsAssetSliders: IConfigSlider[] = [];
  logisticsWebTechnologyOptions: Array<any> = [];
  logisticsFunctionalDepartmentOptions: Array<any> = [];
  readonly logisticsNewsEventGroups: LogisticsNewsEventGroup[] =
    LOGISTICS_NEWS_EVENT_GROUPS;
  logisticsMagicInsightSections: LogisticsMagicInsightSection[] =
    buildLogisticsMagicInsightSections([]);
  activeLogisticsServiceSection = 'general';
  private logisticsSliderPresentationConfigs = new WeakMap<
    Record<string, any>,
    IConfigSlider
  >();

  getLogisticsSliderConfig(filter: Record<string, any>): IConfigSlider {
    if (!this.logisticsSliderPresentationConfigs.has(filter)) {
      this.logisticsSliderPresentationConfigs.set(filter, {
        ...filter,
        formName: filter.label || filter.formName,
      });
    }
    return this.logisticsSliderPresentationConfigs.get(filter);
  }

  getLogisticsSliderDisplayName(filter: Record<string, any>): string {
    const label = filter.formName || filter.label || '';
    const unit = this.getLogisticsSliderUnit(filter);
    return unit && !label.toLowerCase().includes(unit.toLowerCase())
      ? `${label} (${unit})`
      : label;
  }

  private getLogisticsSliderUnit(filter: Record<string, any>): string {
    if (filter.prefix?.includes('£') || filter.label === 'VehiclesTangibleAssets') {
      return '£';
    }
    if (filter.postfix?.includes('%')) {
      return '%';
    }
    const postfix = String(filter.postfix || '').trim().toLowerCase();
    if (/\bmonths?\b/.test(postfix)) return 'months';
    if (/\byears?\b/.test(postfix)) return 'years';
    if (/\bdays?\b/.test(postfix)) return 'days';
    if (/\bmiles?\b/.test(postfix)) return 'miles';
    return '';
  }

  get activeLogisticsMagicInsightSection(): LogisticsMagicInsightSection {
    return (
      this.logisticsMagicInsightSections.find(
        section => section.id === this.activeLogisticsServiceSection
      ) || this.logisticsMagicInsightSections[0]
    );
  }

  get logisticsPostcodeMode(): 'prefix' | 'district' | 'radius' {
    if (this.postcodeBeginsWith) return 'prefix';
    if (this.postcodeDistrict) return 'district';
    if (this.postcodeWithinRadius) return 'radius';
    return this.preferredLogisticsPostcodeMode;
  }

  compareTerritories = (
    left: Record<string, any> | null,
    right: Record<string, any> | null
  ): boolean => {
    if (!left || !right) return left === right;
    return left.id === right.id || left.name === right.name;
  };

  setLogisticsPostcodeMode(mode: 'prefix' | 'district' | 'radius'): void {
    this.preferredLogisticsPostcodeMode = mode;
    this.postcodeBeginsWith = mode === 'prefix' ? this.postcodeBeginsWith : '';
    this.postcodeDistrict = mode === 'district' ? this.postcodeDistrict : '';
    this.postcodeWithinRadius = mode === 'radius' ? this.postcodeWithinRadius : '';
    this.selectedTownId = '';
    this.townName = '';
    if (mode !== 'radius') {
      this.mileRadiusOfPostcode = null;
    }
    this.updateAddressTypeOnInputChange();
  }

  setLogisticsSicSelection(
    option: Record<string, any>,
    selected: boolean
  ): void {
    const selectedProperty =
      this.logisticsSicSelectionMode === 'include' ? 'checked' : 'excluded';
    const opposingProperty =
      this.logisticsSicSelectionMode === 'include' ? 'excluded' : 'checked';
    option[selectedProperty] = selected;
    if (selected) {
      option[opposingProperty] = false;
    }
  }

  isLogisticsSicSelected(option: Record<string, any>): boolean {
    return this.logisticsSicSelectionMode === 'include'
      ? Boolean(option.checked)
      : Boolean(option.excluded);
  }

  isLogisticsWebTechnologyExpanded(option: Record<string, any>): boolean {
    if (!option?.children?.length) return false;
    return this.logisticsWebTechnologyOptions.some(
      technology =>
        option.children.includes(technology.name) && technology.shouldShow
    );
  }

  toggleLogisticsWebTechnologyCategory(option: Record<string, any>): void {
    this.expandOrCollapseItems(option.children || [], 'webTechnologyOptions');
  }

  searchLogisticsWebTechnologies(): void {
    const term = this.logisticsWebTechnologySearch.trim().toLowerCase();
    if (term.length < 2) {
      this.resetLogisticsWebTechnologySearch();
      return;
    }

    const optionsByName = new Map(
      this.logisticsWebTechnologyOptions.map(option => [option.name, option])
    );
    this.logisticsWebTechnologyOptions.forEach(option => {
      option.shouldShow = Boolean(option.description?.toLowerCase().includes(term));
    });
    this.logisticsWebTechnologyOptions
      .filter(option => option.level === 0)
      .forEach(root => {
        const matchingChildren = (root.children || [])
          .map(name => optionsByName.get(name))
          .filter(child => child?.shouldShow);
        if (root.shouldShow) {
          (root.children || []).forEach(name => {
            const child = optionsByName.get(name);
            if (child) child.shouldShow = true;
          });
        } else {
          root.shouldShow = matchingChildren.length > 0;
        }
      });
  }

  resetLogisticsWebTechnologySearch(): void {
    this.logisticsWebTechnologySearch = '';
    this.logisticsWebTechnologyOptions.forEach(option => {
      option.shouldShow = option.level === 0;
    });
  }

  get hasLogisticsWebTechnologyMatches(): boolean {
    return this.logisticsWebTechnologyOptions.some(option => option.shouldShow);
  }

  private buildLogisticsWebTechnologyTree(): void {
    const rootOptions = selectOptionsByValue(
      this.webTechnologyOptions,
      LOGISTICS_WEB_TECHNOLOGY_CATEGORIES,
      option => option.description
    ).filter(option => option.level === 0);
    const includedNames = new Set(rootOptions.map(option => option.name));
    const pendingNames = rootOptions.flatMap(option => option.children || []);

    while (pendingNames.length) {
      const name = pendingNames.pop();
      if (includedNames.has(name)) continue;
      includedNames.add(name);
      const matchingOption = this.webTechnologyOptions.find(
        option => option.name === name
      );
      pendingNames.push(...(matchingOption?.children || []));
    }

    this.logisticsWebTechnologyOptions = this.webTechnologyOptions.filter(
      option => includedNames.has(option.name)
    );
    this.resetLogisticsWebTechnologySearch();
  }

  isLogisticsNewsOptionSelected(categoryNames: string[]): boolean {
    return this.newsEventCategoryGroups
      .flatMap(group => group.subcategories)
      .some(
        option => categoryNames.includes(option.name) && option.checked
      );
  }

  setLogisticsNewsOption(
    categoryNames: string[],
    selected: boolean
  ): void {
    this.newsEventCategoryGroups
      .flatMap(group => group.subcategories)
      .filter(option => categoryNames.includes(option.name))
      .forEach(option => (option.checked = selected));
  }

  setActiveLogisticsServiceSection(sectionId: string): void {
    this.activeLogisticsServiceSection = sectionId;
  }

  setLogisticsResultsPerspective(
    perspective: LogisticsResultsPerspective
  ): void {
    this.logisticsResultsPerspective = perspective;
    try {
      window.localStorage.setItem(
        'zint-logistics-results-perspective',
        perspective
      );
    } catch (_) {
      // A blocked storage API should not prevent the view from changing.
    }
    this.cdr.markForCheck();
  }

  private restoreLogisticsResultsPerspective(): void {
    try {
      const savedPerspective = window.localStorage.getItem(
        'zint-logistics-results-perspective'
      ) as LogisticsResultsPerspective | null;
      if (
        savedPerspective &&
        this.logisticsResultsPerspectives.some(
          perspective => perspective.id === savedPerspective
        )
      ) {
        this.logisticsResultsPerspective = savedPerspective;
      }
    } catch (_) {
      // Continue with the balanced overview when storage is unavailable.
    }
  }

  get canUseLogisticsSkin(): boolean {
    return (
      this.dashboardMode === LOGISTICS_DASHBOARD_SKIN ||
      this.route.snapshot.data['dashboardSkin'] === LOGISTICS_DASHBOARD_SKIN ||
      this.availableDashboardSkins.includes(LOGISTICS_DASHBOARD_SKIN)
    );
  }

  get canUseTaxAccountancySkin(): boolean {
    return (
      this.route.snapshot.data['dashboardSkin'] === 'tax_accountancy' ||
      this.availableDashboardSkins.includes('tax_accountancy')
    );
  }

  openTaxAccountancyDashboard(): void {
    if (!this.canUseTaxAccountancySkin) return;
    // The focused view has fewer controls than Full. Carry the named lookup
    // and start the other criteria fresh, so no hidden filter affects results.
    const name = this.companyNameContains?.trim();
    const queryParams = name ? { companyNameContains: name } : {};
    this.router.navigate(['/dashboard/tax-accountancy'], {
      queryParams,
      info: { dashboardModeSwitch: true },
    });
  }

  get logisticsActiveFilterCount(): number {
    return countActiveLogisticsFilters(this.searchTags);
  }

  switchDashboardMode(mode: 'full' | 'logistics'): void {
    if (mode === 'logistics' && !this.canUseLogisticsSkin) {
      return;
    }

    const path = mode === 'logistics' ? '/dashboard/logistics' : '/dashboard';
    this.refreshSearchTagsFromCurrentFilters();
    const queryParams = JSON.parse(this.getJSONParamsToSave(false));
    delete queryParams.resultsMode;
    this.authenticationService.setDefaultSkin(mode).subscribe({
      next: () => this.router.navigate([path], {
        queryParams,
        info: { dashboardModeSwitch: true },
      }),
      error: () => this.pubMethods.showInfoMessage('Could not save your dashboard preference. Please try again.'),
    });
  }

  private restoreVisibleSliderValues(): void {
    this.mapKeyToSliderElement();
    this.rebuildAllConfigSlidersChildren();

    Object.keys(this.keyToSliderElementMap).forEach(key => {
      const value = this[key];
      if (value !== null && value !== undefined) {
        this.seeIfNeedToUpdateSlider(key, value);
      }
    });

    Object.entries(this.sliderValues).forEach(([label, values]) => {
      if (values.lower !== null && values.lower !== undefined) {
        this.seeIfNeedToUpdateSlider(`lower${label}`, values.lower);
      }
      if (values.upper !== null && values.upper !== undefined) {
        this.seeIfNeedToUpdateSlider(`upper${label}`, values.upper);
      }
    });
  }

  hasAIRAAccess: boolean = false;
  hasPAFAccess: boolean = false;
  hasNYBAccess: boolean = false;
  selectedModules: Record<string, any>[] = []; //has AIRA modules checkboxes
  selectedModulesByName: Record<string, Record<string, any>> = {};

  getRandomPlaceholderCompanyName(placeholderRowIndex: number = 0): string {
    const adjectives = [
      'Blue',
      'Green',
      'Silver',
      'Prime',
      'Swift',
      'Global',
      'Northern',
      'Quantum',
      'Dynamic',
      'Pioneer',
      'Vertex',
      'Summit',
      'Crescent',
      'Harbor',
      'Oakwood',
    ];
    const nouns = [
      'Holdings',
      'Solutions',
      'Industries',
      'Ventures',
      'Group',
      'Partners',
      'Enterprises',
      'Systems',
      'Logistics',
      'Capital',
      'Consulting',
      'Manufacturing',
      'Services',
      'Technologies',
      'Investments',
    ];

    // using random prime numbers for scrambling
    const base = Math.abs(placeholderRowIndex + 7);
    const adj = adjectives[(base * 17 + 11) % adjectives.length];
    const noun = nouns[(base * 31 + 7) % nouns.length];
    return `${adj} ${noun} LTD`;
  }

  getPlaceholderRowCount(): number {
    const locked = this.companyCount()?.locked_mi_companies || 0;
    const pageSize = this.selectedPageSizeLimit || 0;
    const current = this.companies ? this.companies.length : 0;
    if (locked <= 0 || pageSize <= 0) return 0;
    const remaining = pageSize - current;
    if (remaining <= 0) return 0;
    // Fill up to page size unless total would be less than a full page
    return Math.min(locked, remaining);
  }

  getPlaceholderRowIndices(): number[] {
    const n = this.getPlaceholderRowCount();
    return Array.from({ length: n }, (_, i) => i);
  }

  unlockMagicInsightsFromResults(): void {
    this.openMagicInsightsUnlockModal();
  }

  unlockPlaceholderMagicInsights(): void {
    const lockedOnPage = this.getPlaceholderRowCount();
    this.openMagicInsightsUnlockModal(lockedOnPage || null);
  }

  refreshAiraConfigs(): void {
    this.dashboardService
      .getAiraFilterConfig(this.magicInsightsResultsMode())
      .subscribe(configData => {
        this.updateAiraFilterDetails(configData);
      });
  }

  private magicInsightsResultsMode(): string | undefined {
    return this.dashboardMode === LOGISTICS_DASHBOARD_SKIN ||
      this.dashboardMode === 'tax_accountancy'
      ? this.dashboardMode
      : undefined;
  }

  private openMagicInsightsUnlockModal(presetCount?: number | null): void {
    const count = presetCount ?? this.companyCount().count;
    if (this.isLoading().loadingCompanies || this.isLoading().loadingCompanyCount ||
        !validSelectionCount(count, this.companyCount().count)) return;
    // This shortcut includes hidden matches; do not overwrite the visible-row selection.
    this.actionSelection = Object.freeze({ mode: 'query', count, scope: 'all_matching',
      searchParams: this.displayedSearchParams });
    this.cdr.detectChanges();
    this.getInsights.open(this.getActiveFilteredModuleId());
  }

  openGetInsights(moduleId: number = null): void {
    if (!this.captureActionSelection()) return;
    this.cdr.detectChanges();
    this.getInsights.open(moduleId ?? this.getActiveFilteredModuleId());
  }

  // If the user has already narrowed the search to a specific Magic Insights
  // module's filter, that module is the obvious default -- re-showing the full
  // picker would make them pick something they already chose once. When more
  // than one module filter is active, default to whichever comes up first.
  private getActiveFilteredModuleId(): number | null {
    return this.selectedModules.find(
      module => module.checked || module.excludeAnswered
    )?.id ?? null;
  }

  private resetMagicInsightsUnlockPreset(): void {
    this.dataStore.setMagicInsightsUnlockCount(null);
  }

  handleGetInsightsCompleted(event: any): void {
    if (!event?.isOrganisationModule) {
      this.searchCompaniesAfterFilters(false);
    }
  }

  columnsToHide: Array<any> = [
    'id',
    'number',
    'isPlaceholderCompany',
    'hasViewed',
    'hasMIAvailable',
    '_logisticsInsights',
  ];

  pageNumber = 1;
  maxPageNumber: number = null;

  isSuperUser: boolean;
  emailOnAccount: any;
  isReadOnlyUser: any;
  hasPropensityScoreAccess: any;
  hasDeepCrawlAccess: any;
  hasEnergyAndEmissionsAccess: any;
  hasPatentsAccess: any;
  hasShareFilingsAccess: any;
  hasGovernmentContractsAccess: any;
  hasCompanyChargesAccess: any;
  hasCrossSellAccess: any;
  hasRecentAccountsAccess: any;
  hasSupplierDataAccessMessage: any;
  hasTerritoryAssignmentAccessMessage: any;
  hasHubspotAccess: any;
  maxPageSizeLimit: number;
  selectedPageSizeLimit: number;

  isSidebarCollapsed = signal(false);
  mobileSidebarRolledUp = false;
  mobileSidebarBtnFixedTop = false;
  expandedFilterSections: Record<string, boolean> = {};
  confidenceOpen: Record<string, boolean> = {};
  hoveredOverflowingTables: Record<string, any> = {};

  townName: string = '';
  possibleTownNames: Array<any>;

  websiteKeywordInput: string = '';
  possibleWebsiteKeywords: Array<any>;

  eventNameSearchInput: string = '';
  possibleMatchingEvents: Array<any>;

  customColumns: Array<any>;
  customColumnSearchTerm = '';
  filteredCustomColumns: Array<any> = [];
  customColumnNamesToObjects: any = {};

  hoveredColIndex: number = null;
  currentHoverColumnName = '';
  currentHoverRowName = '';
  currentlyEditingColumnName = '';
  currentlyEditingRowName = '';

  tableHeaderColumnNumberCurrentlyClicked: number = null;
  tableColumnNumberCurrentlyHovered: number = null;
  wrappedTableColumnNumbers: Array<number> =
    JSON.parse(localStorage.getItem('zintWrappedColumns')) || [];
  teamMembersForCustomColumns: Array<any> = [];
  columnNamesToWidths: any = {};
  clickedRow: number = null;
  clickedColumn: number = null;

  geographyPostcodeBeginsWithIsEditing = false;
  geographyPostcodeDistrictIsEditing = false;
  geographyWithinRadiusOfPostcodeIsEditing = false;
  geographyWithinRadiusOfTownIsEditing = false;

  selectAllVisibleFunctionalDepartments = false;
  functionalDepartmentTextSearch = '';
  isSearchingFunctionalDepartments = false;
  hasSearchedFunctionalDepartment = false;

  geographicalRevenueTextSearch = '';

  individualViews: Array<any> = [];
  organisationViews: Array<any> = [];
  selectedView: Record<string, any> = null;
  loadResultsView = false;

  availableTerritories: Array<any> = [];
  areTerritoriesEnforced: false;
  selectedTerritory: Record<string, any> = null;

  sliderValues: Record<string, any> = {};
  configTextInputValues: Record<string, any> = {};
  configMultiSelectValues: Record<string, any> = {};
  confidenceValues: Record<string, { lower: number | null; upper: number | null }> = {};

  savedSearchParamName: string = '';

  resultsActionModalTitle: string = '';

  availableCRMList: Record<string, any>[] = [];
  crmExporterName: string = '';
  isCRMExportDisabled = false;

  filterSearchTerm: string = '';
  filterSearchErrorMsg: string = '';

  dynamicFilterIdToKeywordsMappings: Record<string, string[]> = {}; // <-- ADD THIS

  profilePreviewIsOpen = signal(false);

  @ViewChild('profilePreview') previewDrawer: MatDrawer;
  @ViewChild('getInsights') getInsights: GetInsightsComponent;
  @ViewChild('numberUBOsSliderElement')
  numberUBOsSliderElement: ZintNgxSliderComponent;
  @ViewChild('companyAgeSliderElement')
  companyAgeSliderElement: ZintNgxSliderComponent;
  @ViewChild('mileRadiusSliderElement')
  mileRadiusSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfKnownOfficeLocationsSliderElement')
  numberOfKnownOfficeLocationsSliderElement: ZintNgxSliderComponent;
  @ViewChild('geographicalRevenueSliderElement')
  geographicalRevenueSliderElement: ZintNgxSliderComponent;
  @ViewChild('accountsDueSliderElement')
  accountsDueSliderElement: ZintNgxSliderComponent;
  @ViewChild('latestAccountsSubmittedSliderElement')
  latestAccountsSubmittedSliderElement: ZintNgxSliderComponent;
  @ViewChild('investmentRaisedSliderElement')
  investmentRaisedSliderElement: ZintNgxSliderComponent;
  @ViewChild('shareFilingEventWithinDaysSliderElement')
  shareFilingEventWithinDaysSliderElement: ZintNgxSliderComponent;
  @ViewChild('shareFilingCapitalValueSliderElement')
  shareFilingCapitalValueSliderElement: ZintNgxSliderComponent;
  @ViewChild('grantAmountSliderElement')
  grantAmountSliderElement: ZintNgxSliderComponent;
  @ViewChild('grantMonthsUntilProjectEndSliderElement')
  grantMonthsUntilProjectEndSliderElement: ZintNgxSliderComponent;
  @ViewChild('headcountSliderElement')
  headcountSliderElement: ZintNgxSliderComponent;
  @ViewChild('headcountGrowthSliderElement')
  headcountGrowthSliderElement: ZintNgxSliderComponent;
  @ViewChild('departmentHeadcountSliderElement')
  departmentHeadcountSliderElement: ZintNgxSliderComponent;
  @ViewChild('averageDirectorAgeSliderElement')
  averageDirectorAgeSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfDirectorsSliderElement')
  numberOfDirectorsSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfPSCsSliderElement')
  numberOfPSCsSliderElement: ZintNgxSliderComponent;
  @ViewChild('recentDirectorshipMonthRangeSliderElement')
  recentDirectorshipMonthRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('importExportSliderElement')
  importExportSliderElement: ZintNgxSliderComponent;
  @ViewChild('importExportFrequencySliderElement')
  importExportFrequencySliderElement: ZintNgxSliderComponent;
  @ViewChild('importExportFrequencyMonthCountSliderElement')
  importExportFrequencyMonthCountSliderElement: ZintNgxSliderComponent;
  @ViewChild('websiteVisitorsSliderElement')
  websiteVisitorsSliderElement: ZintNgxSliderComponent;
  @ViewChild('hiringDayLimitSliderElement')
  hiringDayLimitSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfRolesHiringForSliderElement')
  numberOfRolesHiringForSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfRolesHiringForDayLimitSliderElement')
  numberOfRolesHiringForDayLimitSliderElement: ZintNgxSliderComponent;
  @ViewChild('daysUntilFinancialYearEndSliderElement')
  daysUntilFinancialYearEndSliderElement: ZintNgxSliderComponent;
  @ViewChild('daysUntilAnniversarySliderElement')
  daysUntilAnniversarySliderElement: ZintNgxSliderComponent;
  @ViewChild('eventsMileRadiusSliderElement')
  eventsMileRadiusSliderElement: ZintNgxSliderComponent;
  @ViewChild('eventsDayRangeSliderElement')
  eventsDayRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('newsDayRangeSliderElement')
  newsDayRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('accountsSearchDayRangeSliderElement')
  accountsSearchDayRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('alertsDayRangeSliderElement')
  alertsDayRangeSliderElement: ZintNgxSliderComponent;

  @ViewChild('totalEnergyKwhSliderElement')
  totalEnergyKwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('gasKwhSliderElement')
  gasKwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('electricKwhSliderElement')
  electricKwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope1KwhSliderElement')
  scope1KwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope2KwhSliderElement')
  scope2KwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope3KwhSliderElement')
  scope3KwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('totalEmissionsKgCo2eSliderElement')
  totalEmissionsKgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope1KgCo2eSliderElement')
  scope1KgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope2KgCo2eSliderElement')
  scope2KgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope3KgCo2eSliderElement')
  scope3KgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('kgCo2ePerMillionRevenueSliderElement')
  kgCo2ePerMillionRevenueSliderElement: ZintNgxSliderComponent;
  @ViewChild('kgCo2ePerEmployeeSliderElement')
  kgCo2ePerEmployeeSliderElement: ZintNgxSliderComponent;
  @ViewChild('patentFiledWithinDaysSliderElement')
  patentFiledWithinDaysSliderElement: ZintNgxSliderComponent;
  @ViewChild('patentAwardedWithinDaysSliderElement')
  patentAwardedWithinDaysSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractValueSliderElement')
  contractValueSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractAwardedWithinMonthsSliderElement')
  contractAwardedWithinMonthsSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractStartMonthsSliderElement')
  contractStartMonthsSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractEndMonthsSliderElement')
  contractEndMonthsSliderElement: ZintNgxSliderComponent;
  @ViewChild('chargeCreatedSliderElement')
  chargeCreatedSliderElement: ZintNgxSliderComponent;

  @ViewChildren('heatScoreSliders')
  heatScoreSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('fleetSliders')
  fleetSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('wagesSliders')
  wagesSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('numberOfActiveTrademarksSliders')
  numberOfActiveTrademarksSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('tanAssetsSliders')
  tanAssetsSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('intanAssetsSliders')
  intanAssetsSliders: QueryList<ZintNgxSliderComponent>;

  @ViewChildren('genericConfigSliders')
  genericConfigSliders: QueryList<ZintNgxSliderComponent>;

  @ViewChildren('confidenceSliders')
  confidenceSliders: QueryList<ZintNgxSliderComponent>;

  allConfigSlidersChildren: ZintNgxSliderComponent[] = [];

  // Buffer for slider updates when components are not yet rendered
  pendingSliderUpdates: Record<
    string,
    { value: number; airaQuestionId?: number | string | null; airaFieldName?: string | null }
  > = {};

  bufferSliderUpdate(
    key: string,
    value: number,
    airaQuestionId: number | string | null = null,
    airaFieldName: string | null = null
  ): void {
    this.pendingSliderUpdates[key] = { value, airaQuestionId, airaFieldName };
  }

  rebuildAllConfigSlidersChildren(): void {
    this.allConfigSlidersChildren = [
      ...this.tanAssetsSliders,
      ...this.intanAssetsSliders,
      ...this.heatScoreSliders,
      ...this.fleetSliders,
      ...this.wagesSliders,
      ...this.numberOfActiveTrademarksSliders,
      ...this.genericConfigSliders,
    ];
  }

  applyPendingSliderUpdates(): void {
    this.rebuildAllConfigSlidersChildren()
    const entries = Object.entries(this.pendingSliderUpdates);
    if (!entries.length) return;
    entries.forEach(([key, entry]) => {
      delete this.pendingSliderUpdates[key];
      this.seeIfNeedToUpdateSlider(
        key, entry.value, entry.airaQuestionId, entry.airaFieldName
      );
    });
    this.refreshSearchTagsFromCurrentFilters()
  }

  refreshSearchTagsFromCurrentFilters(): void {
    try {
      this.updateFilters();
      const params = this.allFilterParams();
      // populate search tags
      this.searchTags = JSON.parse(JSON.stringify(params)).updates;
      this.getActiveSearchParams();
    } catch (_) {
    }
  }


  isLoading = this.dashboardService.isLoading;
  isLoadingZintPropensityScore = this.zintPropensityScoreService.isLoading;

  MIAvailabilityStatus = {
    NoMI: 0,
    hasMI: 1
  } as const;

  hasLoadedFlags = {
    hasLoadedUIData: signal(false),
    hasLoadedUserTags: signal(false),
    hasLoadedUserCrossSellTags: signal(false),
    hasLoadedCustomColumns: signal(false),
    hasLoadedAlertOptions: signal(false),
    hasLoadedBackendSliders: signal(false),
    hasLoadedAiraFilters: signal(false),
  };

  allEndpointsLoaded = computed(() => {
    const allFlags = Object.values(this.hasLoadedFlags).map(signalFn =>
      signalFn()
    );
    return allFlags.every(val => val);
  });

  freezeHeadersIsActive = false;
  isWrapAllColumns =
    (JSON.parse(localStorage.getItem('zintAllColumnsWrapped')) as boolean) ||
    false;
  companyResultsTableHeaderRowMarginLeft = 0;
  isCompanyResultsTableHeaderRowFixed = false;
  resultTableScrollLeft: number = 0;
  showFakeCompanyNameAndCheckboxSection = false;
  horizontalScrollCutoffToShowFakeHeaders = 14;
  lastTableHorizontalPosition = 0;
  startingHeaderRowOffsetTop: number = null;

  zintSlidersConfigs: Record<string, any> = {};
  zintTextInputConfigs: Record<string, any> = {};
  zintMultiSelectConfigs: Record<string, any> = {};
  sliderFieldLabels: string[] = [];

  keyToSliderElementMap: Record<string, any> = {};

  tangibleCategories = [
    'Land And Buildings',
    'Plant And Machinery',
    'Furniture And Fittings',
    'Vehicles',
    'Computer Equipment',
    'Leasehold Assets',
    'Assets Under Construction',
    'Other',
  ];

  inTangibleCategories = [
    'Goodwill',
    'Computer Software',
    'Patents, Trademarks, and Licences',
    'Other Intangible Assets',
  ];

  landOwnershipAndDevelopmentSliders = [];
  sliderSections = []; // this should be renamed to financial sections
  rawAiraFiltersData = [];
  linkedAiraFilters: Record<string, any> = {};
  readyMadeMagicInsightSections: Array<{ key: string; value: any }> = [];
  customMagicInsightSections: Array<{ key: string; value: any }> = [];
  // Metadata keys to exclude from filter rendering in AIRA modules
  airaMetadataKeys = new Set(['id', 'hasFilterAccess', 'isEditable', 'isOrganisationModule', 'newQuestion', 'answerCount', 'companiesCount', 'lastAnswerGeneratedAt']);
  magicCountriesList = extendedCountriesList;
  magicModuleNamesIds: Record<string, any>[] = [];
  nonEditableMagicModuleNamesIds: Record<string, any>[] = [];
  editableMagicModuleNamesIds: Record<string, any>[] = [];
  organisationMagicModuleNamesIds: Record<string, any>[] = [];

  isScreenTooSmall = computed(() =>
    this.pubMethods.isScreenSmallerThanBreakpoint(650)()
  );
  previewDrawerWidth: Signal<string> = signal('820px');

  ngOnInit() {
    const requestedSkin = this.route.snapshot.data['dashboardSkin'];
    // The selected Tax play and results mode control presentation and access,
    // not the restored company criteria.
    const {
      play: _taxPlay,
      resultsMode: _taxResultsMode,
      ...taxSearchParams
    } = this.route.snapshot.queryParams;
    this.urlQueryParams = requestedSkin === 'tax_accountancy'
      ? taxSearchParams : this.route.snapshot.queryParams;
    this.dashboardMode = requestedSkin === LOGISTICS_DASHBOARD_SKIN
      ? 'logistics'
      : requestedSkin === 'tax_accountancy'
        ? 'tax_accountancy'
        : 'full';
    this.syncLogisticsPageSkin();
    this.restoreLogisticsResultsPerspective();

    this.zintSlidersConfigs = {
      tangibleAssets,
      intangibleAssets,
      heatScore,
      fleet,
      wages,
      numberOfActiveTrademarks,
      esosNotifications,
      epcEnergy,
    };
    this.sliderFieldLabels = Object.values(this.zintSlidersConfigs)
      .flat()
      .map(asset => asset.label);
    this.syncLogisticsSliderConfigs();

    this.dynamicFilterIdToKeywordsMappings = { ...filterIdToKeywordsMappings };

    this.bindScrollBars();
    this.updateScrollBarDimensions();
    this.getSliderDetails();
    this.getUIDataAndUpdateCompanies(); // this is the master function. updates all UI components and updates companies.
    this.taggingService
      .getUserTags(false)
      .subscribe(output => this.updateTagOptions(output));
    this.taggingService
      .getUserCrossSellTags(false)
      .subscribe(output => this.updateCrossSellTagOptions(output));
    this.customColumnService
      .getCustomColumns()
      .subscribe(output => this.updateCustomColumnsAndColumnWidths(output));
    this.alertsService
      .getBasicAlertDetails()
      .subscribe(output => this.updateAlerts(output));
    this.zintPropensityScoreService
      .getUserPropensityScoreDetails(false)
      .subscribe(output => this.updateZintPropensityScoreDetails(output));
    this.resultsFormatEditorService
      .getBasicUserResultsFormat()
      .subscribe(data => this.updateAvailableUserViews(data));
    if (this.dashboardMode === 'full') {
      this.companyCount().count = 5500000;
      this.companyCount().exact = false;
      this.getCompanyCount(true);
    } else {
      this.companyCount.set({ count: 0, exact: true });
    }
    this.communicationService.getIsSuperUserMessage().subscribe(isSuperUser => {
      this.isSuperUser = isSuperUser;
    });
    this.communicationService
      .getAvailableDashboardSkins()
      .subscribe(availableDashboardSkins => {
        this.availableDashboardSkins = availableDashboardSkins;
      });
    this.communicationService.getEmailMessage().subscribe(accountEmail => {
      this.emailOnAccount = accountEmail;
    });
    this.communicationService
      .getIsReadOnlyUserMessage()
      .subscribe(isReadOnlyUser => {
        this.isReadOnlyUser = isReadOnlyUser;
      });
    this.communicationService
      .getHasPropensityScoreAccessMessage()
      .subscribe(hasPropensityScoreAccess => {
        this.hasPropensityScoreAccess = hasPropensityScoreAccess;
      });
    this.communicationService
      .getHasDeepCrawlAccessMessage()
      .subscribe(hasDeepCrawlAccess => {
        this.hasDeepCrawlAccess = hasDeepCrawlAccess;
      });
    this.communicationService
      .getHasEnergyAndEmissionsAccessMessage()
      .subscribe(hasEnergyAndEmissionsAccess => {
        this.hasEnergyAndEmissionsAccess = hasEnergyAndEmissionsAccess;
      });
    this.communicationService
      .getHasPatentsAccessMessage()
      .subscribe(hasPatentsAccess => {
        this.hasPatentsAccess = hasPatentsAccess;
      });
    this.communicationService
      .getHasShareFilingsAccessMessage()
      .subscribe(hasShareFilingsAccess => {
        this.hasShareFilingsAccess = hasShareFilingsAccess;
      });
    this.communicationService
      .getHasGovernmentContractsAccessMessage()
      .subscribe(hasGovernmentContractsAccess => {
        this.hasGovernmentContractsAccess = hasGovernmentContractsAccess;
      });
    this.communicationService
      .getHasCompanyChargesAccessAccessMessage()
      .subscribe(hasCompanyChargesAccess => {
        this.hasCompanyChargesAccess = hasCompanyChargesAccess;
      });
    this.communicationService
      .getHasCrossSellAccessMessage()
      .subscribe(hasCrossSellAccess => {
        this.hasCrossSellAccess = hasCrossSellAccess;
      });
    this.communicationService
      .getHasRecentAccountsAccessMessage()
      .subscribe(hasRecentAccountsAccess => {
        this.hasRecentAccountsAccess = hasRecentAccountsAccess;
      });
    this.communicationService
      .getHasSupplierDataAccessMessage()
      .subscribe(hasSupplierDataAccessMessage => {
        this.hasSupplierDataAccessMessage = hasSupplierDataAccessMessage;
      });
    this.communicationService.getHubspotAccess().subscribe(hasHubspotAccess => {
      this.hasHubspotAccess = hasHubspotAccess;
    });
    this.communicationService
      .getHasTerritoryAssignmentAccessMessage()
      .subscribe(hasTerritoryAssignmentAccessMessage => {
        this.updateTerritoryAssignmentDetails(
          hasTerritoryAssignmentAccessMessage
        );
      });
    this.communicationService
      .getHasSemanticSearchAccessMessage()
      .subscribe(hasSemanticSearchAccessMessage => {
        this.hasSemanticSearchAccess = hasSemanticSearchAccessMessage;
      });
    this.communicationService
      .getHasClusterSearchAccess()
      .subscribe(hasClusterSearchAccess => {
        this.hasClusterSearchAccess = hasClusterSearchAccess;
      });
    this.communicationService.getHasAIRAAccess().subscribe(hasAIRAAccess => {
      this.hasAIRAAccess = hasAIRAAccess;
    });
    this.communicationService.getHasPAFAccess().subscribe(hasPAFAccess => {
      this.hasPAFAccess = hasPAFAccess;
    });
    this.communicationService.getHasNYBAccess().subscribe(hasNYBAccess => {
      this.hasNYBAccess = hasNYBAccess;
    });
    this.communicationService
      .getPageLimitFromPlanMessage()
      .subscribe(pageLimit => {
        this.maxPageSizeLimit = pageLimit;
        this.selectedPageSizeLimit =
          this.dashboardMode === 'logistics'
            ? Math.min(10, this.maxPageSizeLimit)
            : this.dashboardMode === 'tax_accountancy'
              ? Math.min(20, this.maxPageSizeLimit)
            : Math.min(50, this.maxPageSizeLimit);
      });

    this.crmService.getAvailableCRMs().subscribe(data => {
      this.availableCRMList = data;
    });

    this.pubMethods.showIEWarningIfRequired();

  }

  ngOnDestroy(): void {
    document.body.classList.remove('logistics-dashboard-skin');
  }

  private syncLogisticsPageSkin(): void {
    document.body.classList.toggle(
      'logistics-dashboard-skin',
      this.dashboardMode === LOGISTICS_DASHBOARD_SKIN
    );
  }

  updateAvailableUserViews(data) {
    this.individualViews = data.individual;
    this.organisationViews = data.organisation;
  }

  layoutForMobile(): void {
    if (this.isScreenTooSmall()) {
      this.disableFixedHeaders();
    }
  }

  toggleFixedHeaders() {
    if (this.freezeHeadersIsActive) {
      this.disableFixedHeaders();
    } else {
      this.enableFixedHeaders();
    }
  }

  enableFixedHeaders() {
    this.freezeHeadersIsActive = true;
    const self = this;

    $('#table-results-section').on('scroll', function () {
      //to show a thin border line when side scrolling
      self.resultTableScrollLeft = $('#table-results-section').scrollLeft();
    });
  }

  disableFixedHeaders() {
    this.freezeHeadersIsActive = false;
    $('#table-results-section').off('scroll');
  }

  bindScrollBars() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(debounceTime(130))
        .subscribe(() => {
          this.ngZone.run(() => this.updateScrollBarDimensions());
        });
    });

    $('.dummy-top-scrollbar-wrapper').mouseup(function () {
      $('#table-results-section').scrollLeft(
        $('.dummy-top-scrollbar-wrapper').scrollLeft()
      );
    });
  }

  mapKeyToSliderElement(): void {
    // this map is to keep the seeIfNeedToUpdateSlider method DRY
    this.keyToSliderElementMap = {
      lowerNumberOfUBOs: this.numberUBOsSliderElement,
      upperNumberOfUBOs: this.numberUBOsSliderElement,
      lowerAge: this.companyAgeSliderElement,
      upperAge: this.companyAgeSliderElement,
      employeeLowerCount: this.headcountSliderElement,
      employeeUpperCount: this.headcountSliderElement,
      lowerHeadCountGrowthPercentage: this.headcountGrowthSliderElement,
      upperHeadCountGrowthPercentage: this.headcountGrowthSliderElement,
      departmentHeadCountLowerCount: this.departmentHeadcountSliderElement,
      departmentHeadCountUpperCount: this.departmentHeadcountSliderElement,
      mileRadiusOfPostcode: this.mileRadiusSliderElement,
      lowerNumberOfKnownOfficeLocations:
        this.numberOfKnownOfficeLocationsSliderElement,
      upperNumberOfKnownOfficeLocations:
        this.numberOfKnownOfficeLocationsSliderElement,
      lowerGeographicalRevenueSelected: this.geographicalRevenueSliderElement,
      upperGeographicalRevenueSelected: this.geographicalRevenueSliderElement,
      importExportTimeScale: this.importExportSliderElement,
      importExportFrequency: this.importExportFrequencySliderElement,
      importExportFrequencyMonthCount:
        this.importExportFrequencyMonthCountSliderElement,
      webTrafficLow: this.websiteVisitorsSliderElement,
      webTrafficHigh: this.websiteVisitorsSliderElement,
      lowerAverageDirectorAgeRange: this.averageDirectorAgeSliderElement,
      upperAverageDirectorAgeRange: this.averageDirectorAgeSliderElement,
      lowerNumberOfDirectors: this.numberOfDirectorsSliderElement,
      upperNumberOfDirectors: this.numberOfDirectorsSliderElement,
      lowerNumberOfPSCs: this.numberOfPSCsSliderElement,
      upperNumberOfPSCs: this.numberOfPSCsSliderElement,
      lowerRecentDirectorshipMonth:
        this.recentDirectorshipMonthRangeSliderElement,
      upperRecentDirectorshipMonth:
        this.recentDirectorshipMonthRangeSliderElement,
      lowerDaysUntilFinancialYearEnd:
        this.daysUntilFinancialYearEndSliderElement,
      upperDaysUntilFinancialYearEnd:
        this.daysUntilFinancialYearEndSliderElement,
      lowerDaysUntilAnniversary: this.daysUntilAnniversarySliderElement,
      upperDaysUntilAnniversary: this.daysUntilAnniversarySliderElement,
      selectedHiringDayLimit: this.hiringDayLimitSliderElement,
      lowerNumberOfRolesHiringFor: this.numberOfRolesHiringForSliderElement,
      upperNumberOfRolesHiringFor: this.numberOfRolesHiringForSliderElement,
      selectedNumberOfRolesHiringForDayLimit:
        this.numberOfRolesHiringForDayLimitSliderElement,
      lowerInvestmentAmount: this.investmentRaisedSliderElement,
      upperInvestmentAmount: this.investmentRaisedSliderElement,
      shareFilingEventWithinDays: this.shareFilingEventWithinDaysSliderElement,
      lowerShareFilingCapitalValue: this.shareFilingCapitalValueSliderElement,
      upperShareFilingCapitalValue: this.shareFilingCapitalValueSliderElement,
      lowerAccountsDueSelected: this.accountsDueSliderElement,
      upperAccountsDueSelected: this.accountsDueSliderElement,
      lowerLatestAccountsSubmittedSelected:
        this.latestAccountsSubmittedSliderElement,
      upperLatestAccountsSubmittedSelected:
        this.latestAccountsSubmittedSliderElement,
      lowerGrantAmount: this.grantAmountSliderElement,
      upperGrantAmount: this.grantAmountSliderElement,
      grantLowerMonthsUntilProjectEnd:
        this.grantMonthsUntilProjectEndSliderElement,
      grantUpperMonthsUntilProjectEnd:
        this.grantMonthsUntilProjectEndSliderElement,
      mileRadiusOfEventsSelected: this.eventsMileRadiusSliderElement,
      eventsWithinDaysLower: this.eventsDayRangeSliderElement,
      eventsWithinDaysUpper: this.eventsDayRangeSliderElement,
      newsWithinDays: this.newsDayRangeSliderElement,
      accountsSearchWithinDays: this.accountsSearchDayRangeSliderElement,
      lowerAlertsDayRange: this.alertsDayRangeSliderElement,
      upperAlertsDayRange: this.alertsDayRangeSliderElement,
      lowerTotalEnergyKwhSelected: this.totalEnergyKwhSliderElement,
      upperTotalEnergyKwhSelected: this.totalEnergyKwhSliderElement,
      lowerGasKwhSelected: this.gasKwhSliderElement,
      upperGasKwhSelected: this.gasKwhSliderElement,
      lowerElectricKwhSelected: this.electricKwhSliderElement,
      upperElectricKwhSelected: this.electricKwhSliderElement,
      lowerScope1KwhSelected: this.scope1KwhSliderElement,
      upperScope1KwhSelected: this.scope1KwhSliderElement,
      lowerScope2KwhSelected: this.scope2KwhSliderElement,
      upperScope2KwhSelected: this.scope2KwhSliderElement,
      lowerScope3KwhSelected: this.scope3KwhSliderElement,
      upperScope3KwhSelected: this.scope3KwhSliderElement,
      lowerTotalEmissionsKgCo2eSelected: this.totalEmissionsKgCo2eSliderElement,
      upperTotalEmissionsKgCo2eSelected: this.totalEmissionsKgCo2eSliderElement,
      lowerScope1KgCo2eSelected: this.scope1KgCo2eSliderElement,
      upperScope1KgCo2eSelected: this.scope1KgCo2eSliderElement,
      lowerScope2KgCo2eSelected: this.scope2KgCo2eSliderElement,
      upperScope2KgCo2eSelected: this.scope2KgCo2eSliderElement,
      lowerScope3KgCo2eSelected: this.scope3KgCo2eSliderElement,
      upperScope3KgCo2eSelected: this.scope3KgCo2eSliderElement,
      lowerKgCo2ePerMillionRevenueSelected:
        this.kgCo2ePerMillionRevenueSliderElement,
      upperKgCo2ePerMillionRevenueSelected:
        this.kgCo2ePerMillionRevenueSliderElement,
      lowerKgCo2ePerEmployeeSelected: this.kgCo2ePerEmployeeSliderElement,
      upperKgCo2ePerEmployeeSelected: this.kgCo2ePerEmployeeSliderElement,
      patentFiledWithinDays: this.patentFiledWithinDaysSliderElement,
      lowerPatentAwardedDays: this.patentAwardedWithinDaysSliderElement,
      upperPatentAwardedDays: this.patentAwardedWithinDaysSliderElement,
      lowerContractValueSelected: this.contractValueSliderElement,
      upperContractValueSelected: this.contractValueSliderElement,
      contractAwardedWithinMonths:
        this.contractAwardedWithinMonthsSliderElement,
      lowerContractStartMonthsSelected: this.contractStartMonthsSliderElement,
      upperContractStartMonthsSelected: this.contractStartMonthsSliderElement,
      lowerContractEndMonthsSelected: this.contractEndMonthsSliderElement,
      upperContractEndMonthsSelected: this.contractEndMonthsSliderElement,
      chargeCreatedMonthsAgoLower: this.chargeCreatedSliderElement,
      chargeCreatedMonthsAgoUpper: this.chargeCreatedSliderElement,
      // ... add any other mappings that are required
    };
  }

  isUpperSlider(key: string): boolean {
    /* check upper as the single sliders now use upperValue */
    const lowerCaseKey = key.toLowerCase();
    if (lowerCaseKey.includes('webtraffic')) {
      // for webTrafficLow and High case
      return !lowerCaseKey.includes('low');
    } else {
      // all other cases
      return !lowerCaseKey.includes('lower');
    }
  }

  updateZintSliderElement(
    sliderToUpdate: IZintSliderElementUpdate,
    airaQuestionId: number | string | null = null
  ): void {
    const { value, key } = sliderToUpdate;
    const isUpper = this.isUpperSlider(key);
    const sliderElement = this.keyToSliderElementMap[key];
    if (!sliderElement) {
      // component not ready yet; buffer for later
      this.bufferSliderUpdate(key, value, airaQuestionId);
      return;
    }
    if (isUpper) {
      sliderElement.updateSliderValues({
        upperUpdate: value,
      });
    } else {
      sliderElement.updateSliderValues({
        lowerUpdate: value,
      });
    }
  }

  updateReusableSlider(
    key: string,
    value: string | number,
    airaQuestionId: number | string | null = null,
    airaFieldName: string | null = null
  ) {
    const propsKey = key.slice(0, 5); // upper or lower
    const updateKey = propsKey === 'lower' ? 'lowerUpdate' : 'upperUpdate';
    const sliderLabel = key.slice(5);
    const airaFilter = this.findAiraConfigFilter(
      airaQuestionId, airaFieldName, 'range_slider'
    );

    // A focused workspace can use a question/field-specific URL key to keep
    // same-labelled Magic Insights controls separate. Remember that key on the
    // matching live config so later slider changes update the restored filter
    // instead of adding a second, label-keyed filter alongside it.
    if (airaFilter && sliderLabel !== airaFilter.label) {
      airaFilter.stateKey = sliderLabel;
    }

    // bail out if not one of the known config sliders
    if (!this.sliderFieldLabels.includes(sliderLabel) && !airaFilter) return;

    // Magic Insights filter labels can collide across modules (e.g. the same
    // "Estimated Currency Volume" slider exists in several FX modules). When an
    // airaQuestionId is supplied (e.g. when reconstructing a saved search) we
    // must match the slider for that specific question; otherwise we can pick a
    // slider belonging to a different module and send the wrong airaQuestionId,
    // which makes the backend return zero results.
    const sliderToUpdate = this.allConfigSlidersChildren?.find(component => {
      const config = component.sliderConfig as IConfigSlider & {
        fieldName?: string; stateKey?: string;
      };
      if (airaFilter) {
        return Number(config?.airaQuestionId) === Number(airaQuestionId) &&
          config?.fieldName === airaFieldName;
      }
      if (config?.label !== sliderLabel) return false;
      if (airaQuestionId != null && config?.airaQuestionId != null) {
        return config.airaQuestionId === airaQuestionId;
      }
      return true;
    });

    if (sliderToUpdate) {
      delete this.pendingSliderUpdates[key];
      if (airaFilter && sliderLabel !== sliderToUpdate.sliderConfig?.label) {
        (sliderToUpdate.sliderConfig as IConfigSlider & { stateKey?: string }).stateKey = sliderLabel;
      }
      sliderToUpdate.updateSliderValues({
        [updateKey]: Number(value),
      });
    } else {
      // Not yet available; buffer for later
      this.bufferSliderUpdate(key, Number(value), airaQuestionId, airaFieldName);
    }
  }

  seeIfNeedToUpdateSlider(
    key: string,
    value: any,
    airaQuestionId: number | string | null = null,
    airaFieldName: string | null = null
  ) {
    let intValue = parseInt(value);

    if (isNaN(intValue) && this.pubMethods.isValidJson(value)) {
      // for aira slider values which are object
      const parsed = JSON.parse(value);
      intValue = parseInt(parsed.value);
      // Carry the question id from the saved filter so the correct module's
      // slider is matched even when the filter label is shared across modules.
      if (parsed && parsed.airaQuestionId != null) {
        airaQuestionId = parsed.airaQuestionId;
        airaFieldName = parsed.fieldName ?? null;
      }

      if (
        parsed &&
        (parsed.hasOwnProperty('lowerConfidence') ||
          parsed.hasOwnProperty('upperConfidence'))
      ) {
        this.updateConfidenceSliderUIFromSavedParam(key, parsed);
      }

    }

    this.updateZintSliderElement({ key, value: intValue }, airaQuestionId);
    if (key !== 'pageNumber') {
      this.updateReusableSlider(key, intValue, airaQuestionId, airaFieldName);
    }
  }


  updateConfidenceSliderUIFromSavedParam(
    key: string,
    parsed: Record<string, any>
  ): void {
    const labelFromKey =
      key.startsWith('lower') || key.startsWith('upper') ? key.slice(5) : key;
    const airaId = parsed?.airaQuestionId;
    if (!airaId || !labelFromKey) return;
    const filter = this.findAiraConfigFilter(airaId, parsed.fieldName);
    const confidenceLabel = filter?.label || labelFromKey;

    const lowerConf =
      parsed.lowerConfidence === undefined || parsed.lowerConfidence === null
        ? null
        : Number(parsed.lowerConfidence);
    const upperConf =
      parsed.upperConfidence === undefined || parsed.upperConfidence === null
        ? null
        : Number(parsed.upperConfidence);

    // Set in confidenceValues
    const confKey = `confidence:${confidenceLabel}Aira${airaId}`;
    this.confidenceValues[confKey] = { lower: lowerConf, upper: upperConf };
    // Then update UI
    this.updateConfidenceSliderUI(airaId, confidenceLabel);
  }

  private findAiraConfigFilter(
    questionId: number | string | null,
    fieldName: string | null,
    filterType?: string
  ): Record<string, any> | undefined {
    if (questionId == null || !fieldName) return undefined;
    return [this.zintSlidersConfigs, this.zintTextInputConfigs, this.zintMultiSelectConfigs]
      .flatMap(configs => Object.values(configs || {}).flat())
      .find((filter: Record<string, any>) =>
        Number(filter.airaQuestionId) === Number(questionId) &&
        filter.fieldName === fieldName &&
        (!filterType || filter.filterType === filterType));
  }

  private configFilterStateKey(filter: Record<string, any>): string {
    return filter?.stateKey || filter?.label;
  }

  updateNonSliderConfigFilters(key: string, value: any) {
    if (key === 'pageSize') return;
    if (this.pubMethods.isValidJson(value)) {
      const parsed = JSON.parse(value);
      if (parsed.filterType === 'text_input') {
        this.updateConfigTextFilters(key, parsed);
      } else if (parsed.filterType === 'multi_select') {
        this.updateConfigMultiSelectFilters(key, parsed);
      }
      // Add more filterType as needed
    }
  }

  updateConfigMultiSelectFilters(key: string, updatedValue: any) {
    const foundObj = this.findAiraConfigFilter(
      updatedValue.airaQuestionId, updatedValue.fieldName, 'multi_select'
    ) || Object.values(this.zintMultiSelectConfigs).flat()
      .find((filter: any) => filter.label === key);
    if (!foundObj?.options) return;
    if (key !== foundObj.label) foundObj.stateKey = key;
    foundObj.options.forEach(option => {
      option.checked = updatedValue.value?.includes(option.value) || false;
    });

    if (updatedValue.hasOwnProperty('lowerConfidence') || updatedValue.hasOwnProperty('upperConfidence')) {
      const lower = updatedValue.lowerConfidence === undefined || updatedValue.lowerConfidence === null ? null : Number(updatedValue.lowerConfidence);
      const upper = updatedValue.upperConfidence === undefined || updatedValue.upperConfidence === null ? null : Number(updatedValue.upperConfidence);
      const confKey = `confidence:${foundObj.label}Aira${foundObj.airaQuestionId}`;
      this.confidenceValues[confKey] = { lower, upper };
      this.updateConfidenceSliderUI(foundObj.airaQuestionId, foundObj.label);
    }
    this.triggerConfigMultiSelectInputWithObjectUpdate(foundObj);
  }

  updateConfigTextFilters(key: string, updatedValue: any) {
    const foundObj = this.findAiraConfigFilter(
      updatedValue.airaQuestionId, updatedValue.fieldName, 'text_input'
    ) || Object.values(this.zintTextInputConfigs).flat()
      .find((filter: any) => filter.label === key);
    if (!foundObj) return;
    if (key !== foundObj.label) foundObj.stateKey = key;
    foundObj.value = updatedValue.value;

    if (updatedValue.hasOwnProperty('lowerConfidence') || updatedValue.hasOwnProperty('upperConfidence')) {
      const lower = updatedValue.lowerConfidence === undefined || updatedValue.lowerConfidence === null ? null : Number(updatedValue.lowerConfidence);
      const upper = updatedValue.upperConfidence === undefined || updatedValue.upperConfidence === null ? null : Number(updatedValue.upperConfidence);
      const confKey = `confidence:${foundObj.label}Aira${foundObj.airaQuestionId}`;
      this.confidenceValues[confKey] = { lower, upper };
      this.updateConfidenceSliderUI(foundObj.airaQuestionId, foundObj.label);
    }

    this.triggerConfigTextInputWithObjectUpdate(updatedValue.value, foundObj);
  }

  // Try to update the visible confidence slider UI for a given item
  // Uses `confidence:${label}Aira${airaQuestionId}` as both UI selector and map key
  updateConfidenceSliderUI(airaQuestionId: string, label: string): void {
    if (!this.confidenceSliders) return;
    const confKey = `confidence:${label}Aira${airaQuestionId}`;
    const slider = this.confidenceSliders?.find(
      confidenceSlider => confidenceSlider?.formName === confKey
    );
    const confidenceObject = this.confidenceValues[confKey];
    this.applyConfidenceUpdatesToSlider(slider, confidenceObject);
  }

  applySavedConfidenceToSliders(): void {
    if (!this.confidenceSliders || !this.confidenceValues) return;
    this.confidenceSliders.forEach(slider => {
      const key = slider?.formName;
      if (!key) return;
      const confidenceObject = this.confidenceValues[key];
      if (!confidenceObject) return;
      this.applyConfidenceUpdatesToSlider(slider, confidenceObject);


    });
  }

  applyConfidenceUpdatesToSlider(
    slider: ZintNgxSliderComponent | undefined,
    confidenceObject: { lower: number | null; upper: number | null } | undefined
  ): void {
    if (!slider || !confidenceObject) return;
    const updateObject: any = {};
    if (confidenceObject.lower !== null && confidenceObject.lower !== undefined) {
      updateObject.lowerUpdate = confidenceObject.lower;
    }
    if (confidenceObject.upper !== null && confidenceObject.upper !== undefined) {
      updateObject.upperUpdate = confidenceObject.upper;
    }
    if (Object.keys(updateObject).length) {
      slider.updateSliderValues(updateObject);
    }
  }

  checkThatBox$(option: Record<string, any>): Observable<any> {
    return of(option).pipe(
      delay(1), // 1ms delay for the change detection - mimics setTimeout(1)
      tap(opt => {
        opt.checked = true;
        opt.shouldShow = true;
      })
    );
  }

  getAllOptionsDict(): Record<string, any> {
    const manualMultiCheckboxes = {
      selectedCompanyStatuses: {
        options: this.companyStatusOptions,
        selector: 'name',
      },
      selectedUBOCountries: {
        options: this.countriesOfUBOsOptions,
        selector: 'code',
      },
      selectedCompanyCategories: {
        options: this.companyCategoryOptions,
        selector: 'name',
      },
      selectedSICCodes: {
        options: this.companySICCodeOptions,
        selector: 'identifier',
      },
      excludedSICCodes: {
        options: this.companySICCodeOptions,
        selector: 'identifier',
        attribute: 'excluded',
      },
      selectedOfficeCountries: {
        options: this.countriesOfOfficesOptions,
        selector: 'code',
      },
      selectedGeographicalRevenueCountries: {
        options: this.countriesOfGeographicalRevenueOptions,
        selector: 'code',
      },
      selectedGeographicalRevenueTerritories: {
        options: this.territoriesOfGeographicalRevenueOptions,
        selector: 'code',
      },
      selectedCountries: {
        options: this.countriesOfOriginOptions,
        selector: 'code',
      },
      selectedFunctionalDepartments: {
        options: this.functionalDepartmentOptions,
        selector: 'name',
      },
      ukRegion: {
        options: this.ukRegions,
        selector: 'name',
      },
      nationalitiesOfDirectorsSelected: {
        options: this.nationalitiesOfDirectorsOptions,
        selector: 'code',
      },
      nationalitiesOfPSCsSelected: {
        options: this.nationalitiesOfPSCsOptions,
        selector: 'code',
      },
      selectedImportExportCategories: {
        options: this.importExportCategories,
        selector: 'unique_identifier',
      },
      selectedWebTechnologies: {
        options: this.webTechnologyOptions,
        selector: 'name',
      },
      selectedFinancialYearEnds: {
        options: this.financialYearEndsOptions,
        selector: 'value',
      },
      trademarkClassIdsSelected: {
        options: this.trademarkClassOptions,
        selector: 'id',
      },
      selectedTrademarkStatuses: {
        options: this.trademarkStatusOptions,
        selector: 'id',
      },
      tagNegativeIdsSelected: {
        options: this.tagsNegativeOptions,
        selector: 'id',
      },
      tagPositiveIdsSelected: {
        options: this.tagsPositiveOptions,
        selector: 'id',
      },
      crossSellTagIdsSelected: {
        options: this.crossSellTagOptions,
        selector: 'id',
      },
      alertIdsSelected: { options: this.alertOptions, selector: 'id' },
      selectedModules: { options: this.selectedModules, selector: 'id' },
    };

    //config multiselect boxes e.g. aira
    let configMultiSelectOptions = {};

    Object.values(this.zintMultiSelectConfigs)?.forEach(sector => {
      sector?.forEach(config => {
        const options = { options: config.options, selector: 'label' };
        configMultiSelectOptions = {
          ...configMultiSelectOptions,
          [config.label]: options,
          [this.configFilterStateKey(config)]: options,
        };
      });
    });

    return { ...manualMultiCheckboxes, ...configMultiSelectOptions };
  }

  selectActiveCompaniesInUI() {
    if (this.companyStatusOptions?.length) {
      this.companyStatusOptions
        .filter(option => option.status_clean == 'Active Companies')
        .map(option => (option.checked = true));
    }
  }

  getOptions(selectedKeyString) {
    return this.getAllOptionsDict()[selectedKeyString];
  }

  checkRequiredBoxes(key, listOfOptionsThatShouldBeSelected) {
    const parsedOptionsThatShouldBeSelected = JSON.parse(
      listOfOptionsThatShouldBeSelected
    );
    const optionsInfo = this.getOptions(key);
    const options = optionsInfo['options'] || [];
    const selector = optionsInfo['selector'];

    let filteredOpts = [];

    /**
     * edge case for hasAIRAModuleInsights checkboxes
     * they behave like multi-checkboxes but
     * they each render within individual module
     */
    if (key === 'selectedModules') {
      const savedOptionsById = new Map(
        parsedOptionsThatShouldBeSelected.map(option => [option.id, option])
      );
      options.forEach(option => {
        const savedOption: any = savedOptionsById.get(option.id);
        option.checked = !!savedOption?.checked;
        option.excludeAnswered = !!savedOption?.excludeAnswered;
        option.shouldShow = !!savedOption;
      });
      return;
    } else {
      filteredOpts = options.filter(option =>
        parsedOptionsThatShouldBeSelected.includes(option[selector])
      );
    }

    filteredOpts?.forEach(option => this.checkThatBox$(option).subscribe());
  }

  updateUIBasedUponDictionaryValuesReturnShouldRunAgain(queryParams): boolean {
    let shouldRunSearch = false;
    for (const key in queryParams) {
      if (queryParams.hasOwnProperty(key)) {
        if (key == 'selectedCustomColumns') {
          this.updateCustomColumnsIfRequired(JSON.parse(queryParams[key]));
        } else if (key == 'selectedView') {
          this.selectedView = JSON.parse(queryParams[key]);
        } else if (key == 'websiteKeywords') {
          this.websiteKeywords = JSON.parse(queryParams[key]);
        } else if (key == 'pageNumber') {
          this.pageNumber = parseInt(queryParams[key]);
        } else if (key == 'editPropensityModelTAM') {
          this.canEditTAM = true;
        } else if (key == 'selectedEvents') {
          this.selectedEvents = JSON.parse(queryParams[key]);
        } else if (
          key === 'selectedAccountants' ||
          key === 'selectedBankers' ||
          key === 'selectedAuditors' ||
          key === 'selectedLawyers'
        ) {
          this[key] = JSON.parse(queryParams[key]);
        } else if (key == 'selectedTerritory') {
          this.selectedTerritory = JSON.parse(queryParams[key]);
          // Because we reuse companySICCodeOptions for selectedSICCodes and excludedSICCodes we need to handle them separately
        } else if (key === 'selectedSICCodes') {
          // for each selected SIC code, find the corresponding companySICCodeOption and set checked to true
          this.restoreOrStashSICCodes(JSON.parse(queryParams[key]), 'checked');
        } else if (key === 'excludedSICCodes') {
          // for each excluded SIC code, find the corresponding companySICCodeOption and set excluded to true
          this.restoreOrStashSICCodes(JSON.parse(queryParams[key]), 'excluded');
        } else if (key === 'savedSearchName') {
          this.savedSearchParamName = queryParams[key];
        } else {
          if (this[key] && Array.isArray(this[key])) {
            if (queryParams[key]) {
              this.checkRequiredBoxes(key, queryParams[key]);
            }
          } else if (this[key] !== undefined) {
            this[key] = queryParams[key];
            this.seeIfNeedToUpdateSlider(key, queryParams[key]);
          } else {
            this.seeIfNeedToUpdateSlider(key, queryParams[key]);
            this.updateNonSliderConfigFilters(key, queryParams[key]);
          }
        }
        if (key !== 'savedSearchName') shouldRunSearch = true;
      }
    }
    return shouldRunSearch;
  }

  // Apply restored SIC-code selections if the options have loaded, otherwise
  // stash them so applyStashedSICCodeSelections() can re-apply once
  // updateUIComponents() populates companySICCodeOptions. Guards against the
  // load-order race where param restore runs before the options land.
  restoreOrStashSICCodes(
    sicCodes: Array<string>,
    property: 'checked' | 'excluded'
  ): void {
    if (this.companySICCodeOptions?.length) {
      this.applySICCodeSelections(sicCodes, property);
    } else if (property === 'checked') {
      this.pendingSelectedSICCodes.push(...sicCodes);
    } else {
      this.pendingExcludedSICCodes.push(...sicCodes);
    }
  }

  applySICCodeSelections(
    sicCodes: Array<string>,
    property: 'checked' | 'excluded'
  ): void {
    sicCodes.forEach(sicCode => {
      const sicCodeOption = this.companySICCodeOptions.find(
        option => option.identifier === sicCode
      );
      if (sicCodeOption) {
        sicCodeOption[property] = true;
        sicCodeOption.shouldShow = true;
      }
    });
  }

  applyStashedSICCodeSelections(): void {
    if (!this.companySICCodeOptions?.length) return;
    if (this.pendingSelectedSICCodes.length) {
      this.applySICCodeSelections(this.pendingSelectedSICCodes, 'checked');
      this.pendingSelectedSICCodes = [];
    }
    if (this.pendingExcludedSICCodes.length) {
      this.applySICCodeSelections(this.pendingExcludedSICCodes, 'excluded');
      this.pendingExcludedSICCodes = [];
    }
  }

  getImpExpDataAndGetUIdata(key: string): void {
    if (key === 'selectedImportExportCategories') {
      const queryString = JSON.parse(this.urlQueryParams[key]).toString();
      this.dashboardService
        .searchImportExportCodes(queryString)
        .subscribe(searchData => {
          this.updateVisibleImportExportCodesAfterSearch(searchData);
          this.checkRequiredBoxes(key, this.urlQueryParams[key]);

          this.getAndUpdateUIData(false);
        });
    }
  }

  protected shouldSearchRestoredParamsAutomatically(): boolean {
    return !this.openedThroughDashboardModeSwitch;
  }

  private openedThroughDashboardModeSwitch = false;

  protected captureDashboardModeSwitchNavigation(): void {
    const info = this.router.getCurrentNavigation()?.extras.info as
      { dashboardModeSwitch?: boolean } | undefined;
    this.openedThroughDashboardModeSwitch = info?.dashboardModeSwitch === true;
  }

  initialiseBasedUponGetParamsIfAllLoaded() {
    /*
      set the keyToSliderMap here after UIData loaded
      need to call this before updateUIBasedUpon...
      */
    this.mapKeyToSliderElement();

    const shouldRunSearchOnceAllParamsLoaded =
      this.updateUIBasedUponDictionaryValuesReturnShouldRunAgain(
        this.urlQueryParams
      );

    if (shouldRunSearchOnceAllParamsLoaded) {
      if (this.openedThroughDashboardModeSwitch && this.dashboardMode === 'full') {
        setTimeout(() => this.getInitialCompanies(), 1);
      } else if (this.shouldSearchRestoredParamsAutomatically()) {
        setTimeout(() => this.searchCompaniesAfterFilters(false), 1);
      }
    } else if (this.dashboardMode !== 'full') {
      // A bare focused dashboard is a filter workspace.
      this.selectActiveCompaniesInUI();
    } else {
      setTimeout(() => this.getInitialCompanies(), 1);
      this.selectActiveCompaniesInUI();
    }
  }

  updateAlerts(data): void {
    this.alertOptions = data.alerts;
    this.teamMembers = data.team_members;
    if (data.team_members && data.team_members.length > 0) {
      this.teamMemberIdToCreateAlertFor = data.team_members[0].id;
    }
    this.hasLoadedFlags.hasLoadedAlertOptions.set(true);
  }

  updateCustomColumnsIfRequired(newCustomColumns): void {
    // Here we need to update the custom columns as selected, e.g. specific values etc.
    // We need to pull the options from the options that are shared in the UI, not through the URL though as if from a saved
    // search then it will likely be out of date.
    let output = [];
    this.customColumns.forEach(col => {
      let matchingColumns = newCustomColumns.filter(_col => col.id == _col.id);
      if (matchingColumns && matchingColumns.length > 0) {
        let matchingColumn = matchingColumns[0];
        if (col.options) {
          let optionsOutput = [];
          col.options.forEach(col_option => {
            let matchingOptions = matchingColumn.options.filter(
              _option => col_option.id == _option.id
            );
            if (matchingOptions && matchingOptions.length > 0) {
              let matchingOption = matchingOptions[0];
              optionsOutput.push(matchingOption);
            } else {
              optionsOutput.push(col_option);
            }
          });
          matchingColumn.options = optionsOutput;
        }
        // Update permissions from current columns
        matchingColumn.can_edit = col.can_edit;
        matchingColumn.can_view = col.can_view;
        output.push(matchingColumn);
      } else {
        output.push(col);
      }
    });
    this.customColumns = output;
    this.updateFilteredCustomColumns();
  }

  updateCustomColumnsAndColumnWidths(output): void {
    this.customColumnNamesToObjects = {};
    this.customColumns = output.custom_columns;
    this.teamMembersForCustomColumns = output.team_members;
    if (this.customColumns) {
      this.customColumns.forEach(col => {
        this.customColumnNamesToObjects[col.name] = col;
        if (col.column_type == 'User') {
          let copyOfTeamMembersToAssign = JSON.parse(
            JSON.stringify(this.teamMembersForCustomColumns)
          );
          col.options = copyOfTeamMembersToAssign;
        }
      });
    }
    this.updateFilteredCustomColumns();
    this.hasLoadedFlags.hasLoadedCustomColumns.set(true);
    if (output.column_widths) {
      this.columnNamesToWidths = output.column_widths;
    } else {
      this.columnNamesToWidths = { 'Company Name': 350 };
    }
  }

  clearAllCurrentlyEditingAndUpdateValue(updatedValue, company_k_v): void {
    company_k_v.val = updatedValue;
    this.currentlyEditingRowName = '';
    this.currentlyEditingColumnName = '';
  }

  updateCurrentlyEditingCols(row_id, col_name): void {
    if (this.customColumnNamesToObjects[col_name]) {
      if (this.customColumnNamesToObjects[col_name]['can_edit']) {
        this.currentlyEditingRowName = row_id;
        this.currentlyEditingColumnName = col_name;
      }
    }
  }

  toggleProfilePreview(companyNumber?: string): void {
    if (this.isScreenTooSmall()) {
      return;
    }

    const currentCompanyNumber = this.dataStore.companyNumberFromParent();
    const isSameCompany = currentCompanyNumber === companyNumber;

    // if no company provided (close button)
    if (!companyNumber) {
      this.profilePreviewIsOpen.set(false);
      this.dataStore.companyNumberFromParent.set('');
      return;
    }

    // For same company, do nothing.
    if (isSameCompany) {
      return;
    }
    this.dataStore.companyNumberFromParent.set('');
    this.cdr.detectChanges();
    this.dataStore.companyNumberFromParent.set(companyNumber);

    this.profilePreviewIsOpen.set(true);

    // Collapse sidebar if needed
    if (!this.isSidebarCollapsed()) {
      this.expandOrCollapseSidebar();
    }
  }

  triggerChangeDetection(event: any) {
    this.cdr.markForCheck();
  }

  drawerStateChanged(isOpen: boolean): void {
    this.profilePreviewIsOpen.set(isOpen);
    if (!isOpen) {
      this.dataStore.companyNumberFromParent.set('');
    }
    this.cdr.markForCheck();
  }

  updateCrossSellTagOptions(output): void {
    this.crossSellTagOptions = output;
    this.hasLoadedFlags.hasLoadedUserCrossSellTags.set(true);
  }

  updateTagOptions(output): void {
    if (output.user_tags) {
      this.tagsPositiveOptions = output.user_tags.map(tag => ({ ...tag, shouldShow: true }));
      const copy = JSON.parse(JSON.stringify(output.user_tags));
      // otherwise we end up with the same data binding!
      this.tagsNegativeOptions = copy.map(tag => ({ ...tag, shouldShow: true }));
      this.hasLoadedFlags.hasLoadedUserTags.set(true);
    }
  }

  updateScrollBarDimensions() {
    $(document).ready(function () {
      $('.dummy-top-scrollbar').width(
        $('#table-results-section').find('table').width()
      );
      $('.dummy-top-scrollbar-wrapper').width(
        $('#table-results-section').width() + 15
      );
    });
  }

  updateCompaniesAndRenderInfo(output, isInitial, pageOffset = 0, searchParams?: HttpParams) {
    if (searchParams) this.displayedSearchParams = searchParams;
    this.displayingInitialCompanies = isInitial;
    this.displayedPageOffset = pageOffset;
    this.companies = output;
    this.syncQuerySelectionCheckboxes();
    this.pubMethods.allowDraggingOftable('.company-results-table');
    this.updateScrollBarDimensions();
    if (!isInitial) {
      this.updateCompanyIfInaccurateEstimate();
      if (this.dashboardMode === 'logistics') {
        requestAnimationFrame(() => this.scrollToLogisticsResults());
      }
    }
    this.cdr.markForCheck();
  }
  getInitialCompanies(): void {
    this.updateFilters();
    const params = this.paramsForCompanySearch(this.allFilterParams());
    this.dashboardService
      .getInitialCompanies()
      .subscribe(output => this.updateCompaniesAndRenderInfo(output, true, 0, params));
  }

  getAndUpdateUIData(isDefaultImpExp = true) {
    this.dashboardService.getUIData().subscribe(data => {
      this.updateUIComponents(data);
      if (isDefaultImpExp) {
        this.importExportCategories = data.import_export_sections;
      }
    });
  }

  getUIDataAndUpdateCompanies(): void {
    if (
      this.route.snapshot.queryParamMap.has('selectedImportExportCategories')
    ) {
      this.getImpExpDataAndGetUIdata('selectedImportExportCategories');
    } else {
      this.getAndUpdateUIData();
    }
  }

  searchWebTechs(): void {
    if (this.webTechsTextSearch && this.webTechsTextSearch.length > 2) {
      this.isSearchingWebTechs = true;
      this.selectedWebTechnologies = this.webTechnologyOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));
      this.selectedWebTechnologies = this.webTechnologyOptions
        .filter(
          option =>
            option.description
              .toLowerCase()
              .indexOf(this.webTechsTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));
    }
  }

  resetWebTechs(): void {
    this.selectedWebTechnologies = this.webTechnologyOptions.map(
      option => (option.shouldShow = false)
    );
    this.selectedWebTechnologies = this.webTechnologyOptions
      .filter(option => option.level === 0)
      .map(option => (option.shouldShow = true));
    this.isSearchingWebTechs = false;
  }

  searchSicCodes(): void {
    if (!this.sicCodeTextSearch?.length) {
      this.resetSicSearch();
    }

    if (this.sicCodeTextSearch && this.sicCodeTextSearch.length > 2) {
      this.isSearchingSicCodes = true;
      this.selectedSICCodes = this.companySICCodeOptions.map(
        option => (option.shouldShow = false)
      );
      this.selectedSICCodes = this.companySICCodeOptions
        .filter(option => {
          if (isNaN(Number(this.sicCodeTextSearch))) {
            return (
              option.description
                .toLowerCase()
                .indexOf(this.sicCodeTextSearch.toLowerCase()) >= 0
            );
          } else {
            // actual sic code number search e.g. 01230 Growing citrus
            return option.identifier.indexOf(this.sicCodeTextSearch) >= 0;
          }
        })
        .map(option => (option.shouldShow = true));
    }
  }

  resetSicSearch(): void {
    this.selectedSICCodes = this.companySICCodeOptions
      .filter(option => (option.shouldShow = true))
      .map(option => (option.shouldShow = false));
    this.isSearchingSicCodes = false;
    this.sicCodeTextSearch = '';
  }

  updateFilters(): void {
    //TODO: check if we can early return without optional chain everything
    this.selectedUBOCountries = this.countriesOfUBOsOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.nationalitiesOfDirectorsSelected = this.nationalitiesOfDirectorsOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.nationalitiesOfPSCsSelected = this.nationalitiesOfPSCsOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.selectedWebTechnologies = this.webTechnologyOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.selectedImportExportCategories = this.importExportCategories
      .filter(option => option.checked)
      .map(option => option.unique_identifier);

    this.selectedCompanyStatuses = this.companyStatusOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.selectedCountries = this.countriesOfOriginOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.selectedOfficeCountries = this.countriesOfOfficesOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.selectedGeographicalRevenueCountries =
      this.countriesOfGeographicalRevenueOptions
        .filter(option => option.checked)
        .map(option => option.code);

    this.selectedGeographicalRevenueTerritories =
      this.territoriesOfGeographicalRevenueOptions
        .filter(option => option.checked)
        .map(option => option.code);

    this.selectedFunctionalDepartments = this.functionalDepartmentOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.ukRegion = this.ukRegions
      .filter(option => option.checked)
      .map(option => option.name);

    this.selectedSICCodes = this.companySICCodeOptions
      .filter(option => option.checked)
      .map(option => option.identifier);

    this.excludedSICCodes = this.companySICCodeOptions
      .filter(option => option.excluded)
      .map(option => option.identifier);

    //noinspection TypeScriptUnresolvedVariable
    this.selectedCompanyCategories = this.companyCategoryOptions
      .filter(option => option.checked)
      .map(option => option.value);

    //noinspection TypeScriptUnresolvedFunction
    this.selectedFinancialYearEnds = this.financialYearEndsOptions
      .filter(option => option.checked)
      .map(option => option.value);

    this.tagPositiveIdsSelected = this.tagsPositiveOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.tagNegativeIdsSelected = this.tagsNegativeOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.crossSellTagIdsSelected = this.crossSellTagOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.trademarkClassIdsSelected = this.trademarkClassOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.selectedTrademarkStatuses = this.trademarkStatusOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.newsEventCategories = this.newsEventCategoryGroups.flatMap(group =>
      group.subcategories.filter(sub => sub.checked).map(sub => sub.name)
    );

    this.alertIdsSelected = this.alertOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.selectedCustomColumns = this.getSelectedCustomColumns();
  }

  getSelectedCustomColumns(): Array<any> {
    const outPutColumns = this.customColumns?.filter(
      column =>
        (column.dropdownInput &&
          (column.dropdownInput == 'isSet' ||
            column.dropdownInput == 'isNotSet' ||
            column.dropdownInput == 'isMe' ||
            column.dropdownInput == 'lockState' ||
            column.boxInput ||
            column.boxInput == 0 ||
            column.boxInputLower ||
            column.boxInputLower == 0 ||
            column.boxInputUpper ||
            column.boxInputUpper == 0)) ||
        column.options.filter(option => option.checked).length > 0
    );
    // remove the attributes that we do not need that clog up the URL params
    outPutColumns?.forEach(col => delete col.edit_users);
    outPutColumns?.forEach(col => delete col.view_users);
    return outPutColumns;
  }

  // Create HttpParams from current state of filters
  allFilterParams(): HttpParams {
    return this.dashboardService.getFilterParameters(
      this.selectedTerritory,
      this.companyNameContains,
      this.accountantNameContains,
      this.bankerNameContains,
      this.auditorNameContains,
      this.lawyerNameContains,
      this.selectedAccountants,
      this.selectedBankers,
      this.selectedAuditors,
      this.selectedLawyers,
      this.selectedCompanyStatuses,
      this.lowerNumberOfUBOs,
      this.upperNumberOfUBOs,
      this.selectedUBOCountries,
      this.selectedCountries,
      this.selectedCompanyCategories,
      this.selectedSICCodes,
      this.excludedSICCodes,
      this.websiteKeywords,
      this.corporateStructureIsTopLevelParentCompany,
      this.corporateStructureHasParent,
      this.corporateStructureDoesNotHaveParent,
      this.corporateStructureHasChild,
      this.corporateStructureDoesNotHaveChild,
      this.corporateStructureRelatedCompanies,
      this.hasWebsite,
      this.employeeLowerCount,
      this.employeeUpperCount,
      this.excludeEstimatedHeadcountFigures,
      this.lowerHeadCountGrowthPercentage,
      this.upperHeadCountGrowthPercentage,
      this.departmentHeadCountLowerCount,
      this.departmentHeadCountUpperCount,
      this.selectedFunctionalDepartments,
      this.lowerAge,
      this.upperAge,
      this.selectedAddressTypeOption,
      this.postcodeBeginsWith,
      this.postcodeDistrict,
      this.ukRegion,
      this.postcodeWithinRadius,
      this.selectedTownId,
      this.mileRadiusOfPostcode,
      this.hasOverseasParent,
      this.hasOfficesOverseas,
      this.selectedOfficeCountries,
      this.lowerNumberOfKnownOfficeLocations,
      this.upperNumberOfKnownOfficeLocations,
      this.selectedGeographicalRevenueCountries,
      this.selectedGeographicalRevenueTerritories,
      this.lowerGeographicalRevenueSelected,
      this.upperGeographicalRevenueSelected,
      this.hasLEINumber,
      this.selectedLEIStatus,
      this.selectedImportExportCategories,
      this.selectedImportingOrExportingOption,
      this.importExportTimeScale,
      this.importExportFrequencyImportingOrExporting,
      this.importExportFrequency,
      this.importExportFrequencyMonthCount,
      this.selectedWebTechnologies,
      this.webTrafficLow,
      this.webTrafficHigh,
      this.homepageSearchTerm,
      this.fullWebsiteSearchTerm,
      this.lowerAverageDirectorAgeRange,
      this.upperAverageDirectorAgeRange,
      this.lowerNumberOfDirectors,
      this.upperNumberOfDirectors,
      this.lowerNumberOfPSCs,
      this.upperNumberOfPSCs,
      this.PSCsRealPersonsOnly,
      this.recentDirectorshipTitleContains,
      this.lowerRecentDirectorshipMonth,
      this.upperRecentDirectorshipMonth,
      this.nationalitiesOfDirectorsSelected,
      this.nationalitiesOfPSCsSelected,
      this.lowerDaysUntilFinancialYearEnd,
      this.upperDaysUntilFinancialYearEnd,
      this.selectedFinancialYearEnds,
      this.lowerDaysUntilAnniversary,
      this.upperDaysUntilAnniversary,
      this.isHiring,
      this.hiringJobTitleContains,
      this.hiringDescriptionContains,
      this.selectedHiringDayLimit,
      this.lowerNumberOfRolesHiringFor,
      this.upperNumberOfRolesHiringFor,
      this.selectedNumberOfRolesHiringForDayLimit,
      this.includeRecruiters,
      this.directorJobTitleContains,
      this.amazonSeller,
      this.listedOnGCloud,
      this.hasPhoneNumber,
      this.hasEmailAddress,
      this.hasRaisedInvestment,
      this.hasShareFilingEvent,
      this.shareFilingEventWithinDays,
      this.lowerShareFilingCapitalValue,
      this.upperShareFilingCapitalValue,
      this.lowerInvestmentAmount,
      this.upperInvestmentAmount,
      this.ignoreRevenueEstimates,
      this.lowerAccountsDueSelected,
      this.upperAccountsDueSelected,
      this.lowerLatestAccountsSubmittedSelected,
      this.upperLatestAccountsSubmittedSelected,
      this.selectedLatestAccountsGroupOption,
      this.selectedLatestAccountsAuditOption,
      this.accountsAreOverdue,
      this.accountsSearchTerm,
      this.accountsSearchWithinDays,
      this.issuedAGrant,
      this.lowerGrantAmount,
      this.upperGrantAmount,
      this.grantLowerMonthsUntilProjectEnd,
      this.grantUpperMonthsUntilProjectEnd,
      this.chargeRegistered,
      this.outstandingChargeRegistered,
      this.noOutstandingCharges,
      this.selectedEvents,
      this.postcodeEventsWithinRadius,
      this.mileRadiusOfEventsSelected,
      this.eventsWithinDaysLower,
      this.eventsWithinDaysUpper,
      this.newsWithinDays,
      this.newsContaining,
      this.newsSource,
      this.newsEventCategories,
      this.trademarkClassIdsSelected,
      this.trademarkDescriptionContains,
      this.trademarkNameContains,
      this.selectedTrademarkStatuses,
      this.excludeCompaniesTaggedByTeam,
      this.tagPositiveIdsSelected,
      this.tagNegativeIdsSelected,
      this.matchAllPositiveTags,
      this.crossSellTagIdsSelected,
      this.crossSellLinkedThroughPeople,
      this.crossSellLinkedThroughCorporateStructure,
      this.similarCompanyUrl,
      this.pageNumber,
      this.sortKey,
      this.sortDirectionIsAscending,
      this.selectedCustomColumns,
      this.unreadAlertsOnly,
      this.lowerAlertsDayRange,
      this.upperAlertsDayRange,
      this.alertIdsSelected,
      this.selectedView,
      this.isLowEnergy,
      this.lowerTotalEnergyKwhSelected,
      this.upperTotalEnergyKwhSelected,
      this.lowerGasKwhSelected,
      this.upperGasKwhSelected,
      this.lowerElectricKwhSelected,
      this.upperElectricKwhSelected,
      this.lowerScope1KwhSelected,
      this.upperScope1KwhSelected,
      this.lowerScope2KwhSelected,
      this.upperScope2KwhSelected,
      this.lowerScope3KwhSelected,
      this.upperScope3KwhSelected,
      this.lowerTotalEmissionsKgCo2eSelected,
      this.upperTotalEmissionsKgCo2eSelected,
      this.lowerScope1KgCo2eSelected,
      this.upperScope1KgCo2eSelected,
      this.lowerScope2KgCo2eSelected,
      this.upperScope2KgCo2eSelected,
      this.lowerScope3KgCo2eSelected,
      this.upperScope3KgCo2eSelected,
      this.lowerKgCo2ePerMillionRevenueSelected,
      this.upperKgCo2ePerMillionRevenueSelected,
      this.lowerKgCo2ePerEmployeeSelected,
      this.upperKgCo2ePerEmployeeSelected,
      this.hasActivePatent,
      this.hasAnyPatentRecord,
      this.patentFiledWithinDays,
      this.lowerPatentAwardedDays,
      this.upperPatentAwardedDays,
      this.hasContract,
      this.lowerContractValueSelected,
      this.upperContractValueSelected,
      this.contractAwardedWithinMonths,
      this.lowerContractStartMonthsSelected,
      this.upperContractStartMonthsSelected,
      this.lowerContractEndMonthsSelected,
      this.upperContractEndMonthsSelected,
      this.contractTitleContains,
      this.contractDescriptionContains,
      this.ignoreSatisfiedCharges,
      this.chargeHolderNameContains,
      this.chargeCreatedMonthsAgoLower,
      this.chargeCreatedMonthsAgoUpper,
      this.supplierTagSelected,
      this.associateWithOneSupplierOnly,
      this.customerTagSelected,
      this.summarySemanticSearchTerm,
      this.summarySemanticSearchConceptMatch,
      this.summarySemanticSearchMinScore,
      this.clusterSearchMode,
      this.sliderValues,
      this.configTextInputValues,
      this.configMultiSelectValues,
      this.selectedPageSizeLimit,
      this.selectedModules,
      this.hasLandTitle,
      this.landTenure,
      this.landCategory
    );
  }

  definedPage(page: number | string): void {
    if (typeof page === 'string') {
      page = Math.abs(parseInt(page));
      if (isNaN(page) || page < 1) {
        this.messageService.showAndHideAfterDelay(
          'Enter positive numbers only!',
          3100
        );
        return;
      }
    }

    if (typeof page === 'number' && page <= this.maxPageNumber) {
      this.pageNumber = page;
      this.searchCompaniesAfterFiltersNoCount();
    }
  }

  private readonly logisticsCoreResultFields = [
    { key: 'Primary Industry', label: 'Industry', icon: 'fa-industry-windows' },
    { key: 'Revenue', label: 'Revenue', icon: 'fa-chart-line-up' },
    { key: 'Headcount', label: 'Employees', icon: 'fa-users' },
  ];

  private readonly logisticsStructuredInsightPriority = [
    'logistics_providers',
    'monthly_parcels_or_shipments_sent',
    'monthly_parcels_or_shipments_received',
    'annual_outbound_pallet_count',
    'annual_inbound_pallet_count',
    'annual_outbound_full_containers_count',
    'annual_inbound_full_containers_count',
    'shipment_categories_sent',
    'shipment_categories_received',
    'number_of_verified_warehouses_used',
    'warehouse_details',
    'outbound_transport_modes',
    'inbound_transport_modes',
  ];

  private readonly logisticsStructuredInsightLabels: Record<string, string> = {
    logistics_providers: 'Logistics providers',
    monthly_parcels_or_shipments_sent: 'Monthly parcels sent',
    monthly_parcels_or_shipments_received: 'Monthly parcels received',
    annual_outbound_pallet_count: 'Outbound pallets per year',
    annual_inbound_pallet_count: 'Inbound pallets per year',
    annual_outbound_full_containers_count: 'Outbound containers per year',
    annual_inbound_full_containers_count: 'Inbound containers per year',
    shipment_categories_sent: 'Shipment categories sent',
    shipment_categories_received: 'Shipment categories received',
    number_of_verified_warehouses_used: 'Verified warehouses',
    warehouse_details: 'Warehouse network',
    outbound_transport_modes: 'Outbound transport modes',
    inbound_transport_modes: 'Inbound transport modes',
  };

  private readonly logisticsDirectionalInsightGroupDefinitions = [
    {
      groupKey: 'parcels',
      label: 'Parcels',
      period: 'Monthly',
      icon: 'fa-box-taped',
      displayMode: 'metrics',
      fields: [
        {
          fieldKey: 'monthly_parcels_or_shipments_sent',
          label: 'Sent',
        },
        {
          fieldKey: 'monthly_parcels_or_shipments_received',
          label: 'Received',
        },
      ],
    },
    {
      groupKey: 'pallets',
      label: 'Pallets',
      period: 'Annual',
      icon: 'fa-pallet-boxes',
      displayMode: 'metrics',
      fields: [
        { fieldKey: 'annual_outbound_pallet_count', label: 'Outbound' },
        { fieldKey: 'annual_inbound_pallet_count', label: 'Inbound' },
      ],
    },
    {
      groupKey: 'containers',
      label: 'Containers',
      period: 'Annual',
      icon: 'fa-container-storage',
      displayMode: 'metrics',
      fields: [
        {
          fieldKey: 'annual_outbound_full_containers_count',
          label: 'Outbound',
        },
        {
          fieldKey: 'annual_inbound_full_containers_count',
          label: 'Inbound',
        },
      ],
    },
    {
      groupKey: 'international-flows',
      label: 'International flows',
      period: 'Estimated country share',
      icon: 'fa-globe-europe',
      displayMode: 'geography',
      fields: [
        {
          fieldKey:
            'geographical_distribution_of_parcels_or_shipments_sent_from_company',
          label: 'Exports',
        },
        {
          fieldKey:
            'geographical_distribution_of_parcels_or_shipments_received_by_company',
          label: 'Imports',
        },
      ],
    },
  ];

  private readonly logisticsPerspectivePriorities: Record<
    LogisticsResultsPerspective,
    string[]
  > = {
    overview: [
      'parcels',
      'international-flows',
      'pallets',
      'logistics_providers',
      'containers',
      'shipment_categories_sent',
      'shipment_categories_received',
      'number_of_verified_warehouses_used',
      'warehouse_details',
      'outbound_transport_modes',
      'inbound_transport_modes',
      'seasonality_score',
      'b2c_ratio',
      'online_marketplaces',
      'returns_policy',
    ],
    'freight-forwarding': [
      'international-flows',
      'containers',
      'pallets',
      'outbound_transport_modes',
      'inbound_transport_modes',
      'shipment_categories_sent',
      'shipment_categories_received',
      'number_of_verified_warehouses_used',
      'warehouse_details',
      'seasonality_score',
      'seasonal_products_sold',
      'logistics_providers',
      'products_to_customers',
      'products_from_suppliers',
      'parcels',
    ],
    'third-party-logistics': [
      'number_of_verified_warehouses_used',
      'warehouse_details',
      'pallets',
      'international-flows',
      'parcels',
      'shipment_categories_sent',
      'shipment_categories_received',
      'logistics_providers',
      'wms_shipping_and_logistics_software_systems',
      'outbound_transport_modes',
      'inbound_transport_modes',
      'seasonality_score',
      'seasonal_products_sold',
      'returns_policy',
      'logistics_operations_overview',
      'logistics_related_challenges',
      'containers',
    ],
    'parcel-courier': [
      'parcels',
      'international-flows',
      'logistics_providers',
      'parcel_size_histogram',
      'parcel_weight_histogram',
      'parcel_value_histogram',
      'b2c_ratio',
      'online_marketplaces',
      'returns_policy',
      'shipment_categories_sent',
      'shipment_categories_received',
      'seasonality_score',
      'seasonal_products_sold',
      'pallets',
      'containers',
    ],
    'customs-trade': [
      'international-flows',
      'containers',
      'outbound_transport_modes',
      'inbound_transport_modes',
      'shipment_categories_sent',
      'shipment_categories_received',
      'products_to_customers',
      'products_from_suppliers',
      'pallets',
      'logistics_operations_overview',
      'logistics_related_challenges',
      'logistics_providers',
      'estimated_credit_worthiness_score',
      'parcels',
    ],
  };

  private readonly logisticsNumberFormatter = new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 0,
  });

  private readonly logisticsCompactNumberFormatter = new Intl.NumberFormat(
    'en-GB',
    {
      notation: 'compact',
      maximumFractionDigits: 1,
    }
  );

  private readonly logisticsInsightPriority = [
    'MI - Logistics Providers',
    'MI - Estimated Monthly Parcels Sent',
    'MI - Estimated Monthly Parcels Received',
    'MI - Outbound Pallets Per Year',
    'MI - Inbound Pallets Per Year',
    'MI - Outbound Full Containers Per Year',
    'MI - Inbound Full Containers Per Year',
    'MI - Shipment Categories Sent',
    'MI - Shipment Categories Received',
    'MI - Outbound Transport Modes',
    'MI - Inbound Transport Modes',
    'MI - Minimum Verified Warehouses',
    'MI - Warehouse Regions',
    'MI - Export Countries',
    'MI - Import Countries',
    'MI - Seasonality Score',
    'MI - Seasonal Products Sold',
    'MI - Online Marketplaces',
    'MI - B2C vs B2B Percentage',
    'MI - Estimated Credit Worthiness Score',
    'MI - Shipping and Logistics Software Systems',
  ];

  getLogisticsCoreFields(company: Record<string, any>): Array<{
    key: string;
    label: string;
    icon: string;
    val: any;
  }> {
    return this.logisticsCoreResultFields.map(field => ({
      ...field,
      val: company?.[field.key],
    }));
  }

  getLogisticsInsightFields(company: Record<string, any>): Array<{
    key: string;
    label: string;
    icon: string;
    val: any;
  }> {
    const insightKeys = Object.keys(company || {}).filter(
      key => key.startsWith('MI - ') && this.hasLogisticsResultValue(company[key])
    );
    insightKeys.sort((left, right) => {
      const leftIndex = this.logisticsInsightPriority.indexOf(left);
      const rightIndex = this.logisticsInsightPriority.indexOf(right);
      return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) -
        (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
    });
    return insightKeys.slice(0, 4).map(key => ({
      key,
      label: key.replace(/^MI - /, ''),
      icon: this.getLogisticsInsightIcon(key),
      val: company[key],
    }));
  }

  getLogisticsStructuredInsights(company: Record<string, any>): Array<any> {
    const insights = company?.['_logisticsInsights'];
    if (!Array.isArray(insights)) return [];

    return [...insights].sort((left, right) => {
      const leftIndex = this.logisticsStructuredInsightPriority.indexOf(
        left.fieldKey
      );
      const rightIndex = this.logisticsStructuredInsightPriority.indexOf(
        right.fieldKey
      );
      return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) -
        (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
    });
  }

  getLogisticsProviderInsight(company: Record<string, any>): any | null {
    return (
      this.getLogisticsStructuredInsights(company).find(
        insight => insight.fieldKey === 'logistics_providers'
      ) || null
    );
  }

  getLogisticsProviders(company: Record<string, any>): Array<any> {
    const value = this.getLogisticsProviderInsight(company)?.value;
    return Array.isArray(value) ? value.slice(0, 4) : [];
  }

  getLogisticsProviderOverflowCount(company: Record<string, any>): number {
    const value = this.getLogisticsProviderInsight(company)?.value;
    return Array.isArray(value) ? Math.max(0, value.length - 4) : 0;
  }

  getLogisticsCardPanels(company: Record<string, any>): Array<any> {
    const structuredInsights = this.getLogisticsStructuredInsights(company);
    if (structuredInsights.length) {
      const directionalGroups = this.getLogisticsDirectionalInsightGroups(company);
      const groupedFieldKeys = new Set(
        directionalGroups.flatMap(group =>
          group.fields.map(field => field.insight.fieldKey)
        )
      );
      const panels = [
        ...structuredInsights
          .filter(insight => insight.fieldKey === 'logistics_providers')
          .map(insight => ({
            panelType: 'provider',
            priorityKey: insight.fieldKey,
            insight,
            dataPointCount: 1,
          })),
        ...directionalGroups.map(group => ({
          panelType: 'directional',
          priorityKey: group.groupKey,
          group,
          dataPointCount: group.fields.length,
        })),
        ...structuredInsights
          .filter(
            insight =>
              insight.fieldKey !== 'logistics_providers' &&
              !groupedFieldKeys.has(insight.fieldKey)
          )
          .map(insight => ({
            panelType: 'insight',
            priorityKey: insight.fieldKey,
            insight,
            dataPointCount: 1,
          })),
      ];
      const priorities =
        this.logisticsPerspectivePriorities[this.logisticsResultsPerspective];
      const structuredFallbackOrder = [
        'logistics_providers',
        'parcels',
        'pallets',
        'containers',
        ...this.logisticsStructuredInsightPriority,
      ];

      return panels.sort((left, right) => {
        const leftPriority = priorities.indexOf(left.priorityKey);
        const rightPriority = priorities.indexOf(right.priorityKey);
        const leftFallback = structuredFallbackOrder.indexOf(left.priorityKey);
        const rightFallback = structuredFallbackOrder.indexOf(right.priorityKey);
        const leftIndex = leftPriority < 0
          ? priorities.length + (leftFallback < 0 ? Number.MAX_SAFE_INTEGER : leftFallback)
          : leftPriority;
        const rightIndex = rightPriority < 0
          ? priorities.length + (rightFallback < 0 ? Number.MAX_SAFE_INTEGER : rightFallback)
          : rightPriority;
        return leftIndex - rightIndex;
      }).slice(0, 4);
    }

    return this.getLogisticsInsightFields(company).map(field => ({
      panelType: 'insight',
      priorityKey: field.key,
      dataPointCount: 1,
      insight: {
        fieldKey: field.key,
        value: field.val,
        displaySchema: {
          label: field.label,
          display_type: 'legacy',
        },
        icon: field.icon,
        legacyKey: field.key,
      },
    }));
  }

  getLogisticsCardInsights(company: Record<string, any>): Array<any> {
    return this.getLogisticsCardPanels(company)
      .filter(panel => panel.panelType === 'insight')
      .map(panel => panel.insight);
  }

  getLogisticsDirectionalInsightGroups(
    company: Record<string, any>
  ): Array<any> {
    const insightsByKey = new Map(
      this.getLogisticsStructuredInsights(company).map(insight => [
        insight.fieldKey,
        insight,
      ])
    );

    return this.logisticsDirectionalInsightGroupDefinitions
      .map(group => ({
        ...group,
        fields: group.fields
          .map(field => ({
            ...field,
            insight: insightsByKey.get(field.fieldKey),
          }))
          .filter(field => Boolean(field.insight)),
      }))
      .filter(group => group.fields.length > 0);
  }

  getLogisticsAvailableInsightCount(company: Record<string, any>): number {
    const structuredInsights = this.getLogisticsStructuredInsights(company);
    if (structuredInsights.length) return structuredInsights.length;

    return Object.keys(company || {}).filter(
      key =>
        key.startsWith('MI - ') &&
        this.hasLogisticsResultValue(company[key])
    ).length;
  }

  getLogisticsVisibleInsightCount(company: Record<string, any>): number {
    return this.getLogisticsCardPanels(company).reduce(
      (count, panel) => count + panel.dataPointCount,
      0
    );
  }

  getLogisticsProductsAndServices(company: Record<string, any>): string[] {
    const productsAndServices =
      company?.['_productsAndServices'] ?? company?.['Products & Services'];
    const values = Array.isArray(productsAndServices)
      ? productsAndServices
      : productsAndServices && typeof productsAndServices === 'object'
        ? Object.values(productsAndServices)
        : [];

    return values
      .filter(value => this.hasLogisticsResultValue(value))
      .map(value => String(value))
      .slice(0, 2);
  }

  getLogisticsStructuredInsightIcon(insight: Record<string, any>): string {
    return (
      insight.icon ||
      this.getLogisticsInsightIcon(
        `${insight.fieldKey || ''} ${insight.displaySchema?.label || ''}`
      )
    );
  }

  getLogisticsStructuredInsightLabel(insight: Record<string, any>): string {
    return (
      this.logisticsStructuredInsightLabels[insight.fieldKey] ||
      insight.displaySchema?.label ||
      insight.fieldKey
    );
  }

  formatLogisticsInsightNumber(
    insight: Record<string, any>,
    includeAppend = true
  ): string {
    const numericValue = Number(insight?.value);
    if (!Number.isFinite(numericValue)) return String(insight?.value ?? '');

    const formatter = insight?.displaySchema?.display_format === 'k'
      ? this.logisticsCompactNumberFormatter
      : this.logisticsNumberFormatter;
    const value = formatter.format(numericValue);
    const prepend = insight?.displaySchema?.prepend || '';
    const append = includeAppend
      ? insight?.displaySchema?.append || ''
      : '';
    return `${prepend}${value}${append ? ` ${append}` : ''}`;
  }

  getLogisticsInsightPills(insight: Record<string, any>): string[] {
    if (!Array.isArray(insight?.value)) return [];
    const schema = insight.displaySchema || {};
    const propertyName =
      schema.text_property_name ||
      schema.label_name ||
      schema.header_key_name ||
      'name';
    const valueName = schema.value_name;
    const values = [...insight.value];
    if (valueName) {
      values.sort(
        (left, right) =>
          Number(right?.[valueName] || 0) -
          Number(left?.[valueName] || 0)
      );
    }
    return values
      .map(value =>
        typeof value === 'object' && value !== null
          ? value[propertyName]
          : value
      )
      .filter(value => this.hasLogisticsResultValue(value))
      .map(value => String(value));
  }

  getLogisticsGeographicalBreakdown(
    insight: Record<string, any>,
    maximumItems = 2
  ): Array<{ countryCode: string; countryName: string; percentage: number }> {
    if (!Array.isArray(insight?.value)) return [];
    const schema = insight.displaySchema || {};
    const countryKey = schema.label_name || 'country_code';
    const percentageKey = schema.value_name || 'percentage';

    return insight.value
      .map(value => {
        const countryCode = String(value?.[countryKey] || '').toUpperCase();
        const country = this.magicCountriesList.find(
          option => option.value.toUpperCase() === countryCode
        );
        return {
          countryCode,
          countryName: country?.name || countryCode,
          percentage: Number(value?.[percentageKey]),
        };
      })
      .filter(
        value =>
          Boolean(value.countryCode) && Number.isFinite(value.percentage)
      )
      .sort((left, right) => right.percentage - left.percentage)
      .slice(0, maximumItems);
  }

  getLogisticsGeographicalOverflowCount(insight: Record<string, any>): number {
    return Array.isArray(insight?.value)
      ? Math.max(0, insight.value.length - 2)
      : 0;
  }

  getLogisticsWebsiteHref(company: Record<string, any>): string {
    const website = String(company?.['URL'] || '').trim();
    if (!website) return '';
    return /^https?:\/\//i.test(website) ? website : `https://${website}`;
  }

  getLogisticsWebsiteLabel(company: Record<string, any>): string {
    const href = this.getLogisticsWebsiteHref(company);
    if (!href) return '';
    try {
      return new URL(href).hostname.replace(/^www\./i, '');
    } catch (_) {
      return String(company?.['URL'] || '');
    }
  }

  private getLogisticsInsightIcon(key: string): string {
    if (/Geographical|Import Shipments|Export Shipments/i.test(key)) {
      return 'fa-globe-europe';
    }
    if (/Returns/i.test(key)) return 'fa-undo';
    if (/Challenge|Initiative|Sustainability/i.test(key)) return 'fa-lightbulb';
    if (/Products To|Products From|Operations Overview/i.test(key)) {
      return 'fa-boxes-stacked';
    }
    if (/Parcel|Shipment/i.test(key)) return 'fa-box-taped';
    if (/Pallet|Container/i.test(key)) return 'fa-pallet-boxes';
    if (/Warehouse/i.test(key)) return 'fa-warehouse-full';
    if (/Transport|Countries/i.test(key)) return 'fa-route';
    if (/Season/i.test(key)) return 'fa-calendar-range';
    if (/Marketplace|B2C|B2B/i.test(key)) return 'fa-store';
    if (/Credit/i.test(key)) return 'fa-badge-check';
    if (/Software/i.test(key)) return 'fa-laptop-code';
    return 'fa-truck-fast';
  }

  getLogisticsSortableFields(): string[] {
    if (!this.companies?.length) return [];
    return Object.keys(this.companies[0]).filter(
      key =>
        key !== 'checked' &&
        this.columnsToHide.indexOf(key) < 0 &&
        (!this.companyColumnsNotAllowedToFilter ||
          this.companyColumnsNotAllowedToFilter.indexOf(key) < 0)
    );
  }

  hasLogisticsResultValue(value: any): boolean {
    const resolvedValue =
      value && typeof value === 'object' && 'value' in value
        ? value.value
        : value;
    return resolvedValue !== null && resolvedValue !== undefined && resolvedValue !== '';
  }

  logisticsResultStart(): number {
    if (!this.companies?.length) return 0;
    return (this.pageNumber - 1) * this.selectedPageSizeLimit + 1;
  }

  logisticsResultEnd(): number {
    return Math.min(
      this.pageNumber * this.selectedPageSizeLimit,
      this.companyCount().count || this.companies?.length || 0
    );
  }

  updateLogisticsPageSize(limit: number): void {
    if (!limit || limit === this.selectedPageSizeLimit) return;
    this.selectedPageSizeLimit = limit;
    this.pageNumber = 1;
    this.searchCompaniesAfterFiltersNoCount();
  }

  updateLogisticsSortKey(sortKey: string): void {
    this.sortKey = sortKey || '';
    this.sortDirectionIsAscending = false;
    this.pageNumber = 1;
    this.searchCompaniesAfterFilters(true);
  }

  toggleLogisticsSortDirection(): void {
    if (!this.sortKey) return;
    this.sortDirectionIsAscending = !this.sortDirectionIsAscending;
    this.pageNumber = 1;
    this.searchCompaniesAfterFilters(true);
  }

  scrollToLogisticsResults(): void {
    const results = document.getElementById('logistics-results-section');
    if (!results) return;
    const topOffset = parseFloat(getComputedStyle(results).scrollMarginTop) || 0;
    window.scrollTo({
      top: window.scrollY + results.getBoundingClientRect().top - topOffset,
      behavior: 'instant',
    });
  }

  scrollToLogisticsFilters(): void {
    document
      .querySelector('.logistics-workspace')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  nextPage(): void {
    if (this.pageNumber != this.maxPageNumber) {
      this.pageNumber += 1;
      this.searchCompaniesAfterFiltersNoCount();
    }
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber -= 1;
      this.searchCompaniesAfterFiltersNoCount();
    }
  }

  sortBy(sortKey): void {
    if (this.sortKey == sortKey) {
      if (!this.sortDirectionIsAscending) {
        this.sortDirectionIsAscending = true;
      } else {
        this.sortKey = null;
        this.sortDirectionIsAscending = false;
      }
    } else {
      this.sortKey = sortKey;
      this.sortDirectionIsAscending = false;
    }
    this.searchCompaniesAfterFilters(true);
  }

  // ZIN-5643: when a P&S semantic term is active, default the result sort to
  // match score (descending) so the closest matches surface first. Only
  // applied when the user has no explicit sort, so a manual column sort still
  // wins; when the term is cleared the auto sort is dropped so the backend
  // default order returns.
  enforceSemanticScoreSortDefault(): void {
    const hasSemanticTerm = !!(this.summarySemanticSearchTerm || '').trim();
    if (hasSemanticTerm && !this.sortKey) {
      this.sortKey = this.semanticSearchScoreSortKey;
      this.sortDirectionIsAscending = false;
    } else if (
      !hasSemanticTerm &&
      this.sortKey === this.semanticSearchScoreSortKey
    ) {
      this.sortKey = '';
      this.sortDirectionIsAscending = false;
    }
  }

  // ZIN-5642: the reused zint-ngx-slider defaults its single thumb to max, so
  // seed it to the current score floor whenever the slider (re)appears. The
  // control is inside an *ngIf, so a setter ViewChild is the reliable hook.
  @ViewChild('semanticMatchQualitySliderElement')
  set semanticMatchQualitySliderElement(slider: ZintNgxSliderComponent) {
    if (slider) {
      setTimeout(
        () =>
          slider.updateSliderValues({
            upperUpdate: this.summarySemanticSearchMinScore,
          }),
        0
      );
    }
  }

  // ZIN-5642: slider handler - single-handle sliders emit the value as `upper`.
  // Like every other slider, this only updates state; the search runs when the
  // user hits Search (and the usual triggers), not on each slider move.
  updateSemanticMatchScore(sliderEvent: any): void {
    this.summarySemanticSearchMinScore = sliderEvent?.upper ?? 0;
  }

  clearSearchParam(paramObj): void {
    if (this[paramObj['param']] !== undefined) {
      this[paramObj['param']] = null;
    } else {
      this.clearSearchForConfigDrivenFilters(paramObj);
    }

    if (paramObj['param'] === 'selectedCustomColumns') {
      this.customColumns.map(column =>
        column.options.map(option => (option.checked = false))
      );
      this.customColumns.map(column => (column.dropdownInput = ''));
      this.customColumns.map(column => (column.lockFilter = ''));
    }

    if (paramObj['param'] === 'selectedModules') {
      this.setHasAIRAModulesEnabledOptions();
    }

    const oldParams = this.allFilterParams()['updates'];

    const getFormatOldParams = {};
    for (const oldParam of oldParams) {
      if (
        oldParam.value !== '' &&
        oldParam.value !== 'null' &&
        oldParam.value !== 'false' &&
        oldParam.value !== null
      ) {
        getFormatOldParams[oldParam.param] = oldParam.value;
      }
    }

    this.resetSpecificFilter(paramObj.param);

    this.updateUIBasedUponDictionaryValuesReturnShouldRunAgain(
      getFormatOldParams
    );

    this.searchTags = JSON.parse(JSON.stringify(oldParams)); // seem to need to do this otherwise it references the old filter parameters?
    this.getActiveSearchParams();
  }

  private clearConfidenceForFilter(paramValue: any, label: string): void {
    if (!this.pubMethods.isValidJson(paramValue)) {
      return;
    }
    try {
      const parsed = JSON.parse(paramValue);
      const airaId = parsed?.airaQuestionId;
      if (airaId) {
        const filter = this.findAiraConfigFilter(airaId, parsed.fieldName);
        const confKey = `confidence:${filter?.label || label}Aira${airaId}`;
        delete this.confidenceValues[confKey];
        const slider = this.confidenceSliders?.find(
          s => s?.formName === confKey
        );
        slider?.reset();
      }
    } catch (e) {
      // Ignore if parsing fails, it's not a filter object with confidence.
    }
  }

  clearSearchForConfigDrivenFilters(paramObj: Record<string, any>): void {
    const paramString: string = paramObj.param;
    let filterType = '';
    if (this.pubMethods.isValidJson(paramObj.value)) {
      try {
        filterType = JSON.parse(paramObj.value).filterType;
      } catch (e) {
        // Not a JSON object with a filterType, so we can ignore.
      }
    }

    if (paramString.startsWith('lower') || paramString.startsWith('upper')) {
      // Config sliders (e.g., for numeric ranges)
      const propsKey = paramString.slice(0, 5); // 'lower' or 'upper'
      const sliderLabel = paramObj.param.slice(5);
      if (this.sliderValues[sliderLabel]) {
        this.sliderValues[sliderLabel][propsKey] = null;
      }
      this.clearConfidenceForFilter(paramObj.value, sliderLabel);
    } else if (filterType === 'multi_select') {
      this.configMultiSelectValues[paramString] = null;
      this.clearConfidenceForFilter(paramObj.value, paramString);
    } else if (filterType === 'text_input') {
      this.configTextInputValues[paramString] = null;
      this.clearConfidenceForFilter(paramObj.value, paramString);
    }
  }

  searchCompaniesAfterFiltersNoCount(isInitial = false, displayedParams?: HttpParams): void {
    this.enforceSemanticScoreSortDefault();
    this.updateFilters();
    const params = displayedParams ?? this.paramsForCompanySearch(this.allFilterParams());
    this.searchTags = JSON.parse(JSON.stringify(params.delete('resultsMode'))).updates;
    this.getActiveSearchParams();
    if (!this.bulkSelection.retainForSearch(params)) this.clearDashboardSelection();
    const pageOffset = displayedParams ? 0 : (this.pageNumber - 1) * this.selectedPageSizeLimit;
    this.dashboardService
      .searchCompaniesNew(params)
      .subscribe(output => this.updateCompaniesAndRenderInfo(output, isInitial, pageOffset, params));
  }

  private paramsForCompanySearch(params: HttpParams): HttpParams {
    return this.dashboardMode === 'full'
      ? params
      : params.set('resultsMode', this.dashboardMode);
  }

  getActiveSearchParams(): void {
    const currentActiveParamStrings = this.searchTags
      .filter(
        ({ value, param }) =>
          value &&
          value !== 'false' &&
          value !== 'null' &&
          param !== 'pageNumber'
      )
      .map(({ param }) => {
        // remove spaces
        return param.replace(/\s+/g, '');
      });

    const cardsToParams = { ...collapseCardToParamsMappings };
    Object.keys(this.linkedAiraFilters).forEach(key => {
      const objKey = this.removeSpecialCharsAndSpaces(key) + '-collapse-card';
      cardsToParams[objKey] = [...magicFilterParams];
    });

    this.activeParamsFromMapper = Object.entries(cardsToParams)
      .filter(([_, filterParams]) => {
        return currentActiveParamStrings.some(activeParam =>
          filterParams.includes(activeParam)
        );
      })
      .map(([cardId]) => cardId);
  }

  highlightFilterSection(cardElementId?: string): boolean {
    if (!cardElementId) return false;
    return this.activeParamsFromMapper.includes(cardElementId);
  }

  searchCompaniesAfterFilters(initialisePageCounter: boolean): void {
    if (initialisePageCounter) {
      this.pageNumber = 1;
    }
    this.searchCompaniesAfterFiltersNoCount();
    this.getCompanyCount(false);

    //close the preview if open
    this.previewDrawer?.close();

    //clear profile data caches
    this.dataStore.clearProfileDataCaches();

    //hide filters panel if smaller screens
    if (this.isScreenTooSmall() && this.dashboardMode === 'full') {
      this.toggleSidebarHeight();
    }
  }

  testIfSearchCompaniesOnEnterPress(event): void {
    if (!this.isLoading().loadingCompanies) {
      this.searchCompaniesAfterFilters(true);
    }
  }

  // When you have accts/website terms, the estimated number is sometimes v low, but you may get back a higher number than that.
  // Sometimes it is too high (but still less than 1 full page), and then you need to chop it down to the smaller number.
  // Update to that number.
  updateCompanyIfInaccurateEstimate(): void {
    if (
      !this.isLoading().loadingCompanies &&
      !this.isLoading().loadingCompanyCount &&
      this.pageNumber == 1 &&
      this.companyCount().exact == false
    ) {
      if (
        this.companies.length > this.companyCount().count ||
        (this.companies.length < this.companyCount().count &&
          this.selectedPageSizeLimit !== this.companies.length)
      ) {
        this.companyCount().count = this.companies.length;
      }
    }
  }

  updateCompanyCountAndPaginationLimits(data, isInitial): void {
    this.companyCount.set(data);
    const companiesPerPage = data['companies_per_page'];
    this.maxPageNumber = Math.ceil(
      this.companyCount().count / companiesPerPage
    );
    if (!isInitial) {
      this.updateCompanyIfInaccurateEstimate();
    }
  }

  getCompanyCount(isInitial): void {
    if (isInitial) {
      this.dashboardService
        .getInitialCompanyCount()
        .subscribe(data =>
          this.updateCompanyCountAndPaginationLimits(data, isInitial)
        );
    } else {
      this.dashboardService
        .getCompanyCount(this.paramsForCompanySearch(this.allFilterParams()))
        .subscribe(data =>
          this.updateCompanyCountAndPaginationLimits(data, isInitial)
        );
    }
  }

  updateUIComponents(data): void {
    this.countriesOfUBOsOptions = data.countries_of_ubos;
    this.numberUBOsRange = data.number_ubos_range;
    this.countriesOfOriginOptions = data.countries_of_origin;
    this.countriesOfOfficesOptions = data.countries_of_offices;
    this.countriesOfGeographicalRevenueOptions =
      data.countries_of_geographical_revenue;
    this.territoriesOfGeographicalRevenueOptions =
      data.territories_of_geographical_revenue;
    this.geographicalRevenueOptions = data.geographical_revenue_options;
    this.ukRegions = (data.uk_regions ?? []).map(region => ({ name: region, checked: false }));
    this.companyCategoryOptions = data.company_categories;
    this.companySICCodeOptions = data.sic_codes;
    // Re-apply any SIC-code selections that were restored before the options
    // had loaded (ZINT-ANGULARAPP-2AY).
    this.applyStashedSICCodeSelections();
    this.companyStatusOptions = data.company_statuses;
    this.numberOfKnownOfficeLocationsRange =
      data.number_of_known_locations_range;
    this.employeeRangeFigures = data.employee_bandings;
    this.headcountGrowthPercentageRange = data.headcount_growth_limits;
    this.functionalDepartmentOptions = data.functional_departments;
    this.logisticsFunctionalDepartmentOptions = selectOptionsByValue(
      this.functionalDepartmentOptions,
      LOGISTICS_FUNCTIONAL_DEPARTMENTS,
      option => option.name
    );
    this.companyAgeRange = data.company_age_range;
    this.LEIStatusOptions = data.lei_status_options;
    this.importingExportingBothOptions = data.importing_or_exporting;
    this.importExportMonthRange = data.import_export_month_range;
    this.importExportFrequencyRange = data.import_export_frequency_range;
    this.importExportFrequencyMonthCountRange =
      data.import_export_frequency_month_count_range;
    this.webTechnologyOptions = data.web_technologies;
    this.buildLogisticsWebTechnologyTree();
    this.webTrafficOptions = data.web_traffic_intervals;
    this.averageDirectorAgeRange = data.average_director_age_range;
    this.numberOfDirectorsRange = data.number_of_directors_range;
    this.numberOfPSCsRange = data.number_of_pscs_range;
    this.recentDirectorshipMonthRange = data.recent_directorship_month_range;
    this.nationalitiesOfDirectorsOptions = data.nationalities_of_directors;
    this.nationalitiesOfPSCsOptions = data.nationalities_of_pscs;
    this.daysUntilFinancialYearEndLimits = data.days_until_financial_year_end;
    this.financialYearEndsOptions = data.financial_year_month_options;
    this.daysUntilAnniversaryLimits = data.days_until_company_anniversary;
    this.hiringDayLimits = data.hiring_day_limits;
    this.numberOfRolesHiringForLimits = data.number_of_roles_hiring_for_limits;
    this.numberOfRolesHiringForDayLimits =
      data.number_of_roles_hiring_for_day_limits;
    this.investmentRaisedOptions = data.investment_raised_intervals;
    this.shareFilingEventWithinDaysOptions =
      data.share_filing_event_within_days_options;
    this.shareFilingCapitalValueOptions =
      data.share_filing_capital_value_options;
    this.accountsDueInRange = data.accounts_due_in_range;
    this.latestAccountsSubmittedRange = data.latest_accounts_submitted_range;
    this.accountGroupOptions = data.account_group_options;
    this.accountAuditOptions = data.account_audit_options;
    this.grantRangeOptions = data.grant_amount_range;
    this.grantMonthsUntilProjectEndRange =
      data.grant_months_until_project_end_range;
    this.mileRadiusRegisteredAddressRange =
      data.mile_radius_registered_address_range;
    this.mileRadiusEventsRange = data.mile_radius_events_range;
    this.eventDaysRange = data.events_days_range;
    this.addressOptions = data.address_options;
    this.newsDaysRange = data.news_events_day_range;
    this.accountsSearchDayRange = data.accounts_search_day_range;
    this.trademarkClassOptions = data.trademark_class_options;
    this.trademarkStatusOptions = data.trademark_status_options;
    this.totalEnergyKwhOptions = data.total_energy_kwh_options;
    this.gasKwhOptions = data.gas_kwh_options;
    this.electricKwhOptions = data.electric_kwh_options;
    this.scope1KwhOptions = data.scope_1_kwh_options;
    this.scope2KwhOptions = data.scope_2_kwh_options;
    this.scope3KwhOptions = data.scope_3_kwh_options;
    this.totalEmissionsKgCo2eOptions = data.total_emissions_kg_co2e_options;
    this.scope1KgCo2eOptions = data.scope_1_kg_co2e_options;
    this.scope2KgCo2eOptions = data.scope_2_kg_co2e_options;
    this.scope3KgCo2eOptions = data.scope_3_kg_co2e_options;
    this.kgCo2ePerMillionRevenueOptions =
      data.kg_co2e_per_million_revenue_options;
    this.kgCo2ePerEmployeeOptions = data.kg_co2e_per_employee_options;
    this.patentFiledWithinDaysOptions = data.patent_filed_within_days_options;
    this.patentAwardedWithinDaysOptions = data.patent_filed_within_days_options;
    this.contractValueOptions = data.contract_value_options;
    this.contractAwardedWithinMonthsOptions =
      data.contract_awarded_within_months_options;
    this.contractStartMonthsOptions = data.contract_start_months_options;
    this.contractEndMonthsOptions = data.contract_end_months_options;
    this.chargeCreatedMonthLimits = data.charge_created_month_limits;

    this.alertFrequencyOptions = data.alert_frequency_options;
    this.propensityWeightingRange = data.propensity_weighting_range;
    this.propensityTAMLimitCount = data.propensity_tam_org_limit;

    this.jsonParamKeysNotAllowedForAlertsOrPropensityScore =
      data.json_param_keys_not_allowed_for_alerts;
    if (this.alertFrequencyOptions && this.alertFrequencyOptions.length) {
      this.selectedAlertFrequency = this.alertFrequencyOptions[0].identifier;
    }
    this.alertsDaysRange = data.alerts_days_range;
    this.companyColumnsNotAllowedToFilter =
      data.company_columns_not_allowed_to_filter;

    this.logisticsNewsEventOptions = selectOptionsByValue(
      this.newsEventCategoryGroups.flatMap(group => group.subcategories),
      LOGISTICS_NEWS_EVENT_CATEGORIES,
      option => option.name
    );

    this.hasLoadedFlags.hasLoadedUIData.set(true);

    this.allConfigSlidersChildren = [
      ...this.tanAssetsSliders,
      ...this.intanAssetsSliders,
      ...this.heatScoreSliders,
      ...this.fleetSliders,
      ...this.wagesSliders,
      ...this.numberOfActiveTrademarksSliders,
      ...this.genericConfigSliders,
    ];

    this.enableFixedHeaders();
    this.layoutForMobile();
    this.applyPendingSliderUpdates();
  }

  ngAfterViewInit(): void {
    this.genericConfigSliders?.changes.subscribe(() => {
      this.applyPendingSliderUpdates();
    });

    // One more pass after initial view stabilizes
    setTimeout(() => {
      this.applyPendingSliderUpdates();
    });

    // Closing without submitting must not leave a preset company count behind for
    // the next time Get insights is opened from a different entry point.
    $('#dashboardGetInsightsModal').on('hidden.bs.modal', () => {
      this.resetMagicInsightsUnlockPreset();
    });
  }

  updateToDefaultImportingOrExporting(): void {
    if (!this.selectedImportingOrExportingOption) {
      this.selectedImportingOrExportingOption =
        this.importingExportingBothOptions[
          this.importingExportingBothOptions.length - 1
        ]['identifier'];
    }
  }

  updateImportExportFrequencyToDefaultImportingOrExporting(): void {
    if (!this.importExportFrequencyImportingOrExporting) {
      this.importExportFrequencyImportingOrExporting =
        this.importingExportingBothOptions[
          this.importingExportingBothOptions.length - 1
        ]['identifier'];
    }
  }

  getJSONParamsToSave(hideOrderingAndPageNumbers) {
    const paramsToSave = this.searchTags.filter(
      param =>
        param.value != '' &&
        param.value != null &&
        param.value != 'false' &&
        param.value != 'null'
    );
    const requiredOutput = {};
    for (const theParam of paramsToSave) {
      if (
        (hideOrderingAndPageNumbers &&
          this.jsonParamKeysNotAllowedForAlertsOrPropensityScore.indexOf(
            theParam.param
          ) < 0) ||
        !hideOrderingAndPageNumbers
      ) {
        requiredOutput[theParam.param] = theParam.value;
      }
    }
    return JSON.stringify(requiredOutput);
  }

  openResultsActionModal(resultAction: string): void {
    this.resultsActionModalTitle = resultAction;

    const modalElement = document.getElementById('searchResultsActionsModal');
    if (modalElement) {
      $(modalElement).modal('show');
    }
  }

  openCRMExportModal(crmName = 'hubspot'): void {
    if (!this.captureActionSelection()) return;
    this.showCRMExportModal(crmName);
  }

  private showCRMExportModal(crmName: string): void {
    this.crmExporterName = crmName;
    this.cdr.detectChanges();

    const modalElement = document.getElementById('dashboardExportToCRMModal');
    if (!modalElement) return;
    const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
    // Bootstrap 5 ignores options for an existing instance but reads its
    // config on each backdrop click and Escape press, so set it directly.
    modal._config.backdrop = 'static';
    this.syncCRMExportModalKeyboard();
    modal.show();
  }

  // Reruns the sync whenever the exporter loading state changes, including
  // while the modal is open. Must run in an injection context.
  watchCRMExportModalKeyboard(): EffectRef {
    return effect(() => this.syncCRMExportModalKeyboard());
  }

  // Block Escape while the exporter loads.
  syncCRMExportModalKeyboard(): void {
    // Read the signal before any early return: an effect only tracks signals
    // read on its last run, and the modal does not exist on the first run.
    const isLoading = this.crmService.wizardLoading().isLoadingExporter;
    const modalElement = document.getElementById('dashboardExportToCRMModal');
    const modal = modalElement && bootstrap.Modal.getInstance(modalElement);
    if (!modal) return;
    modal._config.keyboard = !isLoading;
  }

  getCRMIcon(crmType: string): string {
    const fontAwesomeCRMs = new Set(['hubspot', 'salesforce']);

    if (fontAwesomeCRMs.has(crmType)) {
      return `<i class="fab fa-${crmType}"></i>`;
    }

    const svgPath = `/assets/tech-icons/${crmType}.svg`;
    return `<img src="${svgPath}" alt="${crmType.charAt(0).toUpperCase() + crmType.slice(1)}">`;
  }

  // --------------- SAVED SEARCHES ---------------
  updateAfterSearchHasBeenSaved(data): void {
    if (data.success) {
      this.pubMethods.showInfoMessage(
        'This search has been saved to your account.'
      );
    }
    this.savedSearchName = '';
    this.makeSavedSearchShared = false;
  }

  saveSearch(): void {
    if (!this.savedSearchName?.trim().length) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Enter a valid name'
      );
      return;
    }
    const requiredOutput = this.getJSONParamsToSave(false);
    this.dashboardService
      .saveUserSearch(
        this.savedSearchName,
        requiredOutput,
        this.makeSavedSearchShared
      )
      .subscribe(data => this.updateAfterSearchHasBeenSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }

  // --------------- ALERTS ---------------

  chooseToAllowCreateAlertForm(): void {
    const maxCompaniesToCreateAlertFor = 200000;
    if (
      this.isLoading().loadingCompanyCount ||
      this.companyCount().count > maxCompaniesToCreateAlertFor
    ) {
      if (this.isLoading().loadingCompanyCount) {
        this.pubMethods.log(
          'Please wait until the search has loaded to create an alert.'
        );
      } else if (this.companyCount().count > maxCompaniesToCreateAlertFor) {
        this.pubMethods.log(
          'You can only create an alert for a search with fewer than ' +
            maxCompaniesToCreateAlertFor +
            ' initial organisations.'
        );
      }
    } else {
      this.openResultsActionModal('createAlert');
    }
  }

  updateAfterAlertHadBeenSaved(data): void {
    if (data.success) {
      if (this.isSuperUser) {
        const optionalWarning = data.warning_message
          ? data.warning_message
          : '';
        this.pubMethods.showInfoMessage(
          'This user will be alerted on a ' +
            this.selectedAlertFrequency +
            ' basis of new organisations matching these criteria. ' +
            optionalWarning
        );
      } else {
        this.pubMethods.showInfoMessage(
          'You will be alerted on a ' +
            this.selectedAlertFrequency +
            ' basis of new organisations matching these criteria.'
        );
      }
    }
    this.savedAlertName = '';
  }

  createAlert(): void {
    if (!this.savedAlertName?.trim().length) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Enter a valid alert name.'
      );
      return;
    }

    const requiredOutput = this.getJSONParamsToSave(true);
    this.dashboardService
      .saveUserAlert(
        this.savedAlertName,
        this.selectedAlertFrequency,
        this.teamMemberIdToCreateAlertFor,
        requiredOutput
      )
      .subscribe(data => this.updateAfterAlertHadBeenSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }
  // --------------- ALERTS ---------------

  // --------------- PROPENSITY SCORE ---------------

  updatePropensityModelsForUI(): void {
    if (this.propensityModels) {
      this.editablePropensityModels = this.propensityModels.filter(
        model => model.can_edit
      );
    }
  }

  updateZintPropensityScoreDetails(data): void {
    this.propensityModels = data.propensity_models;
    if (this.propensityModels && this.propensityModels.length > 0) {
      this.selectedPropensityModelId = this.propensityModels[0].id;
    }
    this.updatePropensityModelsForUI();
  }

  newPropensityScoreTAMCreated(data): void {
    if (data.success) {
      this.propensityModels = data.propensity_models;
      if (this.propensityModels && this.propensityModels.length > 0) {
        this.selectedPropensityModelId = data.new_propensity_model_id;
      }
      $('#propensityScoreTAMDefinition').modal('hide');
      this.updatePropensityModelsForUI();
      this.pubMethods.showInfoMessage(
        'Your Propensity Model has been created. Scores will begin to update in the next 1-2 hours.'
      );
    }
  }

  checkIfAddingNewPropensityScoreModel(
    possibleStringToTriggerNewPropensityModel
  ): void {
    if (
      possibleStringToTriggerNewPropensityModel ==
      this.stringToTriggerNewPropensityModel
    ) {
      this.selectedPropensityModelId = null;

      $('#searchResultsActionsModal').modal('hide');
      $('#propensityScoreTAMDefinition').modal('show');
    }
  }

  definePropensityScoreTAM(): void {
    const jsonParams = this.getJSONParamsToSave(true);
    this.zintPropensityScoreService
      .savePropensityScoreTAM(this.propensityScoreTAMName, jsonParams)
      .subscribe(data => this.newPropensityScoreTAMCreated(data));
  }

  updatePropensityScoreComponentAfterSaved(data): void {
    if (data.success) {
      this.pubMethods.showInfoMessage(
        'This Propensity Score Component was saved.'
      );
      this.propensityScoreComponentDescription = '';
      this.propensityScoreWeighting = null;
    }
  }

  savePropensityScoreComponent(): void {
    if (
      this.propensityScoreWeighting === null ||
      this.propensityScoreWeighting === undefined
    ) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'enter a valid weighting'
      );
      return;
    }

    const jsonParams = this.getJSONParamsToSave(true);

    this.zintPropensityScoreService
      .savePropensityScoreComponent(
        this.selectedPropensityModelId,
        this.propensityScoreComponentDescription,
        this.propensityScoreWeighting,
        jsonParams
      )
      .subscribe(data => this.updatePropensityScoreComponentAfterSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }

  validatepropensityScoreWeightingValue(): void {
    if (
      this.propensityWeightingRange &&
      this.propensityWeightingRange.length == 2
    ) {
      if (this.propensityScoreWeighting < this.propensityWeightingRange[0]) {
        this.propensityScoreWeighting = this.propensityWeightingRange[0];
      }
      if (this.propensityScoreWeighting > this.propensityWeightingRange[1]) {
        this.propensityScoreWeighting = this.propensityWeightingRange[1];
      }
    }
  }

  updateAfterTAMOverwritten(data): void {
    if (data.success) {
      this.pubMethods.showInfoMessage(
        'Your TAM has been overwritten. Scores will begin to update in the next 1-2 hours.'
      );
      $('#editPropensityModelTAM').modal('hide');
    }
  }

  updatePropensityModelTAM(): void {
    const jsonParams = this.getJSONParamsToSave(true);
    this.zintPropensityScoreService
      .overwritePropensityScoreTAM(
        this.selectedPropensityModelIdToUpdateTAM,
        jsonParams
      )
      .subscribe(data => this.updateAfterTAMOverwritten(data));
  }

  // --------------- PROPENSITY SCORE ---------------

  // --------------- TERRITORY ---------------

  updateTerritoryAssignmentDetails(hasTerritoryAssignmentAccessMessage) {
    this.hasTerritoryAssignmentAccessMessage =
      hasTerritoryAssignmentAccessMessage;
    this.territoryService.listTerritories().subscribe(data => {
      this.availableTerritories = data;
      this.areTerritoriesEnforced = data.some(
        territory => territory['is_enforced']
      );
    });
  }

  searchCompaniesWithTerritoryFilter(territory: Record<string, any> | null) {
    this.selectedTerritory = territory;

    this.searchCompaniesAfterFilters(true);
  }

  saveTerritory(): void {
    if (!this.territoryName?.trim().length) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Enter a valid territory name'
      );
      return;
    }

    const jsonParams = this.getJSONParamsToSave(true);
    this.territoryService
      .saveTerritory(this.territoryName, jsonParams)
      .subscribe(data => this.updateAfterTerritoryHasBeenSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }

  updateAfterTerritoryHasBeenSaved(data): void {
    if (data.name) {
      this.pubMethods.showInfoMessage(
        `Territory ${data.name} has been saved to your organisation.`
      );
    }
    this.territoryName = '';
  }

  // --------------- TERRITORY ---------------

  // RESETTING
  resetSpecificFilter(filter: string): void {
    const checkboxEl = $(`input[name='${filter}']:checkbox`);

    const inputEl = $(`input[name='${filter}']`);
    checkboxEl.prop('checked', false);
    inputEl.val('');

    this.resetAllZintSliders();
    this.resetConfigTextInputs(filter);
    this.resetMultiCheckboxOptions(filter);
  }

  resetAllFilters(): void {
    this.resetInputsThroughJquery();
    this.resetConfigTextInputs();
    this.resetMultiCheckboxOptions();
    this.resetAllZintSliders();
    this.summarySemanticSearchMinScore = this.defaultSemanticMatchScore;
    this.matchAllPositiveTags = false;
    this.sortKey = '';
    this.sortDirectionIsAscending = false;
    this.selectedAddressTypeOption = '';
    this.selectedLatestAccountsGroupOption = '';
    this.selectedLatestAccountsAuditOption = '';
  }

  resetAllFiltersAndUpdateFilters(): void {
    this.searchForm?.resetForm();
    this.resetAllFilters();
    this.updateFilters();
    const params = this.allFilterParams();
    this.searchTags = JSON.parse(JSON.stringify(params)).updates;
    this.activeParamsFromMapper = [];
  }

  resetLogisticsFilters(): void {
    const enforcedTerritory = this.areTerritoriesEnforced
      ? this.selectedTerritory
      : null;
    this.summarySemanticSearchTerm = '';
    if (!this.areTerritoriesEnforced) {
      this.selectedTerritory = null;
    }
    this.amazonSeller = false;
    this.postcodeBeginsWith = '';
    this.postcodeDistrict = '';
    this.postcodeWithinRadius = '';
    this.preferredLogisticsPostcodeMode = 'radius';
    this.hasPhoneNumber = false;
    this.hasEmailAddress = false;
    this.hasOfficesOverseas = false;
    this.selectedImportingOrExportingOption = '';
    this.importExportFrequencyImportingOrExporting = '';
    this.importExportTextSearch = '';
    this.sicCodeTextSearch = '';
    this.logisticsSicSelectionMode = 'include';
    this.customColumnSearchTerm = '';
    this.newsEventCategoryGroups
      .flatMap(group => group.subcategories)
      .forEach(option => (option.checked = false));
    this.resetAllFiltersAndUpdateFilters();
    if (enforcedTerritory) {
      this.selectedTerritory = enforcedTerritory;
    }
    this.resetSicSearch();
    this.resetLogisticsWebTechnologySearch();
    this.updateFilteredCustomColumns();
    this.selectActiveCompaniesInUI();
    this.refreshSearchTagsFromCurrentFilters();
  }

  resetConfigTextInputs(specificOptionKey?: string): void {
    if (specificOptionKey) {
      let foundKey: string | undefined;
      let foundIndex: number = -1;

      for (const [key, value] of Object.entries(this.zintTextInputConfigs)) {
        foundIndex = value.findIndex(
          (obj: any) => this.configFilterStateKey(obj) === specificOptionKey ||
            obj.label === specificOptionKey
        );
        if (foundIndex !== -1) {
          foundKey = key;
          break;
        }
      }
      if (foundKey && foundIndex !== -1) {
        this.zintTextInputConfigs[foundKey][foundIndex]['value'] = '';
      }
      return;
    }

    // reset value for all
    Object.values(this.zintTextInputConfigs).forEach(array => {
      array.forEach((obj: any) => {
        obj.value = '';
      });
    });
    this.configTextInputValues = {};
  }

  resetMultiCheckboxOptions(specificOptionKey?: string) {
    const optionsDict = this.getAllOptionsDict();

    if (specificOptionKey) {
      const options = optionsDict[specificOptionKey]?.options;
      options?.forEach(option => {
        option['checked'] = false;
      });
      return;
    }

    for (const key in optionsDict) {
      // Check 'attribute' property of the option - default to 'checked'
      const attribute = optionsDict[key]?.attribute || 'checked';

      const options = optionsDict[key].options;
      options.forEach(option => {
        option[attribute] = false;
      });
    }
  }

  resetInputsThroughJquery(): void {
    $('#search-area').find('input:checkbox').prop('checked', false);
    $('#search-area').find('input:text').not('.slider-input').val('');
    $('#search-area').find('select').val('');
  }

  resetAllZintSliders(): void {
    const doubleSliderElements = [
      this.numberUBOsSliderElement,
      this.companyAgeSliderElement,
      this.accountsDueSliderElement,
      this.latestAccountsSubmittedSliderElement,
      this.investmentRaisedSliderElement,
      this.shareFilingEventWithinDaysSliderElement,
      this.shareFilingCapitalValueSliderElement,
      this.grantAmountSliderElement,
      this.grantMonthsUntilProjectEndSliderElement,
      this.headcountSliderElement,
      this.headcountGrowthSliderElement,
      this.departmentHeadcountSliderElement,
      this.averageDirectorAgeSliderElement,
      this.numberOfDirectorsSliderElement,
      this.numberOfPSCsSliderElement,
      this.recentDirectorshipMonthRangeSliderElement,
      this.websiteVisitorsSliderElement,
      this.eventsDayRangeSliderElement,
      this.numberOfRolesHiringForSliderElement,
      this.daysUntilFinancialYearEndSliderElement,
      this.daysUntilAnniversarySliderElement,
      this.alertsDayRangeSliderElement,
      this.numberOfKnownOfficeLocationsSliderElement,
      this.geographicalRevenueSliderElement,
      this.totalEnergyKwhSliderElement,
      this.gasKwhSliderElement,
      this.electricKwhSliderElement,
      this.scope1KwhSliderElement,
      this.scope2KwhSliderElement,
      this.scope3KwhSliderElement,
      this.totalEmissionsKgCo2eSliderElement,
      this.scope1KgCo2eSliderElement,
      this.scope2KgCo2eSliderElement,
      this.scope3KgCo2eSliderElement,
      this.kgCo2ePerMillionRevenueSliderElement,
      this.kgCo2ePerEmployeeSliderElement,
      this.patentFiledWithinDaysSliderElement,
      this.patentAwardedWithinDaysSliderElement,
      this.contractValueSliderElement,
      this.contractStartMonthsSliderElement,
      this.contractEndMonthsSliderElement,
      this.chargeCreatedSliderElement,
    ];

    const singleSliderElements = [
      this.mileRadiusSliderElement,
      this.importExportSliderElement,
      this.importExportFrequencySliderElement,
      this.importExportFrequencyMonthCountSliderElement,
      this.numberOfRolesHiringForDayLimitSliderElement,
      this.hiringDayLimitSliderElement,
      this.newsDayRangeSliderElement,
      this.accountsSearchDayRangeSliderElement,
      this.eventsMileRadiusSliderElement,
      this.contractAwardedWithinMonthsSliderElement,
    ];

    const allManualSliders = [...doubleSliderElements, ...singleSliderElements];

    allManualSliders.forEach(slider => {
      if (slider) {
        slider.reset();
      }
    });

    this.allConfigSlidersChildren?.forEach(slider => {
      if (slider) {
        slider.reset();
      }
    });

    // this is to clear searchTags for configSliders
    this.sliderValues = {};
    this.confidenceValues = {};

    this.setZintSliderLimitsToNull();
  }

  setZintSliderLimitsToNull(): void {
    this.employeeLowerCount = null;
    this.employeeUpperCount = null;
    this.departmentHeadCountLowerCount = null;
    this.departmentHeadCountUpperCount = null;
    this.lowerHeadCountGrowthPercentage = null;
    this.upperHeadCountGrowthPercentage = null;
    this.lowerAge = null;
    this.upperAge = null;
    this.mileRadiusOfPostcode = null;
    this.lowerNumberOfKnownOfficeLocations = null;
    this.upperNumberOfKnownOfficeLocations = null;
    this.lowerGeographicalRevenueSelected = null;
    this.upperGeographicalRevenueSelected = null;
    this.importExportTimeScale = null;
    this.webTrafficLow = null;
    this.webTrafficHigh = null;
    this.lowerAverageDirectorAgeRange = null;
    this.upperAverageDirectorAgeRange = null;
    this.lowerNumberOfDirectors = null;
    this.upperNumberOfDirectors = null;
    this.lowerNumberOfPSCs = null;
    this.upperNumberOfPSCs = null;
    this.lowerRecentDirectorshipMonth = null;
    this.upperRecentDirectorshipMonth = null;
    this.lowerDaysUntilFinancialYearEnd = null;
    this.upperDaysUntilFinancialYearEnd = null;
    this.lowerDaysUntilAnniversary = null;
    this.upperDaysUntilAnniversary = null;
    this.selectedHiringDayLimit = null;
    this.lowerNumberOfRolesHiringFor = null;
    this.upperNumberOfRolesHiringFor = null;
    this.selectedNumberOfRolesHiringForDayLimit = null;
    this.lowerInvestmentAmount = null;
    this.upperInvestmentAmount = null;
    this.shareFilingEventWithinDays = null;
    this.lowerShareFilingCapitalValue = null;
    this.upperShareFilingCapitalValue = null;
    this.lowerAccountsDueSelected = null;
    this.upperAccountsDueSelected = null;
    this.lowerLatestAccountsSubmittedSelected = null;
    this.upperLatestAccountsSubmittedSelected = null;
    this.lowerGrantAmount = null;
    this.upperGrantAmount = null;
    this.grantLowerMonthsUntilProjectEnd = null;
    this.grantUpperMonthsUntilProjectEnd = null;
    this.mileRadiusOfEventsSelected = null;
    this.eventsWithinDaysLower = null;
    this.eventsWithinDaysUpper = null;
    this.newsWithinDays = null;
    this.accountsSearchWithinDays = null;
    this.lowerTotalEnergyKwhSelected = null;
    this.upperTotalEnergyKwhSelected = null;
    this.lowerGasKwhSelected = null;
    this.upperGasKwhSelected = null;
    this.lowerElectricKwhSelected = null;
    this.upperElectricKwhSelected = null;
    this.lowerScope1KwhSelected = null;
    this.upperScope1KwhSelected = null;
    this.lowerScope2KwhSelected = null;
    this.upperScope2KwhSelected = null;
    this.lowerScope3KwhSelected = null;
    this.upperScope3KwhSelected = null;
    this.lowerTotalEmissionsKgCo2eSelected = null;
    this.upperTotalEmissionsKgCo2eSelected = null;
    this.lowerScope1KgCo2eSelected = null;
    this.upperScope1KgCo2eSelected = null;
    this.lowerScope2KgCo2eSelected = null;
    this.upperScope2KgCo2eSelected = null;
    this.lowerScope3KgCo2eSelected = null;
    this.upperScope3KgCo2eSelected = null;
    this.lowerKgCo2ePerMillionRevenueSelected = null;
    this.upperKgCo2ePerMillionRevenueSelected = null;
    this.lowerKgCo2ePerEmployeeSelected = null;
    this.upperKgCo2ePerEmployeeSelected = null;
    this.patentFiledWithinDays = null;
    this.lowerPatentAwardedDays = null;
    this.upperPatentAwardedDays = null;
    this.lowerContractValueSelected = null;
    this.upperContractValueSelected = null;
    this.contractAwardedWithinMonths = null;
    this.lowerContractStartMonthsSelected = null;
    this.upperContractStartMonthsSelected = null;
    this.lowerContractEndMonthsSelected = null;
    this.upperContractEndMonthsSelected = null;
    this.lowerAlertsDayRange = null;
    this.upperAlertsDayRange = null;
  }

  // END RESETTING

  updateEmployeeRange(sliderEvent: Event): void {
    this.employeeLowerCount = sliderEvent['lower'];
    this.employeeUpperCount = sliderEvent['upper'];
  }

  updateHeadcountGrowthRange(sliderEvent: Event): void {
    this.lowerHeadCountGrowthPercentage = sliderEvent['lower'];
    this.upperHeadCountGrowthPercentage = sliderEvent['upper'];
  }

  updateDepartmentHeadcountRange(sliderEvent: Event): void {
    this.departmentHeadCountLowerCount = sliderEvent['lower'];
    this.departmentHeadCountUpperCount = sliderEvent['upper'];
  }

  updateNumberUBOsRange(sliderEvent: Event): void {
    this.lowerNumberOfUBOs = sliderEvent['lower'];
    this.upperNumberOfUBOs = sliderEvent['upper'];
  }

  updateCompanyAgeRange(sliderEvent: Event): void {
    this.lowerAge = sliderEvent['lower'];
    this.upperAge = sliderEvent['upper'];
  }

  updatePostcodeMileageRange(sliderEvent: Event): void {
    this.mileRadiusOfPostcode = sliderEvent['upper'];
  }

  updateNumberOfKnownOfficeLocationsRange(sliderEvent: Event): void {
    this.lowerNumberOfKnownOfficeLocations = sliderEvent['lower'];
    this.upperNumberOfKnownOfficeLocations = sliderEvent['upper'];
  }

  updateGeographicalRevenueRangeSelection(sliderEvent: Event): void {
    this.lowerGeographicalRevenueSelected = sliderEvent['lower'];
    this.upperGeographicalRevenueSelected = sliderEvent['upper'];
  }

  updateImportExportTimeScale(sliderEvent: Event): void {
    this.updateToDefaultImportingOrExporting();
    this.importExportTimeScale = sliderEvent['upper'];
  }

  updateImportExportFrequency(sliderEvent: Event): void {
    this.updateImportExportFrequencyToDefaultImportingOrExporting();
    this.importExportFrequency = sliderEvent['upper'];
  }

  updateImportExportFrequencyMonthCount(sliderEvent: Event): void {
    this.updateImportExportFrequencyToDefaultImportingOrExporting();
    this.importExportFrequencyMonthCount = sliderEvent['upper'];
  }

  updateWebTraffic(sliderEvent: Event): void {
    this.webTrafficLow = sliderEvent['lower'];
    this.webTrafficHigh = sliderEvent['upper'];
  }

  updateAverageDirectorAgeRange(sliderEvent: Event): void {
    this.lowerAverageDirectorAgeRange = sliderEvent['lower'];
    this.upperAverageDirectorAgeRange = sliderEvent['upper'];
  }

  updateNumberOfDirectorsRange(sliderEvent: Event): void {
    this.lowerNumberOfDirectors = sliderEvent['lower'];
    this.upperNumberOfDirectors = sliderEvent['upper'];
  }

  updateNumberOfPSCsRange(sliderEvent: Event): void {
    this.lowerNumberOfPSCs = sliderEvent['lower'];
    this.upperNumberOfPSCs = sliderEvent['upper'];
  }

  updateRecentDirectorshipMonthRange(sliderEvent: Event): void {
    this.lowerRecentDirectorshipMonth = sliderEvent['lower'];
    this.upperRecentDirectorshipMonth = sliderEvent['upper'];
  }

  updateFinancialYearEndRange(sliderEvent: Event): void {
    this.lowerDaysUntilFinancialYearEnd = sliderEvent['lower'];
    this.upperDaysUntilFinancialYearEnd = sliderEvent['upper'];
  }

  updateAnniversaryRange(sliderEvent: Event): void {
    this.lowerDaysUntilAnniversary = sliderEvent['lower'];
    this.upperDaysUntilAnniversary = sliderEvent['upper'];
  }

  updateHiringDayRange(sliderEvent: Event): void {
    this.selectedHiringDayLimit = sliderEvent['upper'];
  }

  updateNumberOfRolesHiringForRange(sliderEvent: Event): void {
    this.lowerNumberOfRolesHiringFor = sliderEvent['lower'];
    this.upperNumberOfRolesHiringFor = sliderEvent['upper'];
  }

  updateNumberOfRolesHiringForDayRange(sliderEvent: Event): void {
    this.selectedNumberOfRolesHiringForDayLimit = sliderEvent['upper'];
  }

  updateInvestmentRaisedRanges(sliderEvent: Event): void {
    this.lowerInvestmentAmount = sliderEvent['lower'];
    this.upperInvestmentAmount = sliderEvent['upper'];
  }

  updateShareFilingEventWithinDays(sliderEvent: Event): void {
    this.shareFilingEventWithinDays = sliderEvent['upper'];
  }

  updateShareFilingCapitalValue(sliderEvent: Event): void {
    this.lowerShareFilingCapitalValue = sliderEvent['lower'];
    this.upperShareFilingCapitalValue = sliderEvent['upper'];
  }

  updateAccountsDueInRange(sliderEvent: Event): void {
    this.lowerAccountsDueSelected = sliderEvent['lower'];
    this.upperAccountsDueSelected = sliderEvent['upper'];
  }

  updatelatestAccountsSubmittedInRange(sliderEvent: Event): void {
    this.lowerLatestAccountsSubmittedSelected = sliderEvent['lower'];
    this.upperLatestAccountsSubmittedSelected = sliderEvent['upper'];
  }

  updateGrantRangeSelected(sliderEvent: Event): void {
    this.lowerGrantAmount = sliderEvent['lower'];
    this.upperGrantAmount = sliderEvent['upper'];
  }

  updateGrantMonthsUntilProjectEndSelected(sliderEvent: Event): void {
    this.grantLowerMonthsUntilProjectEnd = sliderEvent['lower'];
    this.grantUpperMonthsUntilProjectEnd = sliderEvent['upper'];
  }

  updateEventsMileageRange(sliderEvent: Event): void {
    this.mileRadiusOfEventsSelected = sliderEvent['upper'];
  }

  updateEventsDaysRange(sliderEvent: Event): void {
    this.eventsWithinDaysLower = sliderEvent['lower'];
    this.eventsWithinDaysUpper = sliderEvent['upper'];
  }

  updateNewsDays(sliderEvent: Event): void {
    this.newsWithinDays = sliderEvent['upper'];
  }

  updateAccountsSearchDayRange(sliderEvent: Event): void {
    this.accountsSearchWithinDays = sliderEvent['upper'];
  }

  updateAlertsDayRange(sliderEvent: Event): void {
    this.lowerAlertsDayRange = sliderEvent['lower'];
    this.upperAlertsDayRange = sliderEvent['upper'];
  }

  updateTotalEnergyKwhRangeSelection(sliderEvent: Event): void {
    this.lowerTotalEnergyKwhSelected = sliderEvent['lower'];
    this.upperTotalEnergyKwhSelected = sliderEvent['upper'];
  }

  updateGasKwhRangeSelection(sliderEvent: Event): void {
    this.lowerGasKwhSelected = sliderEvent['lower'];
    this.upperGasKwhSelected = sliderEvent['upper'];
  }

  updateElectricKwhRangeSelection(sliderEvent: Event): void {
    this.lowerElectricKwhSelected = sliderEvent['lower'];
    this.upperElectricKwhSelected = sliderEvent['upper'];
  }

  updateScope1KwhRangeSelection(sliderEvent: Event): void {
    this.lowerScope1KwhSelected = sliderEvent['lower'];
    this.upperScope1KwhSelected = sliderEvent['upper'];
  }

  updateScope2KwhRangeSelection(sliderEvent: Event): void {
    this.lowerScope2KwhSelected = sliderEvent['lower'];
    this.upperScope2KwhSelected = sliderEvent['upper'];
  }

  updateScope3KwhRangeSelection(sliderEvent: Event): void {
    this.lowerScope3KwhSelected = sliderEvent['lower'];
    this.upperScope3KwhSelected = sliderEvent['upper'];
  }

  updateTotalEmissionsKgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerTotalEmissionsKgCo2eSelected = sliderEvent['lower'];
    this.upperTotalEmissionsKgCo2eSelected = sliderEvent['upper'];
  }

  updateScope1KgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerScope1KgCo2eSelected = sliderEvent['lower'];
    this.upperScope1KgCo2eSelected = sliderEvent['upper'];
  }

  updateScope2KgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerScope2KgCo2eSelected = sliderEvent['lower'];
    this.upperScope2KgCo2eSelected = sliderEvent['upper'];
  }

  updateScope3KgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerScope3KgCo2eSelected = sliderEvent['lower'];
    this.upperScope3KgCo2eSelected = sliderEvent['upper'];
  }

  updateKgCo2ePerMillionRevenueRangeSelection(sliderEvent: Event): void {
    this.lowerKgCo2ePerMillionRevenueSelected = sliderEvent['lower'];
    this.upperKgCo2ePerMillionRevenueSelected = sliderEvent['upper'];
  }

  updateKgCo2ePerEmployeeRangeSelection(sliderEvent: Event): void {
    this.lowerKgCo2ePerEmployeeSelected = sliderEvent['lower'];
    this.upperKgCo2ePerEmployeeSelected = sliderEvent['upper'];
  }

  updatePatentFiledWithinDays(sliderEvent: Event): void {
    this.patentFiledWithinDays = sliderEvent['upper'];
  }

  updatePatentAwardedWithinDays(sliderEvent: Event): void {
    this.lowerPatentAwardedDays = sliderEvent['lower'];
    this.upperPatentAwardedDays = sliderEvent['upper'];
  }

  updateContractAwardedWithinMonths(sliderEvent: Event): void {
    this.contractAwardedWithinMonths = sliderEvent['upper'];
  }

  updateContractValueRangeSelection(sliderEvent: Event): void {
    this.lowerContractValueSelected = sliderEvent['lower'];
    this.upperContractValueSelected = sliderEvent['upper'];
  }

  updateContractStartMonthsRangeSelection(sliderEvent: Event): void {
    this.lowerContractStartMonthsSelected = sliderEvent['lower'];
    this.upperContractStartMonthsSelected = sliderEvent['upper'];
  }

  updateContractEndMonthsRangeSelection(sliderEvent: Event): void {
    this.lowerContractEndMonthsSelected = sliderEvent['lower'];
    this.upperContractEndMonthsSelected = sliderEvent['upper'];
  }

  updateChargeCreatedRangeSelection(sliderEvent: Event): void {
    this.chargeCreatedMonthsAgoLower = sliderEvent['lower'];
    this.chargeCreatedMonthsAgoUpper = sliderEvent['upper'];
  }

  triggerConfigSliderUpdate(
    sliderUpdatedValues: Record<string, any>,
    key: string
  ): void {
    const { lower, upper } = sliderUpdatedValues;
    if (lower === undefined && upper === undefined) {
      delete this.sliderValues[key];
    } else {
      this.sliderValues[key] = { lower, upper };
    }
  }

  triggerConfigSliderWithObjectUpdate(
    sliderUpdatedValues: Record<string, any>,
    objectWithDetails: Record<string, any>,
    objectIdentifier: string = 'airaQuestionId'
  ): void {
    const { lower, upper } = sliderUpdatedValues;
    const stateKey = this.configFilterStateKey(objectWithDetails);
    if (lower === undefined && upper === undefined) {
      delete this.sliderValues[stateKey];
    } else {
      const confidence = this.confidenceValues[`confidence:${objectWithDetails.label}Aira${objectWithDetails.airaQuestionId}`];
      const confLower = confidence?.lower ?? null;
      const confUpper = confidence?.upper ?? null;
      const lowerDetails =
        lower === null || lower === undefined
          ? null
          : {
              [objectIdentifier]: objectWithDetails[objectIdentifier],
              value: lower,
              fieldName: objectWithDetails['fieldName'],
              filterType: objectWithDetails['filterType'],
              linkedFieldName: objectWithDetails['linkedFieldName'],
              lowerConfidence: confLower,
              upperConfidence: confUpper,
            };
      const upperDetails =
        upper === null || upper === undefined
          ? null
          : {
              [objectIdentifier]: objectWithDetails[objectIdentifier],
              value: upper,
              fieldName: objectWithDetails['fieldName'],
              filterType: objectWithDetails['filterType'],
              linkedFieldName: objectWithDetails['linkedFieldName'],
              lowerConfidence: confLower,
              upperConfidence: confUpper,
            };
      this.sliderValues[stateKey] = {
        lower: lowerDetails,
        upper: upperDetails,
      };
    }
  }

  triggerConfigTextInputWithObjectUpdate(
    textValue: string,
    objectWithDetails: Record<string, any>,
    objectIdentifier: string = 'airaQuestionId'
  ): void {
    const stateKey = this.configFilterStateKey(objectWithDetails);
    if (textValue === null || textValue === undefined || textValue === '') {
      delete this.configTextInputValues[stateKey];
    } else {
      const confidence = this.confidenceValues[`confidence:${objectWithDetails.label}Aira${objectWithDetails.airaQuestionId}`];
      const confLower = confidence?.lower ?? null;
      const confUpper = confidence?.upper ?? null;
      this.configTextInputValues[stateKey] = {
        [objectIdentifier]: objectWithDetails[objectIdentifier],
        value: textValue,
        fieldName: objectWithDetails['fieldName'],
        filterType: objectWithDetails['filterType'],
        linkedFieldName: objectWithDetails['linkedFieldName'],
        lowerConfidence: confLower,
        upperConfidence: confUpper,
      };
    }
  }

  filterMagicCountriesList(
    searchTerm: string,
    sectionKey: any,
    groupKey: any
  ): void {
    const group = this.linkedAiraFilters[sectionKey][groupKey];
    const multiSelect = group.find(
      filter => filter.filterType === 'multi_select'
    );

    if (!multiSelect) {
      return;
    }

    const shouldShowAll = searchTerm.trim().length < 2;
    const lowercasedTerm = searchTerm.toLowerCase();

    multiSelect.options = multiSelect.options.map(option => {
      const shouldShow =
        shouldShowAll || option.name.toLowerCase().includes(lowercasedTerm);
      return { ...option, shouldShow };
    });
  }

  triggerConfigMultiSelectInputWithObjectUpdate(
    objectWithDetails: Record<string, any>,
    objectIdentifier: string = 'airaQuestionId'
  ): void {
    const stateKey = this.configFilterStateKey(objectWithDetails);
    const checkedValues = objectWithDetails.options
      .filter(opt => opt.checked)
      .map(filtered => filtered.value);

    if (!checkedValues?.length) {
      delete this.configMultiSelectValues[stateKey];
    } else {
      const confidence = this.confidenceValues[`confidence:${objectWithDetails.label}Aira${objectWithDetails.airaQuestionId}`];
      const confLower = confidence?.lower ?? null;
      const confUpper = confidence?.upper ?? null;
      this.configMultiSelectValues[stateKey] = {
        [objectIdentifier]: objectWithDetails[objectIdentifier],
        value: checkedValues,
        fieldName: objectWithDetails['fieldName'],
        filterType: objectWithDetails['filterType'],
        linkedFieldName: objectWithDetails['linkedFieldName'],
        lowerConfidence: confLower,
        upperConfidence: confUpper,
      };
    }
  }

  // Confidence slider: track per-filter confidence bounds and merge into existing values
  triggerConfidenceSliderUpdate = (
    sliderUpdatedValues: { lower?: number; upper?: number },
    objectWithDetails: Record<string, any>
  ): void => {
    const lower = sliderUpdatedValues?.lower ?? null;
    const upper = sliderUpdatedValues?.upper ?? null;


    const label = objectWithDetails?.label;
    const airaId = objectWithDetails?.airaQuestionId;
    if (!label) return;
    const stateKey = this.configFilterStateKey(objectWithDetails);

    if (airaId) {
      const confKey = `confidence:${label}Aira${airaId}`;
      this.confidenceValues[confKey] = { lower, upper };
    }

    // Update existing slider entries if present
    const existingSliderEntry = this.sliderValues[stateKey];
    if (existingSliderEntry) {
      if (existingSliderEntry.lower) {
        existingSliderEntry.lower = {
          ...existingSliderEntry.lower,
          lowerConfidence: lower,
          upperConfidence: upper,
        };
      }
      if (existingSliderEntry.upper) {
        existingSliderEntry.upper = {
          ...existingSliderEntry.upper,
          lowerConfidence: lower,
          upperConfidence: upper,
        };
      }
    }

    // Update existing text input entry if present
    if (this.configTextInputValues[stateKey]) {
      this.configTextInputValues[stateKey] = {
        ...this.configTextInputValues[stateKey],
        lowerConfidence: lower,
        upperConfidence: upper,
      };
    }

    // Update existing multi-select entry if present
    if (this.configMultiSelectValues[stateKey]) {
      this.configMultiSelectValues[stateKey] = {
        ...this.configMultiSelectValues[stateKey],
        lowerConfidence: lower,
        upperConfidence: upper,
      };
    }
  };

  getConfidenceKey(filter: Record<string, any>): string {
    if (!filter) return '';
    return `confidence:${filter.label}Aira${filter.airaQuestionId}`;
  }

  toggleConfidencePanel(filter: Record<string, any>): void {
    const key = this.getConfidenceKey(filter);
    this.confidenceOpen[key] = !this.confidenceOpen[key];
  }

  getChildSics(sicObject): Array<any> {
    const childrenSicIds = sicObject['children'];
    return this.companySICCodeOptions.filter(
      sic => childrenSicIds.indexOf(sic.id) > -1
    );
  }

  collapseSICCodes(sicRequired): void {
    const childSics = this.getChildSics(sicRequired);
    for (const sic of childSics) {
      sic.shouldShow = false;
      this.collapseSICCodes(sic);
    }
  }

  expandOrCollapseSICCodes(sicID): void {
    const sicRequired = this.companySICCodeOptions.filter(
      sic => sic.id == sicID
    )[0];

    const childSics = this.getChildSics(sicRequired);

    if (childSics.some(e => e.shouldShow == true)) {
      this.collapseSICCodes(sicRequired);
    } else {
      childSics.map(function (e) {
        e.shouldShow = true;
      });
    }
  }

  updateVisibleImportExportCodesAfterSearch(data): void {
    this.importExportCategories = data.import_export_results;
  }

  searchImportExportCodes(): void {
    this.dashboardService
      .searchImportExportCodes(this.importExportTextSearch)
      .subscribe(data => this.updateVisibleImportExportCodesAfterSearch(data));
  }

  resetImportExportSearch(): void {
    this.dashboardService
      .getInitialImportExportCategories()
      .subscribe(
        data => (this.importExportCategories = data.import_export_sections)
      );
    this.importExportTextSearch = '';
  }

  updateImpExpCats(data, id, impExpType): void {
    for (let i = 0; i < this.importExportCategories.length; i++) {
      // tslint:disable-next-line:prefer-const
      var existingCat = this.importExportCategories[i];
      if (existingCat.id == id && existingCat.impExpType == impExpType) {
        // tslint:disable-next-line:prefer-const
        var insertionIndex = i;
        existingCat.isExpanded = true;
        break;
      }
    }

    for (const child of data.reverse()) {
      existingCat.children.push(String(child.id + '_' + child.impExpType));
      this.importExportCategories.splice(insertionIndex + 1, 0, child);
    }
  }

  collapseImpExpCat(importExportCategory): void {
    for (let i = 0; i < importExportCategory.children.length; i++) {
      const alreadyOpenChild = importExportCategory.children[i];
      const components = alreadyOpenChild.split('_');
      const _id = components[0];
      const impExpType = components[1];
      for (let j = 0; j < this.importExportCategories.length; j++) {
        const existingCat = this.importExportCategories[j];
        if (existingCat.id == _id && existingCat.impExpType == impExpType) {
          this.collapseImpExpCat(existingCat);
          this.importExportCategories.splice(j, 1);
          break;
        }
      }
    }
    importExportCategory.children = [];
    importExportCategory.isExpanded = false;
  }

  expandOrCollapseImportExportCategories(importExportCategory): void {
    if (importExportCategory.isExpanded) {
      this.collapseImpExpCat(importExportCategory);
    } else {
      this.dashboardService
        .getChildImportExportCategories(
          importExportCategory.id,
          importExportCategory.impExpType,
          importExportCategory.level
        )
        .subscribe(data =>
          this.updateImpExpCats(
            data,
            importExportCategory.id,
            importExportCategory.impExpType
          )
        );
    }
  }

  expandOrCollapseItems(companyChildren, type): void {
    const childStatusOptions = this[type].filter(
      el => companyChildren.indexOf(el.name) > -1
    );
    var showValue = childStatusOptions.some(e => e.shouldShow == true)
      ? false
      : true;
    for (const child of childStatusOptions) {
      child.shouldShow = showValue;
    }
  }

  updateCountOfSelectedCompanies(): void {
    if (this.isLoading().loadingCompanies) return;
    if (this.bulkSelection.selection().mode === 'query') {
      this.bulkSelection.updateQueryPage(this.companies, this.displayedPageOffset);
      this.totalCompaniesSelected = selectionCount(this.bulkSelection.selection());
      if (!this.totalCompaniesSelected) {
        this.clearDashboardSelection();
        return;
      }
      this.syncQuerySelectionCheckboxes();
      return;
    }
    this.hasSelectedTotalNumberMatchingFilter = false;
    const selectedCompanies = this.companies.filter(
      company => company.checked == true
    );
    if (!selectedCompanies.length) {
      this.clearDashboardSelection();
      return;
    }
    this.checkboxSelectAllCompanies = selectedCompanies.length === this.companies.length;
    this.totalCompaniesSelected = selectedCompanies.length;
    this.showSelectedNumbersSection = selectedCompanies.length > 0;
    this.bulkSelection.selectIds(selectedCompanies.map(company => company.id), this.displayedSearchParams);
    this.dataStore.changeDataSourceCurrentCompanyIds(
      selectedCompanies.map(company => company.id)
    );
    // Need to do this as current search params need to be shared on individual company downloads so we can get ES column information.request.POST
    this.dataStore.updateJustCurrentSearchParams(this.displayedSearchParams);
  }

  showOrHideInfoOnAllSelectedCompanies(): void {
    if (this.isLoading().loadingCompanies) return;
    if (this.bulkSelection.selection().mode === 'query') {
      this.companies.forEach(company => company.checked = this.checkboxSelectAllCompanies);
      this.updateCountOfSelectedCompanies();
      return;
    }
    if (this.checkboxSelectAllCompanies) {
      this.showSelectedNumbersSection = true;
      this.companies.map(company => (company.checked = true));
      this.totalCompaniesSelected = this.companies.length;
      this.hasSelectedTotalNumberMatchingFilter = false;
      this.bulkSelection.selectIds(this.companies.map(company => company.id), this.displayedSearchParams);
      this.dataStore.updateJustCurrentSearchParams(this.displayedSearchParams);
      this.dataStore.changeDataSourceCurrentCompanyIds(
        this.companies.map(company => company.id)
      );
    } else {
      this.clearDashboardSelection();
    }
  }

  hasSelectedAllCompaniesByQuery(): void {
    this.selectionAmount = this.selectableCompanyCount;
    this.applySelectionAmount();
  }

  updateAddressTypeOnInputChange(): void {
    if (
      this.postcodeWithinRadius ||
      this.postcodeBeginsWith ||
      this.selectedTownId ||
      this.postcodeDistrict ||
      this.ukRegions?.some(r => r.checked)
    ) {
      if (!this.selectedAddressTypeOption) {
        this.selectedAddressTypeOption = this.addressOptions[0];
      }
    }
  }

  postcodeBeginsWithChanged(): void {
    this.selectedTownId = '';
    this.postcodeDistrict = '';
    this.townName = '';
    this.postcodeWithinRadius = '';
    this.updateAddressTypeOnInputChange();
  }

  postcodeDistrictChanged(): void {
    this.selectedTownId = '';
    this.townName = '';
    this.postcodeWithinRadius = '';
    this.updateAddressTypeOnInputChange();
  }

  ukRegionChanged(): void {
    this.updateAddressTypeOnInputChange();
  }

  postcodeWithinRadiusChanged(): void {
    this.selectedTownId = '';
    this.postcodeDistrict = '';
    this.townName = '';
    this.postcodeBeginsWith = '';
    this.updateAddressTypeOnInputChange();
  }

  updateMatchingTowns(data): void {
    if (data.matching_towns) {
      this.possibleTownNames = data.matching_towns;
    }
  }

  townNameChanged(): void {
    if (this.townName && this.townName.length > 2) {
      this.dashboardService
        .getMatchingTowns(this.townName)
        .subscribe(data => this.updateMatchingTowns(data));
    }
  }

  selectShortlistedTownName(inputID, townName): void {
    this.postcodeWithinRadius = '';
    this.postcodeBeginsWith = '';
    this.selectedTownId = inputID;
    this.townName = townName;
    this.updateAddressTypeOnInputChange();
  }

  // ----------- SUPPLIER SEARCH -----------

  private supplierSearchDebounce: any;

  supplierSearchChanged(supplierType: string): void {
    clearTimeout(this.supplierSearchDebounce);

    const inputProp = supplierType + 'SearchInput';
    if (!this[inputProp] || this[inputProp].length < 3 || this.loadingSupplierType) return;

    this.supplierSearchDebounce = setTimeout(() => {
      this.loadingSupplierType = supplierType;
      this.dashboardService
        .searchSuppliers(this[inputProp], supplierType)
        .subscribe(data => {
          const possibleProp = 'possible' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's';
          this[possibleProp] = data.suppliers || [];
          this.loadingSupplierType = '';
        });
    }, 400);
  }

  addSupplier(supplier: {id: number, canonical_name: string}, supplierType: string): void {
    const selectedProp = 'selected' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's';
    if (!this[selectedProp]) this[selectedProp] = [];
    if (!this[selectedProp].some(s => s.id === supplier.id)) {
      this[selectedProp].push(supplier);
    }
    this[supplierType + 'SearchInput'] = '';
    this['possible' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's'] = [];
  }

  removeSupplier(supplierId: number, supplierType: string): void {
    const selectedProp = 'selected' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's';
    this[selectedProp] = this[selectedProp].filter(s => s.id !== supplierId);
  }

  // ----------- HEADCOUNTS/DEPARTMENTS -----------

  resetFunctionalDepartmentSearch(): void {
    this.hasSearchedFunctionalDepartment = false;
    this.selectedFunctionalDepartments = this.functionalDepartmentOptions.map(
      option => (option.shouldShow = true)
    );
    this.functionalDepartmentTextSearch = '';
  }

  searchFunctionalDepartment(): void {
    if (
      this.functionalDepartmentTextSearch &&
      this.functionalDepartmentTextSearch.length > 2
    ) {
      this.isSearchingFunctionalDepartments = true;
      this.hasSearchedFunctionalDepartment = true;
      this.functionalDepartmentOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));

      this.functionalDepartmentOptions
        .filter(
          option =>
            option.name
              .toLowerCase()
              .indexOf(this.functionalDepartmentTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));

      this.isSearchingFunctionalDepartments = false;
    }
  }

  checkIfVisible(option) {
    if (option.shouldShow) {
      option.checked = true;
    }
  }

  checkUncheckAllVisibleFunctionalDepartments(): void {
    if (this.selectAllVisibleFunctionalDepartments) {
      this.functionalDepartmentOptions.forEach(option =>
        this.checkIfVisible(option)
      );
    } else {
      this.functionalDepartmentOptions.forEach(
        option => (option.checked = false)
      );
    }
  }

  // ----------- HEADCOUNTS/DEPARTMENTS -----------

  // ----------- KEYWORDS -----------

  removeWebsiteKeyword(keyword): void {
    const index = this.websiteKeywords.indexOf(keyword);
    if (index >= 0) {
      this.websiteKeywords.splice(index, 1);
      this.possibleWebsiteKeywords = [];
    }
  }

  addWebsiteKeyword(keyword): void {
    const index = this.websiteKeywords.indexOf(keyword);
    if (index < 0) {
      this.websiteKeywords.push(keyword);
      this.possibleWebsiteKeywords = [];
      this.websiteKeywordInput = '';
    }
  }

  forceAddWebsiteKeyword(event): void {
    if (this.websiteKeywordInput && this.websiteKeywordInput.length > 2) {
      this.addWebsiteKeyword(this.websiteKeywordInput.toLowerCase());
    }
  }

  updateWebsiteKeywords(data): void {
    if (data.keywords) {
      this.possibleWebsiteKeywords = data.keywords;
    }
  }

  websiteKeywordChanged(): void {
    if (this.websiteKeywordInput && this.websiteKeywordInput.length > 2) {
      this.dashboardService
        .getMatchingWebsiteKeywords(this.websiteKeywordInput)
        .subscribe(data => this.updateWebsiteKeywords(data));
    }
  }

  // ----------- KEYWORDS -----------

  // ----------- EVENTS -----------

  removeMatchingEvent(event): void {
    const index = this.selectedEvents.indexOf(event);
    if (index >= 0) {
      this.selectedEvents.splice(index, 1);
    }
  }

  addMatchingEvent(possibleMatchingEvent): void {
    const alreadyHaveEventCounter = this.selectedEvents.filter(
      event => event.id == possibleMatchingEvent.id
    ).length;
    if (alreadyHaveEventCounter == 0) {
      this.selectedEvents.push(possibleMatchingEvent);
      this.possibleMatchingEvents = [];
      this.eventNameSearchInput = '';
      this.postcodeEventsWithinRadius = '';
    }
  }

  eventNameSearchChanged(): void {
    if (this.eventNameSearchInput && this.eventNameSearchInput.length > 2) {
      this.dashboardService
        .getMatchingEvents(this.eventNameSearchInput)
        .subscribe(data => this.updatePossibleMatchingEvents(data));
    }
  }

  updatePossibleMatchingEvents(data): void {
    if (data.events) {
      this.possibleMatchingEvents = data.events;
    }
  }

  // ----------- EVENTS -----------

  // ----------- GEOGRAPHICAL REVENUE -----------

  resetGeographicalRevenueSearch(): void {
    this.selectedGeographicalRevenueCountries =
      this.countriesOfGeographicalRevenueOptions.map(
        option => (option.shouldShow = true)
      );
    this.selectedGeographicalRevenueTerritories =
      this.territoriesOfGeographicalRevenueOptions.map(
        option => (option.shouldShow = true)
      );
  }

  searchGeographicalRevenue(): void {
    if (
      this.geographicalRevenueTextSearch &&
      this.geographicalRevenueTextSearch.length > 2
    ) {
      this.countriesOfGeographicalRevenueOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));
      this.countriesOfGeographicalRevenueOptions
        .filter(
          option =>
            option.name
              .toLowerCase()
              .indexOf(this.geographicalRevenueTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));

      this.territoriesOfGeographicalRevenueOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));
      this.territoriesOfGeographicalRevenueOptions
        .filter(
          option =>
            option.name
              .toLowerCase()
              .indexOf(this.geographicalRevenueTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));
    }
  }

  // ----------- GEOGRAPHICAL REVENUE -----------

  expandOrCollapseSidebar(): void {
    this.isSidebarCollapsed.update(value => !value);
    $('.collapse').collapse('hide');
    this.updateScrollBarDimensions();
  }

  expandSideBarIfRequired(): void {
    if (this.isSidebarCollapsed) {
      this.isSidebarCollapsed.set(false);
      this.updateScrollBarDimensions();
    }
  }

  addOrRemoveColumnToWrappedColumnNumbers(columnNumber: number): void {
    if (columnNumber === null) {
      /* null means wrap all columns */
      this.isWrapAllColumns = !this.isWrapAllColumns;
      if (this.isWrapAllColumns) {
        localStorage.setItem(
          'zintAllColumnsWrapped',
          JSON.stringify(this.isWrapAllColumns)
        );
      } else {
        localStorage.removeItem('zintAllColumnsWrapped');
      }

      return;
    }

    if (columnNumber) {
      const index = this.wrappedTableColumnNumbers.indexOf(columnNumber);
      if (index >= 0) {
        this.wrappedTableColumnNumbers.splice(index, 1);
      } else {
        this.wrappedTableColumnNumbers.push(columnNumber);
      }

      localStorage.setItem(
        'zintWrappedColumns',
        JSON.stringify(this.wrappedTableColumnNumbers)
      );
    }
  }

  columnResized($event, columnName): void {
    const width = parseInt($event.target.style.width.replace('px', ''));
    const exisitingValue = this.columnNamesToWidths[columnName];
    // if it's the same then ignore it.
    if (
      (exisitingValue != width || exisitingValue == undefined) &&
      !isNaN(width)
    ) {
      // still resize without waiting for the subscription.
      this.columnNamesToWidths[columnName] = width;

      this.dashboardService
        .saveColumnSize(columnName, width)
        .subscribe(data => {
          if (data.success) {
            this.updateScrollBarDimensions();
          }
        });
    }
  }

  mouseHasLeftHeaderCell($event, columnName): void {
    // Only trigger the column resize event if we are currently clicked on a header cell.
    // We don't want to trigger this if we are just moving the mouse over it.
    if (this.tableHeaderColumnNumberCurrentlyClicked != null) {
      this.tableHeaderColumnNumberCurrentlyClicked = null;
      this.columnResized($event, columnName);
      $($event.target).mouseup();
    }
  }

  showEditColumnsModal(): void {
    $('#editColumnsModal').modal('show');
  }

  toggleDownloadModal(): void {
    if (!this.captureActionSelection()) return;
    this.showDownloadModal = true;
    this.cdr.detectChanges();

    $('#downloadModal').modal('show');

    $('#downloadModal').one('hide.bs.modal', e => {
      this.showDownloadModal = false;
    });
  }

  toggleMagicBulkUnlockModal(): void {
    this.cdr.detectChanges();

    $('#magicInsightsBulkUnlock').modal('show');

    $('#magicInsightsBulkUnlock').on('hidden.bs.modal', e => {
      this.checkboxSelectAllCompanies = false;
      this.showOrHideInfoOnAllSelectedCompanies();
      this.dataStore.setMagicInsightsUnlockCount(null);
      this.cdr.markForCheck();
    });
  }

  toggleMagicInsightsGenerationModal(): void {
    this.cdr.detectChanges();
    $('#magicInsightsGenerationModal').modal('show');
    $('#magicInsightsGenerationModal').on('hidden.bs.modal', e => {
      this.checkboxSelectAllCompanies = false;
      this.showOrHideInfoOnAllSelectedCompanies();
      this.cdr.markForCheck();
    });
  }

  resetSearchForFilters(inputField?: HTMLInputElement) {
    const hiddenElements = document.querySelectorAll('.d-none');
    if (inputField) {
      inputField.value = '';
    }
    this.filterSearchTerm = '';
    this.filterSearchErrorMsg = '';
    hiddenElements.forEach(el => el.classList.remove('d-none'));
  }

  showHideFilters(searchTerm) {
    const trimmedTerm = searchTerm.trim()?.toLowerCase();
    if (trimmedTerm.length < 2) {
      this.resetSearchForFilters();
      return;
    }

    const trimmedTermsArr = trimmedTerm
      .split(' ')
      .map(keyword => keyword.trim());

    const filtersElementsToShow = Object.entries(this.dynamicFilterIdToKeywordsMappings)
      .filter(([_, keywords]) => {
        return trimmedTermsArr.some((term: string) => keywords.includes(term));
      })
      .map(([id]) => document.querySelector('#' + id));

    if (filtersElementsToShow.length) {
      this.filterSearchTerm = searchTerm;
      this.filterSearchErrorMsg = '';
      this.hideSearchableFilters();

      filtersElementsToShow.forEach(element => {
        this.revealElement(element);
      });
    } else {
      this.filterSearchErrorMsg = 'no match found';
    }
  }

  hideSearchableFilters() {
    /**
     * lv1 - top categories - firmographics, operations etc with ids ending starting with searchable-show-hide
     * the same goes for the nested <details> so their ids start with searchable-show-hide
     * lv2 - collapses - top level collapses with ids ending in -collapse-card
     * bottom level filters - all individual sliders and other filters with ids ending in -filter
     */

    const levelOneElements = document.querySelectorAll(
      '[id^="searchable-show-hide-"]'
    );
    levelOneElements.forEach(el => el.classList.add('d-none'));

    const levelTwoElements = document.querySelectorAll(
      '[id$="-collapse-card"]'
    );
    levelTwoElements.forEach(el => el.classList.add('d-none'));

    const allFiltersWithId = document.querySelectorAll('[id$="-filter"]');

    allFiltersWithId.forEach(el => el.classList.add('d-none'));

    const magicInsightGroups = document.querySelectorAll(
      '.searchable-magic-insight-group'
    );
    magicInsightGroups.forEach(el => el.classList.add('d-none'));
  }

  revealElement(element) {
    if (element) {
      element.classList.remove('d-none');

      // Traverse up the DOM to reveal ancestors
      let parent = element.parentElement;
      while (parent) {
        parent.classList.remove('d-none');
        parent = parent.parentElement;
      }
    }
  }

  isFilterSectionDetailsExpanded(event: Event, section: string): void {
    const detailsElement = event.target as HTMLDetailsElement;

    this.expandedFilterSections[section] = detailsElement.open;
  }

  checkIfTableHoveredAndFixed(
    event: Event,
    tableName: string,
    mouseLeave?: boolean
  ): void {
    if (mouseLeave) {
      this.hoveredOverflowingTables[tableName] = false;
      return;
    }

    const divElement = event.target as HTMLDivElement;
    const isNoHoverApplied = divElement.classList.contains('no-hover');

    /* 'no-hover' class is applied by the directive and if the table has this class,
     return the opposite of this check.
    */
    this.hoveredOverflowingTables[tableName] = !isNoHoverApplied;
  }

  getSliceRange(index: number, size: number): Record<string, number> {
    // this returns the dynamic slice indices for the array slice in the template
    const start = index * size;
    const end = start + size;
    return { start, end };
  }

  getSliderDetails(): void {
    this.dashboardService.getSliderDetails().subscribe(data => {
      this.updateSliderDetails(data);
    });
    this.dashboardService
      .getAiraFilterConfig(this.magicInsightsResultsMode())
      .subscribe(data => {
        this.updateAiraFilterDetails(data);
      });
  }

  updateSliderDetails(data): void {
    for (const section of data) {
      const sectionName = section['sectionName'];
      if (sectionName === 'Land Ownership and Development') {
        this.landOwnershipAndDevelopmentSliders.push(section);
      } else {
        this.sliderSections.push(section);
      }
      this.zintSlidersConfigs[sectionName] = section['sliders'];
    }
    this.sliderFieldLabels = Object.values(this.zintSlidersConfigs)
      .flat()
      .map(slider => slider.label);
    this.syncLogisticsSliderConfigs();
    this.hasLoadedFlags.hasLoadedBackendSliders.set(true);
  }

  private syncLogisticsSliderConfigs(): void {
    this.logisticsFinancialSliders = selectSliderConfigs(
      this.zintSlidersConfigs,
      LOGISTICS_FINANCIAL_SLIDER_LABELS
    );
    this.logisticsAssetSliders = selectSliderConfigs(
      this.zintSlidersConfigs,
      LOGISTICS_ASSET_SLIDER_LABELS
    );
  }

  updateAiraFilterDetails(data): void {
    this.rawAiraFiltersData = data;
    this.linkedAiraFilters = {};
    this.magicModuleNamesIds = [];
    this.editableMagicModuleNamesIds = [];
    this.organisationMagicModuleNamesIds = [];
    this.nonEditableMagicModuleNamesIds = [];

    data.forEach((module: any) => {
      const sectionName = module.sectionName;

      // Initialize arrays for this section, overwriting any previous data
      this.zintSlidersConfigs[sectionName] = [];
      this.zintTextInputConfigs[sectionName] = [];
      this.zintMultiSelectConfigs[sectionName] = [];

      this.magicModuleNamesIds.push({
        id: module.id,
        name: module.sectionName,
        isOrganisationModule: module.isOrganisationModule,
        // This config only lists questions that expose a filter, so its length is not the
        // module's question count. Left unset until the endpoint returns a real total.
        questionCount: module.questionCount ?? null,
      });

      if (module.isEditable) {
        this.editableMagicModuleNamesIds.push({
          id: module.id,
          name: module.sectionName,
        });
      }
      else{
        this.nonEditableMagicModuleNamesIds.push({
          id: module.id,
          name: module.sectionName,
        });
      }

      if (module.isOrganisationModule) {
        // If you can view the module then you can also attempt to generate answers (but you must of course have quota assigned to you)
        this.organisationMagicModuleNamesIds.push({
          id: module.id,
          name: module.sectionName,
        });
      }

      // this is for searching for matching magic insights filters.
      const sectionKeywords = this.getKeywords(sectionName);
      const moduleCardId = this.removeSpecialCharsAndSpaces(sectionName) + '-collapse-card';
      const moduleTypeKeywords = module.isOrganisationModule
        ? ['custom', 'research']
        : ['ready', 'made'];
      this.dynamicFilterIdToKeywordsMappings[moduleCardId] = [
        ...sectionKeywords,
        ...moduleTypeKeywords,
        'magic',
        'aira',
      ];

      module.questions?.forEach((question: any) => {
        Object.values(question.filters).forEach((filtersGroup: any[]) => {
          filtersGroup.forEach((filter: any) => {
            if (filter.label) {
              const filterId = this.simpleKebabCase(filter.label) + '-filter';
              const filterKeywords = this.getKeywords(filter.label);
              this.dynamicFilterIdToKeywordsMappings[filterId] = [...filterKeywords, ...sectionKeywords, 'magic', 'aira'];
            }
            if (filter.filterType === 'range_slider') {
              this.zintSlidersConfigs[sectionName].push(filter);
            } else if (filter.filterType === 'text_input') {
              this.zintTextInputConfigs[sectionName].push(filter);
            } else if (filter.filterType === 'multi_select') {
              this.zintMultiSelectConfigs[sectionName].push(filter);
            }
          });
        });
      });
    });

    this.sliderFieldLabels = Object.values(this.zintSlidersConfigs)
      .flat()
      .map((slider: any) => slider.label);

    this.groupAiraFiltersByLinkedFieldName();
    this.processAIRAMuliSelectOptions();
    this.logisticsMagicInsightSections =
      buildLogisticsMagicInsightSections(this.rawAiraFiltersData);

    this.hasLoadedFlags.hasLoadedAiraFilters.set(true);
  }

  processAIRAMuliSelectOptions(): void {
    this.rawAiraFiltersData.forEach(module => {
      if (module.questions) {
        module.questions.forEach(question => {
          Object.values(question.filters).forEach((filtersGroup: any[]) => {
            filtersGroup.forEach(filter => {
              if (
                filter.filterType === 'multi_select' &&
                filter.options_type &&
                filter.options_type === 'countries_and_territories'
              ) {
                // we have to make a deep copy of the object from file.
                filter.options = JSON.parse(
                  JSON.stringify(this.magicCountriesList)
                );
              }
            });
          });
        });
      }
    });
  }

  groupAiraFiltersByLinkedFieldName() {
    this.rawAiraFiltersData.forEach(section => {
      const sectionName = section.sectionName;
      // Initialise the section in linkedAiraFilters if not present.
      if (!this.linkedAiraFilters[sectionName]) {
        this.linkedAiraFilters[sectionName] = {
          id: section.id,
          hasFilterAccess: section.hasFilterAccess || false,
          isEditable: section.isEditable || false,
          isOrganisationModule: section.isOrganisationModule || false,
          newQuestion: '',
          answerCount: section.answerCount,
          lastAnswerGeneratedAt: section.lastAnswerGeneratedAt,
        };
      }

      if (section.questions) {
        section.questions.forEach(question => {
          Object.keys(question.filters).forEach(linkedFieldKey => {
            if (!this.linkedAiraFilters[sectionName][linkedFieldKey]) {
              this.linkedAiraFilters[sectionName][linkedFieldKey] = [];
            }
            this.linkedAiraFilters[sectionName][linkedFieldKey].push(
              ...question.filters[linkedFieldKey]
            );
          });
        });
      }
    });
    this.setMagicInsightSections();
    this.setHasAIRAModulesEnabledOptions();
  }

  setMagicInsightSections(): void {
    const sections = Object.keys(this.linkedAiraFilters).map(key => ({
      key,
      value: this.linkedAiraFilters[key],
    }));
    this.readyMadeMagicInsightSections = sections.filter(
      section => !section.value.isOrganisationModule
    );
    this.customMagicInsightSections = sections.filter(
      section => section.value.isOrganisationModule
    );
  }

  setHasAIRAModulesEnabledOptions(): void {
    this.selectedModules = [];
    this.selectedModulesByName = {};
    Object.keys(this.linkedAiraFilters).forEach(key => {
      const selectedModule = {
        id: this.linkedAiraFilters[key].id,
        name: key,
        checked: false,
        excludeAnswered: false,
        isOrganisationModule: this.linkedAiraFilters[key].isOrganisationModule,
      };
      this.selectedModules.push(selectedModule);
      this.selectedModulesByName[key] = selectedModule;
    });
  }

  setModuleAnswerFilter(
    sectionName: string,
    filter: 'checked' | 'excludeAnswered',
    enabled: boolean
  ): void {
    const module = this.selectedModulesByName[sectionName];
    if (!module) return;
    module[filter] = enabled;
    if (enabled) {
      module[filter === 'checked' ? 'excludeAnswered' : 'checked'] = false;
    }
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.dashboardMode === 'logistics') {
      const queryBar = document.querySelector(
        '.logistics-query-bar'
      ) as HTMLElement | null;
      this.showLogisticsStickySearch = Boolean(
        queryBar && queryBar.getBoundingClientRect().bottom < 64
      );
      return;
    }

    if (this.dashboardMode !== 'full') return;

    if (!this.isScreenTooSmall()) {
      return;
    } else {
      const sidebar = document.querySelector('.sidebar-grid') as HTMLDivElement;
      const sidebarTop = sidebar.getBoundingClientRect().top;

      if (sidebarTop <= 18) {
        this.mobileSidebarBtnFixedTop = true;
      } else {
        this.mobileSidebarBtnFixedTop = false;
      }
    }
  }

  toggleSidebarHeight(resetting = false) {
    const sidebar = document.querySelector('.sidebar-grid') as HTMLDivElement;
    // Focused dashboard skins render their own filters without the full
    // dashboard sidebar. The shared viewport effect still runs for them.
    if (!sidebar) return;
    if (resetting) {
      sidebar.style.height = '100dvh';
    }

    if (!sidebar.style.height) {
      sidebar.style.height = '0.1px';
      this.mobileSidebarRolledUp = true;
    } else {
      sidebar.style.height = '';
      this.mobileSidebarRolledUp = false;
    }
  }

  createAiraModuleQuestion(section: any): void {
    const questionText = section.value.newQuestion;
    if (!questionText || questionText.trim().length < 3) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Please enter a valid question.'
      );
      return;
    }
    this.dashboardService
      .createAiraModuleQuestion(section.value.id, questionText)
      .subscribe(data => {
        if (data.id) {
          this.dashboardService
            .getAiraFilterConfig(this.magicInsightsResultsMode())
            .subscribe(newData => {
              this.updateAiraFilterDetails(newData);
            });
          this.pubMethods.showInfoMessage('New question created!');
          section.value.newQuestion = '';
        }
      });
  }

  removeSpecialCharsAndSpaces(str: string): string {
    // Removes spaces AND special characters like &, %, $, etc.
    // Keeps letters, numbers, hyphens, and underscores.
    return str.replace(/[^a-zA-Z0-9-_]/g, '');
  }

  private simpleKebabCase(text: string): string {
    if (!text) return '';
    return text
      .replace(/([a-z])([A-Z])/g, '$1-$2') // Handle camelCase
      .replace(/[\s_]+/g, '-') // Replace spaces and underscores
      .toLowerCase();
  }

  private getKeywords(text: string): string[] {
    if (!text) return [];
    // Split by space, underscore, or hyphen
    return text.toLowerCase().split(/[\s_-]+/);
  }

  filterTags(): void {
    const trimmedTerm = this.tagSearchTerm.trim().toLowerCase();
    const filterList = (tag) => {
      if (trimmedTerm.length < 2) {
        tag.shouldShow = true;
      } else {
        // Check if tag name exists before calling toLowerCase
        tag.shouldShow = tag.name && tag.name.toLowerCase().includes(trimmedTerm);
      }
      return tag;
    };

    this.tagsPositiveOptions = this.tagsPositiveOptions.map(filterList);
    this.tagsNegativeOptions = this.tagsNegativeOptions.map(filterList);
  }


  updateFilteredCustomColumns(): void {
    if (!this.customColumns) {
      this.filteredCustomColumns = [];
      return;
    }

    const trimmedTerm = this.customColumnSearchTerm.trim().toLowerCase();

    this.filteredCustomColumns = this.customColumns.filter(col => {
      if (trimmedTerm.length < 2) {
        return true; // Show all columns if search is empty
      }

      // Filter by name
      return col.name && col.name.toLowerCase().includes(trimmedTerm);
    });
  }

  filterCustomColumns(): void {
    this.updateFilteredCustomColumns();
  }




}
